// Force rebuild: Fix provider directory back button navigation - 2025-12-02-v36
// First import on purpose: decides whether this document is a pop-out and
// shields localStorage.activeTab before anything else can read or write it.
import { POPOUT_BOOT } from "@/lib/popoutBoot";
import React, { useState, useEffect, useCallback } from "react";
import { DesktopUpdateBanner } from "@/components/desktop/DesktopUpdateBanner";
import { DesktopRefreshHandler } from "@/components/desktop/DesktopRefreshHandler";
import { PAGE_POPOUT_PATH, appTabKey, isInAnyPopout, isValidTabId, openAppPopout, openMessagingPopout, parseHostNavigateMessage, postToHost, recordFallbackTab, recordIdFromSpec, recordParamsFor, shouldFollowStorageEvent, type AppTabSpec } from "@/lib/popout";
import { currentDesktopWindowLabel, isFramedDocument, isSecondaryAppWindow } from "@/lib/desktopWindow";
import { AppTabsHost } from "@/components/popout/AppTabsHost";
import { tabShortcutFromEvent } from "@/lib/tabStrip";
import { usePopoutPages } from "@/components/popout/usePopoutPages";
import { OpenInMenu } from "@/components/popout/OpenInMenu";
import { MainTabPanels, MainTabStrip } from "@/components/popout/MainWindowTabs";
import { PRIMARY_UID, useMainWindowTabs } from "@/components/popout/useMainWindowTabs";
import { PopoutNoAccess } from "@/components/popout/PopoutNoAccess";
import { useSidebarPageAccess } from "@/hooks/useSidebarPageAccess";
import TrivenyRail from "./src/platform/shared/components/Layout/TrivenyRail";
import { CreateReferralForm } from "@/components/CreateReferralForm";
import { ReferralDetailView } from "@/components/ReferralDetailView";
import { ReferralEditForm } from "@/components/ReferralEditForm";
import Homepage from "@/components/Homepage";
// import LoginPage from "@/components/LoginPage";
import { ReferralQueue } from "@/components/ReferralQueue";
import { CandidateFinder } from "@/components/CandidateFinder";
import { Directory } from "@/components/Directory";
import ProviderDirectory from "@/components/ProviderDirectory";
import { ProviderCommunications } from "@/components/ProviderCommunications";
import { AutoMatchReferrals } from "@/components/AutoMatchReferrals";
import { CapacityDashboard } from "@/components/CapacityDashboard";
import { CaseWorkspace } from "@/components/workspace/CaseWorkspace";
import MemberProfiles from "@/components/MemberProfiles";
import { OTFRTFSubmission } from "@/components/OTFRTFSubmission";
import { LiveFeed } from "@/components/LiveFeed";
import ImportManagerEnhanced from "@/components/admin/ImportManagerEnhanced";
import MemberList from "@/components/members/MemberListNew";
import AddEditMember from "@/components/members/AddEditMemberNew";
import { MemberProfile } from "@/components/members/memberProfileSwitches";
import MemberDuplicatesPanel from "@/components/members/MemberDuplicatesPanel";
import ContactListPage from "@/components/contacts/ContactListPage";
import ContactDetailPage from "@/components/contacts/ContactDetailPage";
import AddContactForm from "@/components/contacts/AddContactForm";
import MemberFieldConfigManager from "@/components/admin/MemberFieldConfigManager";
import FilterChipsManager from "@/components/admin/FilterChipsManager";
import FieldMappingManager from "@/components/admin/FieldMappingManager";
import ProgramManagement from "@/components/admin/ProgramManagement";
import ContactTypesPage from "@/components/admin/ContactTypesPage";
import { CBOManagement } from "@/components/admin/CBOManagement";
import { OrganizationSetupWizard } from "@/components/admin/OrganizationSetupWizard";
import { CBOForm } from "@/components/admin/CBOForm";
import OrganizationSettingsPage from "@/components/admin/OrganizationSettingsPage";
import { UserManagement } from "@/components/admin/UserManagement";
// CommunicationTemplates is now reached via CommunicationTemplatesHub (My Org tab).
import CampaignTemplates from "@/components/admin/CampaignTemplates";
import { LetterTemplatesPage } from "@/components/communications/LetterTemplatesPage";
import { organizationsAPI, type Organization } from "./services/api/organizations";
import { RegisterWithInvite } from "@/components/auth/RegisterWithInvite";
import { ForgotPassword } from "@/components/auth/ForgotPassword";
import { ResetPassword } from "@/components/auth/ResetPassword";
import { ReConsentModal } from "@/components/auth/ReConsentModal";
import SelfServiceSignup from "@/components/SelfServiceSignup";
import { EnrollmentDetail } from "@/components/members/memberProfileSwitches";
import EnrollmentListNew from "@/components/enrollments/EnrollmentListNew";
import CreateEnrollmentPage from "@/components/enrollments/CreateEnrollmentPage";
import EligibilityPreScreenForm from "@/components/eligibility/EligibilityPreScreenForm";
import EligibilityVerificationPanel from "@/components/eligibility/EligibilityVerificationPanel";
import DirectVerificationPanel from "@/components/eligibility/DirectVerificationPanel";
import FfsTransitionCohortPage from "@/components/eligibility/FfsTransitionCohortPage";
import VerifyPage from "@/components/verify/VerifyPage";
import CredentialSetupWizard from "@/components/eligibility/CredentialSetupWizard";
import { WorkflowTemplateList } from "@/components/workflows/WorkflowTemplateList";
import { WorkflowInstanceTracker } from "@/components/workflows/WorkflowInstanceTracker";
import { AutomationRules } from "@/components/workflows/AutomationRules";
import { FormTemplateBuilder } from "@/components/forms/FormTemplateBuilder";
import { FormLibrary } from "@/components/members/memberProfileSwitches";
import { FormAnalyticsDashboard } from "@/components/forms/FormAnalyticsDashboard";
import { AnalyticsDashboard } from "@/components/analytics/AnalyticsDashboard";
import { IntegrationsDashboard } from "@/components/integrations/IntegrationsDashboard";
import RAGAdminDashboard from "@/components/admin/RAGAdminDashboard";
import TestRunnerDashboard from "@/components/admin/TestRunnerDashboard";
import DeveloperPortalPage from "@/src/app/developer/page";
import SandboxManagementPage from "@/src/app/admin/sandboxes/page";
import { AIOrchestrationDashboard } from "@/components/ai/AIOrchestrationDashboard";
import { ExternalServicesCatalog } from "@/components/ai/ExternalServicesCatalog";
import EnrichmentDashboard from "@/components/enrichment/EnrichmentDashboard";
import { GroupsPage } from "@/components/groups/GroupsPage";
import { BulkCommunicationsPage } from "@/components/communications/BulkCommunicationsPage";
import { CampaignsPage } from "@/components/communications/CampaignsPage";
import { ChatflowBuilder } from "@/components/communications/ChatflowBuilder";
// EngagementsPage is now embedded as a tab within BulkCommunicationsPage
import { CommunicationHistoryPage } from "@/components/communications/CommunicationHistoryPage";
// TRUS-709: the 2-Way Inbox (InboxPage) is retired; its entry points open the member Inbox.
import { MEMBER_INBOX_TAB, redirectLegacyInboxTab, requestOpenMemberInbox } from "@/lib/memberInboxNavigation";
import CSRInboxPage from "@/components/communications/CSRInboxPage";
// TemplateLibraryGallery is now reached via CommunicationTemplatesHub (Universal tab).
import CommunicationTemplatesHub from "@/components/communications/CommunicationTemplatesHub";
// Legacy V1 referral tab ids — routed to the unified V2 Care Coordination hub.
// 'finder' included so the provider-directory manual-match loop returns to the
// unified hub (Manual Match sub-tab) instead of the retired standalone page.
// A pop-out (a tabbed window, or a page inside one) shares localStorage with
// the main window. It must never write activeTab: the main window listens for
// `storage` events on that key and would jump to whatever page the pop-out
// wrote. In a pop-out the key is virtualised per window (lib/popoutStorageShield),
// so this write stays local there; the early return is belt and braces.
function persistActiveTab(tab: string): void {
  if (isInAnyPopout()) return;
  localStorage.setItem('activeTab', tab);
}

/**
 * Pages that are a fixed-height app surface (a conversation pane with its own
 * scrolling message list and a composer pinned to the bottom) rather than a
 * document. For these the content area is exactly the viewport left under the
 * shell (tab strip, page header, pillar bar): <main> becomes a flex column of
 * that height and the page fills the rest, so the page itself never scrolls
 * and the composer is always on screen. Every other page keeps natural
 * document scrolling (and the footer).
 */
const FULL_HEIGHT_TABS = new Set(['workspace-messaging', 'inbox', 'v2-comms']);

/** A tabbed pop-out window (/popout/app). */
const IS_TABS_HOST = POPOUT_BOOT.kind === 'app';
/** A single page, chromeless — what each tab of a tabbed window shows (/popout/page?tab=…). */
const PAGE_POPOUT: AppTabSpec | null | undefined =
  POPOUT_BOOT.kind === 'page' ? (POPOUT_BOOT as { page: AppTabSpec | null }).page : undefined;
const IS_PAGE_POPOUT = POPOUT_BOOT.kind === 'page';

const LEGACY_REFERRAL_TAB_IDS = ['queue', 'create-referral', 'livefeed', 'unmatched', 'matching', 'finder', 'workspace', 'care-plan-driven'];
import { ReferralAutomationRuleEditor, UUHPDataIngestionPanel, AuditExportButton, UUHPCampaignProgressPage, UUHPJourneyBuilderPage } from "@/components/communications/EngagementDemoFlowComponents";
import CampaignSetUpPage from "@/components/communications/CampaignSetUpPage";
import { WhatsAppTemplateManager } from "@/components/communications/WhatsAppTemplateManager";
import { WhatsAppBusinessSettings } from "@/components/settings/WhatsAppBusinessSettings";
import { SenderNumberAssignments } from "@/components/settings/SenderNumberAssignments";
import { SenderVerificationWizard } from "@/components/settings/SenderVerificationWizard";
import { PhoneNumberManagement } from "@/components/settings/PhoneNumberManagement";
import { TestPage } from "@/components/communications/TestPage";
import ActionTracker from "@/components/incentives/ActionTracker";
import ProgramMilestoneConfiguration from "@/components/incentives/ProgramMilestoneConfiguration";
import ProgramMilestoneDashboardSelector from "@/components/incentives/ProgramMilestoneDashboardSelector";
import MemberMilestoneProgressSelector from "@/components/incentives/MemberMilestoneProgressSelector";
import PendingMilestoneApprovals from "@/components/incentives/PendingMilestoneApprovals";
import MilestoneManagement from "@/components/incentives/MilestoneManagement";
import SponsorDashboard from "./src/pages/SponsorDashboard";
import CalendarPage from "@/components/calendar/CalendarPage";
import VisitJoinPage, { parseVisitPath, VISIT_JOIN_STAFF_SIGNIN_KEY } from "@/components/calendar/VisitJoinPage";
import TelehealthDashboard from "@/components/telehealth/TelehealthDashboard";
import TelehealthReporting from "@/components/telehealth/TelehealthReporting";
import TelehealthWaitingRoom from "@/components/telehealth/TelehealthWaitingRoom";
import TelehealthVideoRoomWrapper from "@/components/telehealth/TelehealthVideoRoomWrapper";
import TelehealthEncountersList from "@/components/telehealth/TelehealthEncountersList";
import TelehealthByMember from "@/components/telehealth/TelehealthByMember";
import OAuthIntegrations from "@/components/integrations/OAuthIntegrations";
import FundDeposits from "@/components/financial/FundDeposits";
import MemberPayouts from "@/components/financial/MemberPayouts";
import ReconciliationDashboard from "@/components/financial/ReconciliationDashboard";
import CardManagement from "@/components/payments/CardManagement";
import TransactionHistory from "@/components/payments/TransactionHistory";
import MemberCardPortal from "@/components/payments/MemberCardPortal";
import MyPhoneNumberCard from "@/components/settings/MyPhoneNumberCard";
import SponsorTerms from "@/components/sponsors/SponsorTerms";
import NetworkOverview from "@/components/network/NetworkOverview";
import ProgramMarketplace from "@/components/marketplace/ProgramMarketplace";
import CBODiscovery from "@/components/marketplace/CBODiscovery";
import ProgramIntelligenceCatalog from "@/components/marketplace/ProgramIntelligenceCatalog";
import { UserPreferences } from "@/components/settings/UserPreferences";
import TrivenyUserPreferences from "@/src/platform/admin/pages/TrivenyUserPreferences";
import { TranslationManagement } from "@/components/admin/TranslationManagement";
import { useReferrals, useCBOs, useMetrics, useHealthCheck, useProviderCount } from "@/hooks/useApi";
import { FrontendReferral } from "./services/dataTransform";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { BrandingProvider, useBranding } from "@/contexts/BrandingContext";
import { ProgramProvider } from "@/contexts/ProgramContext";
import SandboxBanner from "@/components/common/SandboxBanner";
import SandboxSplash from "@/components/common/SandboxSplash";
import SandboxDisclosure from "@/components/common/SandboxDisclosure";
import { DataShieldProvider } from "@/contexts/DataShieldContext";
import { KeyboardShortcutProvider } from "@/contexts/KeyboardShortcutContext";
import { GlobalShortcuts } from "@/components/GlobalShortcuts";
import { KeyboardShortcutsHelp } from "@/components/KeyboardShortcutsHelp";
import { CommandPalette, getPageDescriptor } from "@/components/CommandPalette";
import { CopilotPanel } from "@/components/copilot/CopilotPanel";
import { useCopilotLauncher } from "@/components/copilot/useCopilotLauncher";
import { AuthModal } from "@/components/AuthModal";
import { UserProfile } from "@/components/UserProfile";
import { SessionTimeoutWarning } from "@/components/SessionTimeoutWarning";
import { Footer } from "@/components/Footer";
import RailwayTest from "@/components/RailwayTest";
import { config } from "@/src/config";

// Feedback & Support Components
import { FeedbackWidget } from "@/components/feedback/FeedbackWidget";
import { SupportChat } from "@/components/support/SupportChat";
// NOTE: FeedbackDashboard, PlainTicketsDashboard, and FeedbackTaskList have been consolidated
// into CustomerSupportPage (accessible via platform-support tab) to avoid duplication
import { SelfSignupDashboard } from "@/components/admin/SelfSignupDashboard";
import { ViewerCapabilities } from "@/components/viewer/ViewerCapabilities";
import ComplianceDocumentsPage from "./src/app/admin/compliance-documents/page";
import ComplianceDocumentEditorPage from "./src/app/admin/compliance-documents/editor/page";
import { DocumentCategoryManager } from "@/components/admin/DocumentCategoryManager";
import { HardCopyTrackingList, OrgTrackingSettings } from "@/components/hard-copy-tracking";
import { SignatureCenter } from "@/components/signatures/SignatureCenter";
import { PublicSigningPage } from "@/components/signatures/PublicSigningPage";
import { PublicSurveyPage } from "@/components/surveys/PublicSurveyPage";
import { MemberRecertificationPage } from "@/src/app/member/eligibility/recertification/page";

// Work Compliance Components (Medicaid H.R.1 Work Requirements)
import MemberWorkComplianceDashboard from "@/components/workCompliance/MemberWorkComplianceDashboard";
import WorkActivitySubmissionForm from "@/components/workCompliance/WorkActivitySubmissionForm";
import WorkDocumentUploader from "@/components/workCompliance/WorkDocumentUploader";

import CBOComplianceDashboard from "@/components/workCompliance/CBOComplianceDashboard";
import MemberComplianceHistory from "@/components/workCompliance/MemberComplianceHistory";

// Billing Components
import ClaimsPage from "./src/app/billing/claims/page";
import DocumentProcessingPage from "./src/app/billing/document-processing/page";
import BulkClaimEntryPage from "./src/app/billing/claims/bulk/page";
import NewClaimPage from "./src/app/billing/claims/new/page";
import BillingAnalyticsPage from "./src/app/billing/analytics/page";
import ProgramBillingPage from "./src/app/admin/program-billing/page";
import PMPMBillingPage from "./src/app/admin/pmpm-billing/page";
import PDFTemplatesPage from "./src/app/admin/pdf-templates/page";
import ReportDistributionPage from "./src/app/admin/report-distribution/page";
import ScheduledReportsPage from "./src/app/admin/scheduled-reports/page";
import EcmProgressNotesReportPage from "./src/app/admin/ecm-progress-notes-report/page";
import ClearinghousePage from "./src/app/admin/clearinghouse/page";
import McpBillingRulesPage from "./src/app/admin/mcp-billing-rules/page";
import BillingAIPage from "./src/app/admin/billing-ai/page";
import BillingDataSourcesPage from "./src/app/admin/billing-data-sources/page";
import BillingMlPage from "./src/app/admin/billing-ml/page";
import SyntheticPretrainPage from "./src/app/admin/billing-ml/synthetic-pretrain/page";
import SAMLSSOConfigPage from "./src/app/admin/saml-sso/page";
import HL7ConfigPage from "./src/app/admin/hl7-config/page";
import IHEConfigPage from "./src/app/admin/ihe-config/page";
import EmployerBenefitsManagementPage from "./src/app/admin/employer-benefits/page";
import ProgramConsentAdminPage from "./src/app/admin/program-consent/page";
import CBOReimbursementDashboard from "./src/app/billing/reimbursement/page";
import OutreachBillingPage from "./src/app/billing/outreach/page";
import OutreachWorklistPage from "./src/app/billing/outreach-worklist/page";
import OutreachTypesPage from "./src/app/billing/outreach-types/page";
import ClientBillingPage from "./src/platform/modules/billing/components/ClientBillingPage";
import ConfigMigrationPage from "./src/app/admin/config-migration/page";
import FeatureCapabilitiesPage from "./src/app/admin/feature-capabilities/page";
import StaffProfilesPage from "./src/app/admin/staff-profiles/page";
import TemplateVariablesPage from "./src/app/admin/template-variables/page";

// Service Referral Components
import { ServiceReferralsList, ServiceReferralWorkspace } from "@/components/service-referrals";
import { HelpLineQueue } from "@/components/help-line/HelpLineQueue";
import ProviderReferralResponse from "@/components/referrals/ProviderReferralResponse";

// Webinar Management
import WebinarManagementPage from "./src/app/admin/webinars/page";
import EventRegistrationsPage from "./src/app/admin/event-registrations/page";
import ResourcesManagementPage from "./src/app/admin/resources/page";

// Member Retention & Engagement Intelligence Components
import { MemberRetentionDashboard, SponsorRetentionDashboard, GeographicRiskMap, CommunicationFeaturesPanel } from "@/components/retention";
import { EmergencySituationRoom } from "@/components/emergency/EmergencySituationRoom";
import { EmergencyIncidentList } from "@/components/emergency/EmergencyIncidentList";
import { OutreachActivitiesPage } from "@/components/outreach/OutreachActivitiesPage";
import { MyFormsInbox } from "@/components/forms/MyFormsInbox";
import { EventTrackingPage } from "@/components/events/EventTrackingPage";
import { ActivityReportsPage } from "@/components/reports/ActivityReportsPage";
import { EmergencyCampaignWizard } from "@/components/emergency/EmergencyCampaignWizard";
import { ExternalAlertFeed } from "@/components/emergency/ExternalAlertFeed";
import { CBODataEntry, CaseNotesAnalysis } from "@/components/cbo";
import { EmployeeRetentionDashboard, EmployerBenefitsAnalyticsDashboard, EmployerWorkflowOrchestration } from "@/components/employer";
import EmployerJourneyDashboard from "@/components/employer/EmployerJourneyDashboard";
import EmployerOnboardingWizard from "@/components/employer/EmployerOnboardingWizard";
import EmployerQuickWins from "@/components/employer/EmployerQuickWins";
import EmployerBenchmarking from "@/components/employer/EmployerBenchmarking";
import EmployerAlertsCenter from "@/components/employer/EmployerAlertsCenter";
import EmployerCostTransparency from "@/components/employer/EmployerCostTransparency";

// Workforce Module Components
import WorkforceDashboard from "./src/app/workforce/dashboard/page";
import WorkforceCandidates from "./src/app/workforce/candidates/page";
import WorkforceRoles from "./src/app/workforce/roles/page";
import WorkforceCertifications from "./src/app/workforce/certifications/page";
import WorkforceCurricula from "./src/app/workforce/curricula/page";
import LmsCoursesAdminPage from "./src/app/workforce/courses/page";
import StaffLearnPage from "./src/app/workforce/learn/page";
import WorkforceMentoring from "./src/app/workforce/mentoring/page";
import WorkforceTrainingSchedule from "./src/app/workforce/training-schedule/page";
import LearningHubPage from "./src/app/workforce/hubs/LearningHubPage";
import TrainingHubPage from "./src/app/workforce/hubs/TrainingHubPage";
import WorkforceRoster from "./src/app/workforce/roster/page";
import WorkforceSkillsGap from "./src/app/workforce/skills-gap/page";
import WorkforceCases from "./src/app/workforce/cases/page";
import WorkforceAIAssistant from "./src/app/workforce/ai-assistant/page";
import WorkforceUnifiedDashboard from "./src/app/workforce/unified-dashboard/page";
import WorkforceAssessments from "./src/app/workforce/assessments/page";
import BehavioralAssessmentPage from "./components/behavioral-assessment/BehavioralAssessmentPage";
import { isBehavioralAssessmentAllowed } from "./components/behavioral-assessment/orgGate";
import SocialNeedsScreenerPage from "./components/social-needs-screener/SocialNeedsScreenerPage";

// TCO (Trust Care Organization) Service Referrals Components
import {
  TCOServiceReferralsList,
  TCOReferralCreateForm,
  TCOWorkflowDashboard,
  CareCoordinationHub,
  WorkflowInstanceDetail,
} from "@/components/tco-service-referrals";

import CarePlanDrivenReferrals from "@/components/referrals/CarePlanDrivenReferrals";

// CCH (Community Care Hub) Module Page — v2 tabbed pages
import CCHModulePage from "@/components/v2/CCHModulePage";
import { CCHCareManagementPage, CCHHubAdminPage, CCHReportingPage } from "@/components/v2/CCHTabbedPages";

// TCO Workflow Template Creation Page
import NewWorkflowTemplatePage from "./src/app/care/workflows/templates/new/page";

// TCO Workflow Template Edit Page
import EditWorkflowTemplatePage from "./src/app/care/workflows/templates/edit/page";
import HouseholdDetailView from "@/components/households/HouseholdDetailView";

// Health Plans Components
import { HealthPlansPage, HealthPlanDetail, EmployerPlansManager, MemberEnrollmentsTab, PharmacyBenefitsManager, EmployerBenefitsDashboard } from "@/components/health-plans";
import { HealthConnect } from "@/components/health/HealthConnect";

// Social Media & Media Library Components
import { SocialMediaSettings } from "@/components/settings/SocialMediaSettings";
import { MediaLibrary } from "@/components/media/MediaLibrary";

// Data Privacy & Shield Settings
import { DataPrivacySettings } from "@/components/settings/DataPrivacySettings";
import { InvoiceManagement } from "@/components/billing/InvoiceManagement";
import { InvoicePaymentPortal } from "@/components/billing/InvoicePaymentPortal";
import PublicFormPage from "@/components/forms/PublicFormPage";
import OutreachLandingPage from "@/components/outreach/OutreachLandingPage";
import PartnerConnectPage from "@/components/outreach/PartnerConnectPage";
import PublicSurveyResultsPage from "@/components/forms/PublicSurveyResultsPage";
import SmsOptInPage from "@/components/SmsOptInPage";
import DonationsPublicPage from "@/components/DonationsPublicPage";
import DonorEventPublicPage from "@/components/DonorEventPublicPage";
import FormPreviewPage from "@/components/forms/FormPreviewPage";
// TRUS-545: a form as its own savable page, deep-linked from a member.
import FormPage, { parseFormPagePath } from "@/components/forms/FormPage";
import PublicLinksPage from "@/components/admin/PublicLinksPage";
import { SecurityComplianceDashboard } from "@/components/compliance/SecurityComplianceDashboard";
import { ComplianceDashboard as ECMComplianceDashboard } from "@/components/ecmReminders/ComplianceDashboard";
import { BreakGlassManagement } from "@/components/settings/BreakGlassManagement";
import { ExternalSystemsManagement } from "@/components/settings/ExternalSystemsManagement";
import { FieldSensitivitySettings } from "@/components/settings/FieldSensitivitySettings";
import { OCRTemplateSettings } from "@/components/settings/OCRTemplateSettings";

// Interactive Content (Surveys, Polls, Quizzes)
import { InteractiveContentBuilder } from "@/components/interactive-content";

// Community Programs (LCC and similar orgs)
import { GrantCalendarDashboard } from "@/components/grants/GrantCalendarDashboard";
import DonationsPublicPage from "@/components/DonationsPublicPage";
import DonorEventPublicPage from "@/components/DonorEventPublicPage";
import { DonorPipelineBoard } from "@/components/donors/DonorPipelineBoard";
import { DonationsPage } from "@/components/donors/DonationsPage";
import { DonorEventsPage } from "@/components/donors/DonorEventsPage";
import { CohortBuilder } from "@/components/donors/CohortBuilder";
import DoulasPage from "@/src/app/doulas/page";
import { ProviderAcceptancePortal } from "@/components/provider";
import SponsorCohort from "@/components/sponsor/SponsorCohort";
import SponsorFundedPayments from "@/components/sponsor/SponsorFundedPayments";
import { FundraisingHome } from "@/components/donors/FundraisingHome";
import { MutualAidBoard } from "@/components/mutual-aid/MutualAidBoard";

// Workspace Messaging Components
import { MessagingWorkspace } from "@/components/messaging/MessagingWorkspace";
import { CallsHost } from "@/components/messaging/calls/CallsHost";
import { StaffNotificationsHost } from "@/components/notifications/StaffNotificationsHost";

// Platform Module Components (Marketing & Sales Platform)
import {
  PlatformDashboard,
  WebPresencePage,
  MarketingAutomationPage,
  ContactDirectoryPage,
  SalesPipelinePage,
  RevOpsPage,
  AgentControlPage,
  CustomerSupportPage,
  IntentIntelligencePage,
  VisitorIdentificationPage,
  ColdOutreachPage,
  LeadBuilderPage,
  MarketplaceIntegrationsPage,
  OutreachWorkspace,
} from "./src/platform/admin/pages";
// Triveny.ai-only home page (renders instead of PlatformDashboard when on Triveny)
import TrivenyMyDay from "./src/platform/admin/pages/TrivenyMyDay";
import TrivenyTeamPanel from "./src/platform/admin/pages/TrivenyTeamPanel";
import TrivenyOrganizations from "./src/platform/admin/pages/TrivenyOrganizations";
import TrivenyWorkspaceAdmin from "./src/platform/admin/pages/TrivenyWorkspaceAdmin";
import TrivenyHome from "./src/platform/admin/pages/TrivenyHome";
import TrivenyPublicHome from "./src/platform/admin/pages/TrivenyPublicHome";
import PlatformRolesPage from "./src/platform/admin/pages/PlatformRolesPage";
import TvPillarBar, { pillarForTab } from "./src/platform/shared/components/Layout/TvPillarBar";
import { deepLinkTabForPath } from "./src/platform/shared/utils/deepLinkTab";
import TrivenyConsentGate from "./src/platform/admin/pages/TrivenyConsentGate";
import OrgSwitcher from "./src/platform/admin/components/OrgSwitcher";

// Error Boundary for section-level crash recovery
import { SectionErrorBoundary } from "@/components/ErrorBoundary";

// RFP Feature Components (Vision Y Compromiso CBO Requirements)
import {
  AccessibilityProvider,
  useAccessibility,
} from "@/contexts/AccessibilityContext";
import {
  RoleProvider,
  useRole,
} from "@/contexts/RoleContext";
import {
  GuidedTourProvider,
} from "@/contexts/GuidedTourContext";
import {
  AccessibilitySettingsPanel,
  AccessibilityButton,
  SkipToContent,
} from "@/components/accessibility/AccessibilitySettingsPanel";
import { TourOverlay, TourLauncher } from "@/components/guided-tour/TourOverlay";
import { OfflineBanner, SyncIndicator } from "@/components/offline/OfflineIndicator";
// PromotoraDashboard and SupervisorDashboard removed — consolidated into CmHome v2

// Staff v2 Redesign — lazy-loaded for code splitting (capability-gated behind "staff_v2_redesign")
import { V2Sidebar } from "@/components/v2/V2Sidebar";
import V2PageChrome from "@/components/v2/V2PageChrome";
import { TabbedChrome } from "@/components/weavein/TabbedChrome";
import { V2LayoutShell } from "@/components/v2/V2LayoutShell";
import V2MemberFavoritesBar from "@/components/v2/V2MemberFavoritesBar";
import { useCapability } from "@/hooks/useCapabilities";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
const CmHome = lazyWithRetry(() => import("@/components/v2/CmHome"));
const V2Supervisor = lazyWithRetry(() => import("@/components/v2/V2Supervisor"));
const V2ReferralHub = lazyWithRetry(() => import("@/components/v2/V2ReferralHub"));
// TRUS-757: public provider response page, /referral/respond?token= (no login).
const ProviderRespondPage = lazyWithRetry(() => import("@/components/referral-hub/ProviderRespondPage"));
const V2CalendarHub = lazyWithRetry(() => import("@/components/v2/V2CalendarHub"));
const V2CommsHub = lazyWithRetry(() => import("@/components/v2/V2CommsHub"));
const V2AdminAccess = lazyWithRetry(() => import("@/components/v2/V2AdminAccess"));
const V2InboxComposer = lazyWithRetry(() => import("@/components/v2/V2InboxComposer"));
const V2QuickTaskModal = lazyWithRetry(() => import("@/components/v2/V2QuickTaskModal"));
// Web Presence v2 — the layered (Craft.js) Design Studio editor + its read-only
// preview. Standalone full-screen surfaces reached at /design/v2 and /design/v2/view
// (Triveny). Lazy so @craftjs stays out of the main bundle.
const CraftBuilder = lazyWithRetry(() => import("@/src/platform/modules/web-presence-v2/CraftBuilder"));
const CraftView = lazyWithRetry(() => import("@/src/platform/modules/web-presence-v2/CraftBuilder").then((m) => ({ default: m.CraftView })));
// Suspense fallback for lazy v2 components
const V2Loading = () => (
  <div className="flex h-full items-center justify-center bg-paper">
    <div className="flex flex-col items-center gap-2 text-ink-400">
      <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" opacity="0.25"/><path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
      <span className="text-xs">Loading…</span>
    </div>
  </div>
);
import { TerminologyGlossary } from "@/components/communications/TerminologyGlossary";
import { RoleTerminologyManager } from "@/components/admin/RoleTerminologyManager";
import { TeamAssignmentManager } from "@/components/admin/TeamAssignmentManager";
import { UserGoalsManager } from "@/components/admin/UserGoalsManager";
import { PageCapabilitiesManagement } from "@/components/admin/PageCapabilitiesManagement";
import { NavigationLayoutAdmin } from "@/components/admin/NavigationLayoutAdmin";
import { RolePageAccessManagement } from "@/components/admin/RolePageAccessManagement";
import { OrganizationRolesManager } from "@/components/admin/OrganizationRolesManager";
import { UnifiedAccessControl } from "@/components/admin/UnifiedAccessControl";
import { EffectivePermissionsViewer } from "@/components/admin/EffectivePermissionsViewer";
import { useServiceWorker } from "@/hooks/useServiceWorker";
import { useRFPCapabilities, RFP_CAPABILITIES } from "@/hooks/useRFPCapabilities";
import { authFetch } from '@/lib/authFetch';
import { trackPageView, installErrorTracking, installInteractionTracking } from '@/src/lib/activityTracker';
import { useWebPushNavigation, useWebPushSync } from '@/hooks/useWebPush';
import { WebPushBanner } from '@/components/messaging/WebPushBanner';
import { useDesktopNotificationPermissionOnLogin } from '@/hooks/useDesktopNotificationPermission';
import { parsePushTarget, routeStaffPushTarget } from '@/lib/pushNavigation';
import { OPEN_RECORD_EVENT, parkRecord, parseRecordLink, type RecordLink } from '@/lib/recordDeepLinks';
import { setDatadogUser, clearDatadogUser } from '@/src/providers/DatadogProvider';
import { ImpersonationBanner } from '@/components/impersonation/ImpersonationBanner';
import { getImpersonation, isImpersonating } from '@/lib/impersonation';
import {
  RFPCapabilityGate,
  AccessibilityGate,
  GuidedTourGate,
} from "@/components/common/RFPCapabilityGate";

// SMS Notification Service - Simulation
const sendSMSNotification = async (phoneNumber: string, message: string, memberName: string) => {
  // SECURITY: Removed PII logging - phone numbers, names, and messages should NOT be logged to console
  // This prevents PHI/PII exposure in browser dev tools

  try {
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Simulate successful SMS delivery
    const messageId = `SMS_${Date.now()}`;
    // Only log non-sensitive information
    console.log(`✅ SMS notification sent (Message ID: ${messageId})`);

    return { success: true, messageId };
  } catch (error) {
    console.error(`❌ SMS notification failed:`, error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

// Handle referral status changes and send notifications
const handleReferralStatusChange = async (referral: Referral, newStatus: string, oldStatus: string) => {
  // Only send notifications for specific status changes
  if (newStatus === "Matched" || newStatus === "Authorized") {
    const phoneNumber = "9253609696"; // Your phone number for testing
    const providerName = "Community Health Center"; // In real app, this would be the matched provider
    
    let message = "";
    if (newStatus === "Matched") {
      message = `Hi ${referral.memberName}, great news! We've found a provider for your ${referral.sdohNeeds.join(", ")} needs. ${providerName} will be in touch soon to schedule your services.`;
    } else if (newStatus === "Authorized") {
      message = `Hi ${referral.memberName}, your referral has been authorized! ${providerName} is ready to provide your ${referral.sdohNeeds.join(", ")} services. You should hear from them within 24 hours.`;
    }
    
    try {
      await sendSMSNotification(phoneNumber, message, referral.memberName);
      // SECURITY: Removed referral ID from console log to prevent PII exposure
      console.log(`✅ SMS notification sent for status change: ${oldStatus} → ${newStatus}`);
    } catch (error) {
      // SECURITY: Only log error message, not referral ID
      console.error(`❌ Failed to send SMS notification:`, error);
    }
  }
};



// Use the FrontendReferral type from dataTransform
type Referral = FrontendReferral;

const API_BASE_URL = config.api.baseUrl;

// ============================================================================
// TRIVENY.AI PRODUCT GATING
// ============================================================================
// Triveny.ai is the M&S-only product (config.isTriveny === true). Triveny users
// must NEVER reach healthcare tabs. We enforce this two ways:
//  1. Sidebar filters healthcare groups (see SidebarNavigation.tsx).
//  2. If a user somehow gets into a healthcare tab anyway (deep link,
//     localStorage holdover from a WeaveIn session, URL manipulation), the
//     useEffect below redirects them to the platform-dashboard on every
//     activeTab change.
// The set lists tabs that are explicitly M&S/shared. Anything NOT in this set
// is treated as healthcare and redirected.
const TRIVENY_ALLOWED_TABS = new Set<string>([
  "home",
  "dashboard",
  // Triveny.ai full-bleed home (landing surface — Design / Market / Engage launcher)
  "triveny-home",
  // M&S platform tabs
  "platform-dashboard",
  "platform-marketing-analytics",
  "platform-web-presence",
  "platform-marketing",
  "platform-contacts",
  "platform-contacts-view",
  "platform-contacts-new",
  "platform-contacts-edit",
  "platform-sales",
  "platform-revops",
  "platform-agents",
  "weavein-agents",
  "platform-invoices",
  "platform-support",
  "platform-team",
  "platform-intelligence",
  "platform-visitor-id",
  "platform-cold-outreach",
  "platform-lead-builder",
  // Unified Outreach workspace — Triveny-only wrapper that tabs across
  // Visitor ID + Lead Builder + Cold Outreach + Funnel. The three individual
  // tabs above stay reachable so existing deep links keep working; they
  // redirect into the workspace's matching sub-tab when isTriveny.
  "platform-outreach",
  "platform-marketplace",
  // Tenant admin — partially allowed for Triveny.
  //   • cbo-management: Triveny tenants only ever have ONE org (their own
  //     tenant row, e.g. Triveny Demo Co = cbo_id 609). CBOManagement
  //     now branches on config.isTriveny to render a single-org view
  //     that routes "Configure" to org-settings.
  //   • org-settings: needs an organizationId. When navigated without one
  //     (e.g. from the command palette), the App auto-defaults to the
  //     user's activeOrgId so the page can render.
  //   • user-management: backend /api/cbos/{id}/users* endpoints are
  //     dual-auth via get_current_user → platform JWTs work. Allowed
  //     so the Users icon on the My Organization row actually lands on
  //     the user-management page instead of redirecting to My Day.
  //   • client-billing still hits /api/cbos billing endpoints that don't
  //     yet have dual-auth wiring — kept OUT of the allowed set until a
  //     Triveny-side equivalent exists.
  "cbo-management",
  "org-settings",
  "user-management",
  "user-permissions",
  "platform-roles",
  "access-control",
  "nav-layout",
  "data-privacy-settings",
  "security-compliance",
  "external-systems",
  // Settings + profile
  "settings",
  "profile",
  "notifications",
  "preferences",
  "user-preferences",
  // Shared workflows/forms (used by M&S too)
  "workflows",
  "forms",
  "form-builder",
  // Unified inbox surfaces — launched from TrivenyHome top nav (no longer in
  // the sidebar). workspace-messaging is the combined messaging workspace
  // (the real Inbox, member conversations included); 'inbox' was the 2-Way
  // Inbox and now redirects there (TRUS-709), kept for old links.
  "workspace-messaging",
  "inbox",
  // Auth flows
  "login",
  "register",
  "forgot-password",
  "reset-password",
  "verify-2fa",
]);

function AppContent() {
  // A page pop-out starts on its own page; everything else restores below.
  const [activeTab, setActiveTab] = useState(() => PAGE_POPOUT?.tab ?? "home");
  const [permissionsViewerContext, setPermissionsViewerContext] = useState<{ userId: number; userName: string } | null>(null);
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  // Copilot — the in-app agent. `copilotSeed` carries a question typed into the
  // palette so it is asked on open rather than retyped.
  const [isCopilotOpen, setIsCopilotOpen] = useState(false);
  const [copilotSeed, setCopilotSeed] = useState<string | undefined>(undefined);
  const openCopilot = useCallback((question?: string) => {
    setCopilotSeed(question);
    setIsCopilotOpen(true);
  }, []);
  // Listen for the open-command-palette custom event (fired from
  // TrivenyMyDay's Quick Actions button and anywhere else that wants
  // to surface the palette without coupling to AppContent's state).
  useEffect(() => {
    const handler = () => setIsCommandPaletteOpen(true);
    window.addEventListener('open-command-palette', handler);
    return () => window.removeEventListener('open-command-palette', handler);
  }, []);
  // (The desktop app's notification permission prompt runs after sign-in:
  // useDesktopNotificationPermissionOnLogin below. Auto-update is handled by
  // <DesktopUpdateBanner /> — a user-controlled "Install & Restart".)
  useEffect(() => {
    // Prime the auth-token cache (migrates a legacy localStorage token into the OS
    // keychain on desktop). Safe no-op on web.
    import('@/lib/authFetch').then((m) => m.primeAuthToken()).catch(() => {});
    // Observability agent: capture uncaught errors + unhandled rejections the user hits.
    installErrorTracking();
    // Activity report: which section (tab) and which fields a user worked in.
    // Field LABELS only, never values (TRUS-664).
    installInteractionTracking();
    // Mirror webview console.* to the Rust log (stdout + app log dir) so the
    // updater/notification flow is diagnosable. Desktop only.
    if (typeof window !== 'undefined' && '__TAURI__' in window) {
      import('@tauri-apps/plugin-log').then((m) => m.attachConsole()).catch(() => {});
    }
  }, []);
  // The page in front, for "Open in…" and File ▸ New Window. A ref so the
  // menu listener below (registered once) always reads the current page.
  const currentPageRef = React.useRef<AppTabSpec | null>(null);
  /** In a tabbed window: the tab in front (AppTabsHost reports it). */
  const popoutHostFrontRef = React.useRef<AppTabSpec | null>(null);
  /** Main window: open the page in front in a new in-window tab (File ▸ New Tab). */
  const newMainTabRef = React.useRef<(() => void) | null>(null);
  // Desktop File ▸ New Messaging Window (Cmd/Ctrl+Shift+M) and File ▸ New
  // Window (Cmd/Ctrl+Shift+N, the page you are on). The shell sends the focused
  // window's label so exactly one window — main or a pop-out — acts. AppContent
  // mounts in pop-outs too, so this one listener covers every window. Pages
  // inside a tabbed window's iframes report that window's label, so they stand
  // down and let the tabbed window answer.
  useEffect(() => {
    if (typeof window === 'undefined' || !('__TAURI__' in window)) return;
    if (isFramedDocument()) return;
    const unlisteners: Array<() => void> = [];
    let cancelled = false;
    const forMe = (payload: string | null) => {
      const mine = currentDesktopWindowLabel();
      return !(payload && mine && payload !== mine);
    };
    import('@tauri-apps/api/event')
      .then(async ({ listen }) => {
        const fns = [
          await listen<string | null>('menu://new-messaging-window', (e) => {
            if (!forMe(e.payload)) return;
            void openMessagingPopout({ tabs: [{ kind: 'members' }] }, 'window').then((r) => {
              if (!r.ok) console.error('New messaging window failed:', r.error);
            });
          }),
          // File ▸ New Tab (Cmd/Ctrl+T): a new tab in the main window's own strip.
          await listen<string | null>('menu://new-tab', (e) => {
            if (!forMe(e.payload)) return;
            newMainTabRef.current?.();
          }),
          await listen<string | null>('menu://new-window', (e) => {
            if (!forMe(e.payload)) return;
            // A tabbed window answers for its front tab (AppTabsHost registers it).
            const spec = currentPageRef.current ?? popoutHostFrontRef.current;
            if (!spec) return;
            void openAppPopout({ tabs: [spec] }, 'window').then((r) => {
              if (!r.ok) console.error('New window failed:', r.error);
            });
          }),
        ];
        if (cancelled) fns.forEach((fn) => fn());
        else unlisteners.push(...fns);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      unlisteners.forEach((fn) => fn());
    };
  }, []);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedReferralId, setSelectedReferralId] = useState<string | number | null>(null);
  // A page pop-out (a tab) reopened on a record page carries the record id in its params.
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(() => recordIdFromSpec(PAGE_POPOUT, 'member'));
  const [selectedIncidentId, setSelectedIncidentId] = useState<number | null>(null);
  const [selectedIncidentName, setSelectedIncidentName] = useState<string>('');
  const [selectedEnrollmentId, _setSelectedEnrollmentId] = useState<string | null>(
    () => localStorage.getItem('selectedEnrollmentId')
  );
  const setSelectedEnrollmentId = (id: string | null) => {
    _setSelectedEnrollmentId(id);
    if (id) { localStorage.setItem('selectedEnrollmentId', id); }
    else { localStorage.removeItem('selectedEnrollmentId'); }
  };

  // Triveny.ai product gating: if a Triveny user lands on a healthcare-only
  // tab (deep link, stale localStorage, etc.), force-redirect to the M&S
  // platform dashboard. Sidebar filtering already hides these from the menu;
  // this is the second line of defense for URL-based / programmatic nav.
  useEffect(() => {
    if (config.isTriveny && !TRIVENY_ALLOWED_TABS.has(activeTab)) {
      setActiveTab("platform-dashboard");
    }
  }, [activeTab]);

  // Product-aware document.title — the static <title> in index.html says
  // "WeaveIn"; this swaps it to "Triveny.ai" once React mounts so Triveny
  // browser tabs / bookmarks / share previews show the correct brand.
  useEffect(() => {
    document.title = config.app.name;
  }, []);

  // Sync selectedEnrollmentId from localStorage when navigating to enrollments-view
  // (e.g. from notification deep-links that set localStorage before navigating)
  useEffect(() => {
    if (activeTab === 'enrollments-view') {
      const stored = localStorage.getItem('selectedEnrollmentId');
      if (stored && stored !== selectedEnrollmentId) {
        _setSelectedEnrollmentId(stored);
      } else if (!stored && selectedEnrollmentId) {
        // No deep-linked enrollment id → show the enrollments LIST, not a
        // stale detail. (Palette / program drill-in navigate here without
        // setting selectedEnrollmentId.)
        _setSelectedEnrollmentId(null);
      }
    }
  }, [activeTab]);

  const [selectedHealthPlanId, setSelectedHealthPlanId] = useState<string | null>(null);
  const [selectedEmployerId, setSelectedEmployerId] = useState<string | null>(null);
  const [selectedTCOReferralId, setSelectedTCOReferralId] = useState<number | null>(null);
  const [selectedTCOTemplateId, setSelectedTCOTemplateId] = useState<number | null>(null);
  const [selectedTCOInstanceId, setSelectedTCOInstanceId] = useState<number | null>(null);
  const [selectedHouseholdId, setSelectedHouseholdId] = useState<number | null>(() => {
    const id = recordIdFromSpec(PAGE_POPOUT, 'household');
    return id ? Number(id) : null;
  });
  const [lastFormProcessingMethod, setLastFormProcessingMethod] = useState<boolean>(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return config.isTriveny && localStorage.getItem('triveny_rail_collapsed') === '1';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    if (config.isTriveny) {
      try {
        localStorage.setItem('triveny_rail_collapsed', sidebarCollapsed ? '1' : '0');
      } catch {
        /* ignore */
      }
    }
  }, [sidebarCollapsed]);
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem('sidebar_width');
    return saved ? parseInt(saved) : 256; // Default 256px
  });
  const [navigationHistory, setNavigationHistory] = useState<string[]>(["home"]);

  // State for unpublished referrals manual matching workflow
  const [referralForManualMatching, setReferralForManualMatching] = useState<Referral | null>(null);
  const [selectedProviderForMatching, setSelectedProviderForMatching] = useState<any | null>(null);

  // State for provider communications
  const [selectedProviderForComms, setSelectedProviderForComms] = useState<any | null>(null);

  // View and Edit states
  const [viewingReferralId, setViewingReferralId] = useState<string | number | null>(null);
  const [editingReferralId, setEditingReferralId] = useState<string | number | null>(null);
  
  // Authentication state
  const { user, token, loading: authLoading, logout, timeRemaining, sessionWarningSeconds, extendSession } = useAuth();
  const { currentRole: orgSpecificRole } = useRole();

  // Effective role: prefer org-specific role, fallback to global user role
  const effectiveRole = orgSpecificRole || user?.role;
  const isOrgAdmin = effectiveRole === "super_admin" || effectiveRole === "ops_admin" || effectiveRole === "org_admin";

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(false);

  // Re-consent state for existing sessions
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consentStatus, setConsentStatus] = useState<{
    terms_needs_update: boolean;
    hipaa_needs_update: boolean;
    terms_current_version?: string;
    hipaa_current_version?: string;
    terms_signed_version?: string;
    hipaa_signed_version?: string;
    all_consents_current: boolean;
  } | null>(null);

  // CBO Management state
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [cboFormOpen, setCboFormOpen] = useState(false);
  const [settingsOrgId, setSettingsOrgId] = useState<number | null>(null);
  const [settingsOrgName, setSettingsOrgName] = useState<string>('');
  // Independent Client Billing & Payments page opened for a specific org (the $
  // icon in CBOManagement). Distinct from Org Settings → Billing tab.
  const [billingOrgId, setBillingOrgId] = useState<number | null>(null);
  const [billingOrgName, setBillingOrgName] = useState<string>('');

  // Sponsor context state
  const [activeSponsorId, setActiveSponsorId] = useState<number | null>(() => {
    const sponsorId = localStorage.getItem('active_sponsor_id');
    return sponsorId ? parseInt(sponsorId) : null;
  });
  const [cboRefreshTrigger, setCboRefreshTrigger] = useState(0);

  // Organization ID state - reactive to localStorage changes.
  //
  // TRUS-530: `activeOrgId || 1` is NOT a neutral default — organization 1 is
  // Trust Care Organization, the platform org. TRUS-435 leaves active_cbo_id
  // UNSET for a super_admin whose JWT has no cbo_id until OrganizationContext
  // resolves it, so that fallback aims writes at the platform org while the
  // admin believes they are in their own workspace.
  //
  // Every surface that WRITES org-scoped configuration or member data is now
  // gated on `&& activeOrgId` in its tab condition and passes the raw value —
  // matching the pattern organization-roles / data-privacy-settings already
  // used. The remaining `|| 1` call sites are deliberate:
  //   * MemberRetentionDashboard / GeographicRiskMap / ProgramMarketplace /
  //     ProgramIntelligenceCatalog — analytics and catalog READS. The window is
  //     one API call, and the only users who reach it with an unresolved org are
  //     platform admins, for whom cbo 1 data is not an exposure.
  //   * UserManagement — carries its own targeted guard (the invite flow is
  //     blocked while unresolved) so the read-only roster still renders.
  //   * EffectivePermissionsViewer — read-only.
  // Adding a new org-scoped WRITE surface? Gate it; don't reach for `|| 1`.
  // Pinned by src/__tests__/unit/orgScopedWriteSurfaces.test.ts.
  const [activeOrgId, setActiveOrgId] = useState<number | null>(() => {
    const orgId = localStorage.getItem('active_cbo_id');
    return orgId ? parseInt(orgId) : null;
  });

  // Active organization name - resolved from API when activeOrgId changes
  const [activeOrgName, setActiveOrgName] = useState<string>("Organization");

  // Employer ID state - reactive to localStorage changes
  const [activeEmployerId, setActiveEmployerId] = useState<string | null>(() => {
    return localStorage.getItem('active_employer_id');
  });

  // Listen for localStorage changes (from OrganizationSwitcher)
  useEffect(() => {
    const handleStorageChange = () => {
      const sponsorId = localStorage.getItem('active_sponsor_id');
      const newSponsorId = sponsorId ? parseInt(sponsorId) : null;
      if (newSponsorId !== activeSponsorId) {
        console.log('📍 App.tsx: activeSponsorId updated from localStorage:', newSponsorId);
        setActiveSponsorId(newSponsorId);
      }

      const orgId = localStorage.getItem('active_cbo_id');
      const newOrgId = orgId ? parseInt(orgId) : null;
      if (newOrgId !== activeOrgId) {
        console.log('📍 App.tsx: activeOrgId updated from localStorage:', newOrgId);
        setActiveOrgId(newOrgId);
      }

      const employerId = localStorage.getItem('active_employer_id');
      if (employerId !== activeEmployerId) {
        console.log('📍 App.tsx: activeEmployerId updated from localStorage:', employerId);
        setActiveEmployerId(employerId);
      }
    };

    // Check on window focus (when switching tabs)
    window.addEventListener('focus', handleStorageChange);

    // Check periodically (every 500ms) for changes
    const interval = setInterval(handleStorageChange, 500);

    return () => {
      window.removeEventListener('focus', handleStorageChange);
      clearInterval(interval);
    };
  }, [activeSponsorId, activeOrgId, activeEmployerId]);

  // Resolve active organization name when activeOrgId changes
  useEffect(() => {
    if (!activeOrgId || !token) {
      setActiveOrgName("Organization");
      return;
    }
    // Triveny tenant_users don't belong to healthcare CBOs — getMyOrganizations
    // hits /api/cbos/users/me/organizations which will 401 with a platform JWT.
    // Use the tenant name from the JWT/localStorage user instead.
    if (config.isTriveny) {
      try {
        const cached = localStorage.getItem('user');
        if (cached) {
          const parsed = JSON.parse(cached);
          setActiveOrgName(parsed?.tenant_name || parsed?.tenant_slug || 'Triveny');
          return;
        }
      } catch {
        /* fall through */
      }
      setActiveOrgName('Triveny');
      return;
    }
    organizationsAPI.getMyOrganizations().then(orgs => {
      const match = orgs.find(o => o.cbo_id === activeOrgId);
      setActiveOrgName(match?.organization?.name || "Organization");
    }).catch(() => {
      setActiveOrgName("Organization");
    });
  }, [activeOrgId, token]);

  // Behavioral Assessment is a pilot feature gated to specific orgs
  // (Sycamores / Trust Care). Hide the page for everyone else. The backend
  // also 403s for non-allowed orgs (defense in depth). See orgGate.ts.
  const behavioralAssessmentAllowed = React.useMemo(
    () => isBehavioralAssessmentAllowed(activeOrgId, activeOrgName),
    [activeOrgId, activeOrgName],
  );

  // Social Needs Screener is gated per-org via the feature-capability registry
  // (organization_capabilities.social_needs_screening). The backend independently
  // 403s non-enabled orgs (defense in depth) via check_capability_enabled.
  const { enabled: socialNeedsScreenerAllowed } = useCapability(
    activeOrgId,
    "social_needs_screening",
  );

  // FFS transition cohort (TRUS-739) is gated per org by its own capability,
  // ffs_transition (off by default: it flags members by an aid-code proxy for
  // UIS membership). A direct URL or a remembered tab shows the standard
  // no-access state; the API also 403s (capability_disabled).
  const { enabled: ffsTransitionAllowed, loading: ffsTransitionLoading } = useCapability(
    activeOrgId,
    "ffs_transition",
  );

  // WeaveIn Verify (H.R. 1 hours) is gated per org on hr1_verify. The sidebar
  // item comes from page_capabilities (medicaid.verify); this stops a direct URL
  // or a remembered tab from rendering it elsewhere. The API also 403s.
  const { enabled: verifyAllowed, loading: verifyLoading } = useCapability(activeOrgId, "hr1_verify");

  // Auto-default settingsOrgId when the user lands on org-settings without
  // an explicit org context (e.g. via the command palette quick-actions).
  // Without this, the route handler's `settingsOrgId &&` guard fails and
  // the user sees a blank page. On Triveny the tenant only ever manages
  // their own org, so falling back to activeOrgId is unambiguous; on
  // WeaveIn the same fallback is the user's currently-active CBO which
  // is the right default for the "Branding/Billing/etc." palette shortcuts.
  useEffect(() => {
    if (activeTab === 'org-settings' && !settingsOrgId && activeOrgId) {
      setSettingsOrgId(activeOrgId);
      setSettingsOrgName(activeOrgName || 'Organization');
    }
  }, [activeTab, settingsOrgId, activeOrgId, activeOrgName]);

  const isAuthenticated = !!user;
  // The on-screen launcher. Asked here rather than inside the sidebar so the
  // probe runs once for the app, and so the sidebar stays a presentational
  // component that is handed what to render.
  //
  // Declared AFTER isAuthenticated, not beside the other Copilot state at the
  // top: a hook call that reads a const declared further down is a temporal
  // dead zone error, and `const` hoists without initialising.
  const copilotLauncher = useCopilotLauncher(isAuthenticated && !config.isTriveny);
  // Browser push (TRUS-668): re-register this browser after sign-in when the
  // user already allowed notifications. Never prompts; skips the desktop app
  // and pop-out / iframe windows (lib/webPush).
  // Not while a super admin is viewing as someone (TRUS-747): this browser
  // must never be registered for that person's notifications.
  useWebPushSync(isAuthenticated && !config.isTriveny && !isImpersonating(), 'staff');
  // Desktop app (Tauri): notifications are on by default, so ask the OS for
  // permission on the first launch after sign-in (no-op once decided, in the
  // browser and in pop-out windows).
  useDesktopNotificationPermissionOnLogin(isAuthenticated);
  // Desktop: open the encrypted offline store once authenticated (needs user/cbo
  // identity). The offline hooks use this singleton; no React context needed.
  useEffect(() => {
    if (!isAuthenticated) return;
    // TRUS-747: never copy a viewed-as user's records into this machine's store.
    if (isImpersonating()) return;
    if (typeof window === 'undefined' || !('__TAURI__' in window)) return;
    // One window owns the encrypted store and its background sync. A messaging
    // pop-out opening it too would run migrations and sync concurrently.
    if (isSecondaryAppWindow()) return;
    import('@/lib/offline/desktopOfflineService')
      .then((m) => m.initializeOfflineDb())
      .catch((e) => console.error('offline init failed', e));
  }, [isAuthenticated]);
  const currentUser = user?.name || null;
  // TrivenyRail expects an object — its userInitials helper reads .name/.email.
  // The legacy `currentUser` string is preserved for the 7+ healthcare-side
  // callsites that consume the SidebarNavigation's plain-string prop.
  const trivenyCurrentUser = user
    ? { id: user.id, name: user.name, email: user.email }
    : null;
  const { branding } = useBranding();

  // Single source of truth for "is this user allowed inside the platform UI?"
  // Healthcare roles: super_admin / ops_admin / org_admin manage CBO tenants
  //   and have access to the platform module via dual-auth.
  // Platform roles: platform_admin / tenant_admin live in
  //   platform_shared.tenant_users (Triveny.ai users); they only have
  //   platform access by definition.
  // tenant_user / tenant_viewer roles get the same surfaces but reduced
  //   write access — gated at the endpoint level by the backend, not here.
  const platformRoles = new Set([
    'super_admin',
    'ops_admin',
    'org_admin',
    'platform_admin',
    'tenant_admin',
    'tenant_user',
    'tenant_viewer',
  ]);
  const canAccessPlatform = !!user && platformRoles.has(user.role);

  // Internal operator console (RevOps pricing, Sales pipeline, RevOps Invoices).
  // These are INTERNAL operator surfaces — org_admin (a client-org admin) must NOT
  // see them. Org admins manage their own billing under Client Billing & Payments
  // (their assigned plan + only the invoices sent/assigned to them). Excludes
  // org_admin / tenant_user / tenant_viewer.
  const internalOpsRoles = new Set(['super_admin', 'ops_admin', 'platform_admin', 'tenant_admin']);
  const canAccessInternalOps = !!user && internalOpsRoles.has(user.role);

  // Staff v2 redesign — capability gate for rollback. On Triveny we don't
  // expose this gate (the platform has its own design system) AND the
  // V2 is now the only WeaveIn UX — the staff_v2_redesign gate is retired so
  // there's a single sidebar/layout to maintain. Triveny keeps its own rail
  // (TrivenyRail), so V2 applies to WeaveIn only. (Was gated on the
  // staff_v2_redesign capability per-org; flipped on for all WeaveIn orgs.)
  const isV2Enabled = !config.isTriveny;

  // V2 sidebar collapse state — drives main content margin
  const [v2SidebarCollapsed, setV2SidebarCollapsed] = useState(false);

  // V2 member favorites filter
  const [v2FavoriteIds, setV2FavoriteIds] = useState<number[] | null>(null);

  // V2 quick action modals (command palette → compose, new task, new note)
  const [v2QuickAction, setV2QuickAction] = useState<'compose' | 'task' | 'note' | null>(null);
  // Set alongside v2QuickAction='task' when opening an EXISTING task for edit
  // (My Day "Open" buttons); null means the modal creates a new task.
  const [v2EditTaskId, setV2EditTaskId] = useState<number | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.action === 'compose') setV2QuickAction('compose');
      else if (detail?.action === 'new-task') { setV2EditTaskId(null); setV2QuickAction('task'); }
      else if (detail?.action === 'new-note') setV2QuickAction('note');
      else if (detail?.action === 'edit-task' && detail?.taskId) {
        setV2EditTaskId(Number(detail.taskId));
        setV2QuickAction('task');
      }
    };
    window.addEventListener('v2-quick-action', handler);
    return () => window.removeEventListener('v2-quick-action', handler);
  }, []);

  // V2 role preview (super_admin/ops_admin can simulate other roles for demos)
  const [v2PreviewRole, setV2PreviewRole] = useState<string | null>(null);
  // Fetched from /api/organizations/{cbo}/available-roles so the preview
  // dropdown shows the org's CUSTOM role display names (e.g. Somos:
  // "Economic Justice Manager", "FUERTES Coordinator", "Promotora")
  // instead of generic system_role slugs. Hardcoded fallback preserved
  // for the no-org case (anonymous / pre-org-switch).
  // `id` is the role_terminology.id (present for org-specific roles); undefined
  // for the hardcoded system-role fallbacks below. It lets the sidebar preview
  // scope nav-config by id, which is the only way custom org roles resolve.
  const [v2PreviewRoles, setV2PreviewRoles] = useState<Array<{ value: string; label: string; id?: number }>>([]);
  useEffect(() => {
    if (!activeOrgId) {
      setV2PreviewRoles([
        { value: 'case_manager', label: 'Case Manager' },
        { value: 'promotora', label: 'Promotora' },
        { value: 'supervisor', label: 'Supervisor' },
        { value: 'org_admin', label: 'Org Admin' },
        { value: 'viewer', label: 'Viewer' },
      ]);
      return;
    }
    const canPreview =
      user?.role === 'super_admin' ||
      user?.role === 'ops_admin' ||
      orgSpecificRole === 'super_admin' ||
      orgSpecificRole === 'ops_admin';
    if (!canPreview) {
      setV2PreviewRoles([]);
      return;
    }
    // Use authFetch so Authorization + X-CBO-ID are set automatically.
    import('@/lib/authFetch').then(({ authFetchJson }) => {
      authFetchJson<Array<{ id: number; type: string; display_name: string }>>(
        `/api/organizations/${activeOrgId}/available-roles?include_system_roles=false`,
      )
        .then((rows) => {
          setV2PreviewRoles(
            (rows || []).map((r) => ({ value: r.type, label: r.display_name || r.type, id: r.id })),
          );
        })
        .catch(() => {
          // On failure, fall back to a sensible default
          setV2PreviewRoles([
            { value: 'staff', label: 'Staff' },
            { value: 'supervisor', label: 'Supervisor' },
            { value: 'org_admin', label: 'Org Admin' },
            { value: 'viewer', label: 'Viewer' },
          ]);
        });
    });
  }, [activeOrgId, user?.role, orgSpecificRole]);

  // When v2 is enabled, add theme class to body for Radix portals
  useEffect(() => {
    if (isV2Enabled) {
      document.body.classList.add('v2-theme-portals');
    } else {
      document.body.classList.remove('v2-theme-portals');
    }
    return () => document.body.classList.remove('v2-theme-portals');
  }, [isV2Enabled]);

  // When v2 is enabled, all users land on My Day
  useEffect(() => {
    if (isV2Enabled && (activeTab === 'home' || activeTab === 'promotora-dashboard' || activeTab === 'supervisor-dashboard')) {
      setActiveTab('v2-home');
    }
  }, [isV2Enabled, activeTab]);

  // Check consent status for authenticated users (existing sessions)
  useEffect(() => {
    const checkConsentStatus = async () => {
      if (!isAuthenticated || !user?.email || !token) {
        return;
      }
      // Triveny is not a HIPAA-covered entity — the consent module
      // (terms-of-use + HIPAA authorization) doesn't apply. ms-api
      // doesn't expose /api/consent/status anyway, so calling it on
      // Triveny just produces a 404 in the console. Skip cleanly.
      if (config.isTriveny) {
        return;
      }

      try {
        // Note: /api/consent/status uses auth token to identify user, no email path param needed
        const response = await authFetch(`${config.weaveinApi.baseUrl}/api/consent/status`);

        if (response.ok) {
          const status = await response.json();
          console.log('📋 Consent status check for existing session:', status);

          if (status.terms_needs_update || status.hipaa_needs_update) {
            console.log('⚠️ User requires re-consent, showing modal');
            console.log('   Terms: current=' + status.terms_current_version + ', signed=' + status.terms_signed_version + ', needs_update=' + status.terms_needs_update);
            console.log('   HIPAA: current=' + status.hipaa_current_version + ', signed=' + status.hipaa_signed_version + ', needs_update=' + status.hipaa_needs_update);
            setConsentStatus(status);
            setShowConsentModal(true);
          } else {
            console.log('✅ User consents are current, no re-consent needed');
          }
        }
      } catch (error) {
        console.error('Failed to check consent status:', error);
      }
    };

    checkConsentStatus();
  }, [isAuthenticated, user?.email, token]);

  // First-party page-view tracking — records each tab/page the user opens to
  // frontend_activity_log (powers the admin Access & Activity report). Fire-and-forget.
  useEffect(() => {
    if (isAuthenticated && activeTab) {
      trackPageView(activeTab);
    }
  }, [activeTab, isAuthenticated]);

  // Tell the support widget which screen the user is on.
  //
  // This app is a single-route SPA: the URL stays at "/" and navigation is an
  // activeTab change, so the widget cannot infer the screen from location. Left
  // to guess, it read the page's first heading — which reported the MEMBER
  // PROFILE (e.g. "297") for a problem the user hit inside a progress-note form
  // opened over it. Declaring the tab + the selected record here, and tagging
  // overlays with data-tsw-screen, is what makes a ticket name the real screen.
  useEffect(() => {
    const w = window as any;
    if (!w.TrustSupportWidget || typeof w.TrustSupportWidget.setScreen !== 'function') return;
    const page = getPageDescriptor(activeTab);
    try {
      w.TrustSupportWidget.setScreen({
        tab: activeTab,
        label: page?.label || activeTab,
        section: page?.group || null,
        entity_type: selectedMemberId ? 'member' : null,
        entity_id: selectedMemberId || null,
        entity_label: selectedMemberId ? `Member ${selectedMemberId}` : null,
      });
    } catch {
      /* context reporting must never break navigation */
    }
  }, [activeTab, selectedMemberId]);

  // Tag Datadog RUM with the current user/org so existing session replays and
  // RUM events become attributable to a specific person (complements the
  // first-party Access & Activity report). Clears on logout.
  useEffect(() => {
    if (user?.id) {
      const impersonation = getImpersonation();
      setDatadogUser({
        id: String(user.id),
        email: user.email,
        name: user.name,
        role: effectiveRole,
        organizationId: activeOrgId ? String(activeOrgId) : undefined,
        organizationName: activeOrgName || undefined,
        // TRUS-747: RUM sessions recorded during View as user name the admin.
        impersonatorEmail: impersonation?.actorEmail || undefined,
        impersonationSessionId: impersonation ? String(impersonation.sessionId) : undefined,
      });
    } else {
      clearDatadogUser();
    }
  }, [user?.id, user?.email, user?.name, effectiveRole, activeOrgId, activeOrgName]);

  // API integration hooks - only fetch when authenticated
  const {
    data: referrals,
    loading: referralsLoading,
    error: referralsError,
    createReferral: apiCreateReferral,
    updateReferralStatus: apiUpdateReferralStatus,
    refetch: fetchReferrals,
  } = useReferrals(undefined, isAuthenticated && !config.isTriveny);

  // CBOs / metrics / referrals are healthcare concepts. On Triveny the
  // backend has no CBO directory and the platform JWT won't authenticate
  // against /api/cbos or /api/referrals — pass `false` so the hooks
  // never fire those requests.
  const { data: cbos } = useCBOs(isAuthenticated && !config.isTriveny);
  const { data: metrics, loading: metricsLoading, error: metricsError } = useMetrics(
    isAuthenticated && !config.isTriveny,
  );
  const { data: healthData } = useHealthCheck();
  const { data: providerCount } = useProviderCount();

  // Suppress unused variable warnings
  // console.log('Debug - cbos:', cbos?.length, 'health:', healthData, 'providerCount:', providerCount);
  // Function to update referral status and send notifications
  const updateReferralStatus = async (referralId: string | number, newStatus: string) => {
    const success = await apiUpdateReferralStatus(referralId, newStatus);
    if (success) {
      // Find the referral to trigger SMS notification
      const referral = referrals?.find(r => String(r.id) === String(referralId));
      if (referral) {
        const oldStatus = referral.status;
        const updatedReferral = { ...referral, status: newStatus as any };
        handleReferralStatusChange(updatedReferral, newStatus, oldStatus);
      }
    }
  };

  // Detect mobile/tablet view for responsive layout
  useEffect(() => {
    const checkMobile = () => {
      const width = window.innerWidth;
      const isPhone = width < 768; // sm breakpoint - phones
      const tablet = width >= 768 && width < 1024; // md breakpoint - tablets/landscape
      const mobile = width < 1024; // lg breakpoint

      setIsMobile(mobile);
      setIsTablet(tablet);

      // Phones: close sidebar by default (overlay mode)
      // Tablets: sidebar visible but can be toggled
      if (isPhone) {
        setSidebarOpen(false);
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Redirect member-only users to member portal (only on initial login, not on navigation)
  useEffect(() => {
    // Skip if already handled this session or if we're just navigating
    const memberRedirectHandled = sessionStorage.getItem('member_redirect_handled');
    if (memberRedirectHandled) {
      return;
    }

    if (!authLoading && user && user.role === 'member') {
      // Mark as handled to prevent re-triggering on back navigation
      sessionStorage.setItem('member_redirect_handled', 'true');
      // User has 'member' role - they should use the member portal instead
      console.log('Member-role user detected, redirecting to member portal');
      // Clear the staff auth token
      localStorage.removeItem('auth_token');
      localStorage.removeItem('session_start');
      // Redirect to member login
      window.location.href = '/member/login';
    }

    // Viewer role protection - always redirect viewers to capabilities page as their default
    if (!authLoading && user && user.role === 'viewer') {
      const allowedViewerTabs = ['viewer-capabilities', 'signup'];
      // Always redirect to viewer-capabilities unless on signup page
      if (!allowedViewerTabs.includes(activeTab)) {
        console.log('Viewer-role user detected, redirecting to viewer capabilities page');
        setActiveTab('viewer-capabilities');
        persistActiveTab('viewer-capabilities');
      }
    }
  }, [authLoading, user, activeTab]);

  // Restore saved tab after authentication is verified
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      let savedTab = localStorage.getItem('activeTab');
      // Single-experience: migrate any stale legacy V1 referral tab to the hub.
      if (savedTab && LEGACY_REFERRAL_TAB_IDS.includes(savedTab)) {
        savedTab = 'v2-referrals';
        persistActiveTab(savedTab);
      }
      // Triveny.ai default: when a Triveny user logs in, send them to the
      // M&S dashboard regardless of any stale activeTab from a WeaveIn session
      // (and ignore healthcare tabs that may be in localStorage from a
      // previous WeaveIn login on the same browser).
      if (config.isTriveny) {
        if (savedTab && TRIVENY_ALLOWED_TABS.has(savedTab) && savedTab !== "home") {
          setActiveTab(savedTab);
        } else {
          // Triveny default landing: the full-bleed home surface
          // (Design / Market / Engage launcher). My Day + Inbox are launched
          // from the home's top nav, not the sidebar.
          setActiveTab("triveny-home");
        }
        return;
      }
      if (savedTab && savedTab !== "home") {
        setActiveTab(savedTab);
      }
    }
  }, [authLoading, isAuthenticated]);

  // Triveny.ai: unauthenticated visitors have nowhere to go (no public
  // marketing site exists yet — Triveny is a B2B M&S platform with a
  // password-gated workspace). Redirect them to /login instead of letting
  // the catch-all route fall through to <Homepage />, which is WeaveIn-
  // branded marketing copy.
  useEffect(() => {
    if (authLoading) return;
    if (!config.isTriveny) return;
    if (isAuthenticated) return;
    // Allow the few public surfaces that should still render on triveny.ai
    const path = window.location.pathname;
    // Public landing lives ONLY on the apex (triveny.ai / www.triveny.ai).
    // app.triveny.ai stays the authenticated app, so its root still bounces
    // unauthenticated visitors to /login.
    {
      // The entire apex (triveny.ai / www) is the public marketing site, so
      // never bounce its paths to /login (login lives on app.triveny.ai).
      const lh = window.location.hostname;
      if (lh === 'triveny.ai' || lh === 'www.triveny.ai') return;
    }
    const publicPaths = ['/login', '/forgot-password', '/reset-password', '/signup', '/register'];
    if (publicPaths.some(p => path === p || path.startsWith(p + '/'))) return;
    if (path.startsWith('/consent/')) return; // legal docs
    if (path === '/setup-2fa') return; // 2FA enrollment — page handles its own auth gate
    if (path.startsWith('/auth/oauth-success')) return; // OAuth callback landing
    // If a token exists but useAuth hasn't resolved yet, don't redirect —
    // verifyToken is still in flight (possibly retrying a 502). Otherwise
    // we'd loop the user back to /login the instant they finish signing in.
    if (localStorage.getItem('auth_token')) return;
    window.location.replace('/login');
  }, [authLoading, isAuthenticated]);

  // Triveny.ai: enforce 2FA enrollment for ALL authenticated users, not just
  // fresh logins. Returning sessions (where the user already has a valid
  // platform JWT in localStorage from a pre-2FA-rollout login) bypass the
  // completeLoginAndRedirect /setup-2fa redirect entirely — they'd never be
  // prompted to enroll. Mirror that check here so any authenticated Triveny
  // user with mfa_enabled=false on their tenant_users row is force-routed
  // into the TOTP setup flow before they can use the app.
  useEffect(() => {
    if (authLoading) return;
    if (!config.isTriveny) return;
    if (!isAuthenticated) return;
    const path = window.location.pathname;
    // Don't loop on the enrollment page itself, the public auth surfaces,
    // or callback landings.
    if (path === '/setup-2fa') return;
    if (path === '/login' || path.startsWith('/login/')) return;
    if (path.startsWith('/forgot-password') || path.startsWith('/reset-password')) return;
    if (path.startsWith('/consent/')) return;
    if (path.startsWith('/auth/oauth-success')) return;

    const token = localStorage.getItem('auth_token');
    if (!token) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${config.weaveinApi.baseUrl}/api/platform/auth/2fa/methods`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return; // Don't bounce on transient backend errors
        const data = await res.json();
        if (cancelled) return;
        if (data && data.mfa_enabled === false) {
          window.location.replace('/setup-2fa');
        }
      } catch {
        /* Network error — leave session alone, try again next mount */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated]);

  // Check for public referral response page (no auth required)
  const isProviderReferralPage = window.location.pathname.startsWith('/referral/');

  // Check for public signing page (no auth required)
  const isSigningPage = window.location.pathname.startsWith('/sign/');
  const signingToken = isSigningPage
    ? window.location.pathname.split('/sign/')[1]?.split('/')[0] || ''
    : '';

  // Check for public invoice payment portal (no auth required)
  const isInvoicePortalPage = window.location.pathname.startsWith('/invoice-portal/');
  const invoicePortalToken = isInvoicePortalPage
    ? window.location.pathname.split('/invoice-portal/')[1]?.split('/')[0] || ''
    : '';

  // Check for public form page (no auth required)
  const isPublicFormPage = window.location.pathname.startsWith('/public/forms/');
  // Cold-outreach prospect landing page (TRUS-772, no auth): /o/<token>
  const outreachLandingToken = /^\/o\/(o_[A-Za-z0-9_-]{8,62})\/?$/.exec(window.location.pathname)?.[1] || '';
  const isOutreachLandingPage = outreachLandingToken !== '';
  // TRUS-778 Phase 4: partner connect page, linked from referral emails.
  const partnerConnectToken = /^\/connect\/(c_[A-Za-z0-9_-]{16,60})\/?$/.exec(window.location.pathname)?.[1] || '';
  const isPartnerConnectPage = partnerConnectToken !== '';
  const publicFormToken = isPublicFormPage
    ? window.location.pathname.split('/public/forms/')[1]?.split('/')[0] || ''
    : '';

  // Public SMS opt-in form (A2P 10DLC, no auth). Bare /sms-opt-in renders the
  // WeaveIn brand form; /sms-opt-in/{slug} targets a specific organization.
  const isSmsOptInPage =
    window.location.pathname === '/sms-opt-in' ||
    window.location.pathname.startsWith('/sms-opt-in/');
  const smsOptInSlug = isSmsOptInPage
    ? window.location.pathname.split('/sms-opt-in/')[1]?.split('/')[0] || 'weavein'
    : '';

  // Public donation checkout (no auth). /donate/{cboId} renders a donor-facing
  // gift form that redirects to Stripe-hosted Checkout (Fundraising Suite A5).
  const isDonatePage =
    window.location.pathname === '/donate' || window.location.pathname.startsWith('/donate/');
  const donateCboId = isDonatePage
    ? window.location.pathname.split('/donate/')[1]?.split('/')[0] || ''
    : '';

  // Public donor-event landing + RSVP (no auth). /event/{id} — Fundraising
  // Suite donor-management component D. Links to /donate/{cbo_id} for gifts.
  const isDonorEventPage = window.location.pathname.startsWith('/event/');
  const donorEventId = isDonorEventPage
    ? window.location.pathname.split('/event/')[1]?.split('/')[0] || ''
    : '';

  // IG-7: public survey response page (no auth required). Reached from the
  // IG-6 distribution link {FRONTEND_URL}/surveys/{survey_id}?member_id=...
  const isPublicSurveyPage = window.location.pathname.startsWith('/surveys/');
  const publicSurveyId = isPublicSurveyPage
    ? window.location.pathname.split('/surveys/')[1]?.split('/')[0] || ''
    : '';

  // IG-12: member-side Medi-Cal recertification landing. Reached from the
  // IG-11 SMS/email alerts at {FRONTEND_URL}/member/eligibility/recertification
  // ?member_id=...  Both authed (shows personalized status) and unauthed
  // (sign-in CTA) flows are handled inside the component itself.
  const isMemberRecertPage = window.location.pathname.startsWith('/member/eligibility/recertification');

  // TRUS-688: the WeaveIn join link for a virtual event (/visit/{eventId}),
  // which replaced public meet.jit.si rooms. The page decides what to do per
  // signed-in staff user; members are pointed to the app. A signed-out visitor
  // who picks "Staff: sign in" falls through to the normal login below, and the
  // page renders once they are signed in.
  const visitJoinEventId = parseVisitPath(window.location.pathname);
  const isVisitJoinPage = visitJoinEventId !== null;
  let visitJoinStaffSignIn = false;
  if (isVisitJoinPage) {
    try {
      visitJoinStaffSignIn = sessionStorage.getItem(VISIT_JOIN_STAFF_SIGNIN_KEY) === '1';
    } catch {
      visitJoinStaffSignIn = false;
    }
  }

  // Check for authenticated full-page form preview (/forms/preview/{templateId})
  const isFormPreviewPage = window.location.pathname.startsWith('/forms/preview/');
  const formPreviewIdRaw = isFormPreviewPage
    ? window.location.pathname.split('/forms/preview/')[1]?.split('/')[0] || ''
    : '';
  const formPreviewId = formPreviewIdRaw ? parseInt(formPreviewIdRaw, 10) : null;

  // TRUS-545: the standalone, savable form page. /forms/instance/{id} opens an
  // existing instance; /forms/new/{templateId}?member_id=... starts one against
  // a member. The member context has to ride in the URL — a standalone route
  // has no React context from the profile the link was opened from.
  const formPageProps = parseFormPagePath(window.location.pathname, window.location.search);
  const isFormPage = formPageProps !== null;

  // Web Presence v2 — the layered Design Studio editor (/design/v2) and its
  // read-only preview (/design/v2/view). Full-screen standalone surfaces; the
  // classic Studio deep-links here with ?page=<id>&home=1. Editing needs auth.
  const isCraftBuilderPage = window.location.pathname === '/design/v2';
  const isCraftViewPage = window.location.pathname === '/design/v2/view';

  // Tabbed pop-out: any app page as browser-like tabs in their own browser tab
  // or desktop window, without the sidebar shell. Its state lives in the URL,
  // never in activeTab (see persistActiveTab). The first release's messaging
  // pop-out URLs are rewritten to this on load (lib/popout.ts).
  const isPopoutPage = IS_TABS_HOST;
  const popoutOrgType: 'cbo' | 'employer' | 'sponsor' = activeEmployerId ? 'employer' : activeSponsorId ? 'sponsor' : 'cbo';

  // Main-window tabs: signed in, at desktop / tablet width, the main window has
  // its own tab strip — tab 1 is this page, rendered natively; the "+" opens
  // more pages as tabs (page pop-outs in iframes). Not on phones, not in a
  // pop-out, and not on the Triveny full-bleed home (an early return below,
  // with no content area to put a strip in).
  const mainTabs = useMainWindowTabs({
    host: isAuthenticated && !IS_TABS_HOST && !IS_PAGE_POPOUT,
    visible: (!isMobile || isTablet) && !(config.isTriveny && activeTab === 'triveny-home'),
    userId: user?.id ?? null,
    primarySpec: { tab: activeTab },
    isDesktop: config.isDesktop,
  });
  /** The page the shell (sidebar highlight, "Open in…") is about: the tab in front. */
  const shellActiveTab = mainTabs.activeFrame?.spec?.tab ?? activeTab;
  newMainTabRef.current = mainTabs.visible
    ? () => {
        const front = mainTabs.tabs.find((x) => x.uid === mainTabs.activeUid);
        if (front) mainTabs.addTab(mainTabs.specOf(front));
      }
    : null;

  // A tabbed window, and the main window's strip, need the "+" menu's page list (and its fetches).
  const popoutPages = usePopoutPages(IS_TABS_HOST || mainTabs.visible ? activeOrgId : null, popoutOrgType, user?.role);
  const onPopoutActiveChange = useCallback((spec: AppTabSpec | null) => {
    popoutHostFrontRef.current = spec;
  }, []);

  // Page pop-out: one page, chromeless, inside a tabbed window's iframe (or a
  // browser tab of its own). It checks page access itself, because there is no
  // sidebar to have hidden a page the user may not open: a direct URL to a
  // forbidden page shows the no-access state instead of the page.
  const pageAccess = useSidebarPageAccess(IS_PAGE_POPOUT && !config.isTriveny ? activeOrgId : null);
  const pagePopoutBlocked =
    IS_PAGE_POPOUT && (!PAGE_POPOUT || (!config.isTriveny && !pageAccess.loading && !pageAccess.hasPageAccess(activeTab)));
  // Parameters the page was opened with (Messaging: which view / channel), and
  // a title it gives itself (a channel's name). Cleared when the page navigates.
  const [pagePopoutParams, setPagePopoutParams] = useState<Record<string, string> | undefined>(() => PAGE_POPOUT?.params);
  const [pagePopoutTitle, setPagePopoutTitle] = useState<string | undefined>(undefined);
  const pagePopoutStartTab = React.useRef(PAGE_POPOUT?.tab ?? null);
  useEffect(() => {
    if (!IS_PAGE_POPOUT) return;
    if (activeTab !== pagePopoutStartTab.current) {
      pagePopoutStartTab.current = activeTab;
      setPagePopoutParams(undefined);
      setPagePopoutTitle(undefined);
    }
  }, [activeTab]);
  const onPopoutChannelChange = useCallback((ch: { id: number; name: string } | null) => {
    if (!IS_PAGE_POPOUT) return;
    // Remember the open conversation (a reload / "Open in…" comes back to it) but
    // do NOT retitle the tab after it - the Inbox tab stays "Inbox".
    setPagePopoutParams((prev) =>
      ch ? { view: 'channels', channel: String(ch.id) } : prev?.channel ? { view: 'channels' } : prev,
    );
  }, []);

  // A record page (a member's profile, a household) carries the record's id
  // with it, so a reloaded tab, or "Open in…" a new tab/window, reopens the
  // same record instead of a blank page (lib/popout.ts RECORD_TABS).
  const withRecordParams = (spec: AppTabSpec): AppTabSpec => {
    const rec = recordParamsFor(spec.tab, { member: selectedMemberId, household: selectedHouseholdId });
    return rec ? { ...spec, params: { ...(spec.params || {}), ...rec } } : spec;
  };

  // A record page with no record in memory (e.g. the main window reloaded on a
  // member's profile, whose id was never saved) rendered nothing: show its list.
  useEffect(() => {
    const fallback = recordFallbackTab(activeTab, { member: selectedMemberId, household: selectedHouseholdId });
    if (!fallback) return;
    setActiveTab(fallback);
    persistActiveTab(fallback);
  }, [activeTab, selectedMemberId, selectedHouseholdId]);

  // Keep "Open in…" / File ▸ New Window pointed at the page in front.
  // In the main window that is the tab in front of its strip.
  const frontFrameSpec = mainTabs.activeFrame?.spec ?? null;
  useEffect(() => {
    currentPageRef.current = IS_TABS_HOST
      ? null
      : frontFrameSpec ?? withRecordParams({ tab: activeTab, ...(pagePopoutParams ? { params: pagePopoutParams } : {}) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, pagePopoutParams, frontFrameSpec, selectedMemberId, selectedHouseholdId]);

  // Page pop-out → its tab host: which page this tab now shows (it may have
  // navigated) and what to call it. The URL follows, so reloading the frame
  // comes back on the same page.
  useEffect(() => {
    if (!IS_PAGE_POPOUT || !PAGE_POPOUT) return;
    const spec: AppTabSpec = withRecordParams({ tab: activeTab, ...(pagePopoutParams ? { params: pagePopoutParams } : {}) });
    // A tab id outside the URL-safe set cannot round-trip; keep the last good one.
    if (!isValidTabId(activeTab)) return;
    const key = appTabKey(spec);
    postToHost({ type: 'weavein:page-state', key, ...(pagePopoutTitle ? { title: pagePopoutTitle } : {}) });
    try {
      const next = window.location.pathname.startsWith(PAGE_POPOUT_PATH)
        ? `${PAGE_POPOUT_PATH}?tab=${key}`
        : `${window.location.pathname}?popout=page&tab=${key}`;
      if (`${window.location.pathname}${window.location.search}` !== next) {
        window.history.replaceState(window.history.state, '', next);
      }
    } catch {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, pagePopoutParams, pagePopoutTitle, selectedMemberId, selectedHouseholdId]);

  // A key pressed inside an iframe never reaches the tab host, so the page
  // forwards the tab-strip shortcuts (Cmd/Ctrl+1…9, Cmd/Ctrl+W on desktop, and
  // the move-tab keys — lib/tabStrip.ts tabShortcutFromEvent).
  useEffect(() => {
    if (!IS_PAGE_POPOUT || !isFramedDocument()) return;
    const onKey = (e: KeyboardEvent) => {
      const key = tabShortcutFromEvent(e);
      if (!key || (key === 'w' && !config.isDesktop)) return;
      if (postToHost({ type: 'weavein:shortcut', key })) e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Default page handling - always start on home page for weavein.ai
  // Removed automatic mobile redirect to livefeed
  useEffect(() => {
    // Skip normal routing for public pages and the standalone form preview
    if (isOutreachLandingPage || isPartnerConnectPage || isProviderReferralPage || isInvoicePortalPage || isSigningPage || isPublicFormPage || isSmsOptInPage || isDonatePage || isDonorEventPage || isPublicSurveyPage || isMemberRecertPage || isVisitJoinPage || isFormPreviewPage || isFormPage || isCraftBuilderPage || isCraftViewPage || isPopoutPage || IS_PAGE_POPOUT) {
      return;
    }

    // Full-page deep links land on the react-router catch-all (/*) and boot
    // this SPA, which otherwise ignores the pathname and restores the saved
    // tab. A few out-of-band URLs must instead open a specific console:
    //   - /admin/observability — the observability alert (opened by NotificationBell)
    //   - /agents — the Agents console, linked by the RCA pipeline's held /
    //     blocked / stalled emails + in-app notifications (TRUS-357). The concrete
    //     tab is product-aware: the WeaveIn build opens the WeaveIn Agents console
    //     (weavein-agents), the Triveny build opens the platform one (platform-agents).
    // Honor those here so the link doesn't dead-end on the restored/home tab.
    // (This dispatch was previously dropped when the block was rewritten for the
    // admin-alert tabs, which re-broke the /agents link — see deepLinkTabForPath.)
    const pathTab = window.location.pathname.replace(/^\//, '');
    const deepLinkTab = deepLinkTabForPath(window.location.pathname, { isTriveny: config.isTriveny });
    if (deepLinkTab) {
      setActiveTab(deepLinkTab);
      setNavigationHistory([deepLinkTab]);
      persistActiveTab(deepLinkTab);
      return;
    }

    // TRUS-685: record links (/members-view?id=…, /tco-workflow-instance?id=…,
    // /workflow-instances?instance=…, /approvals?request=…) opened as a full
    // page load. Set the record, open its tab, and park it for the page.
    const recordLink = parseRecordLink(window.location.pathname, window.location.search);
    if (recordLink) {
      if (recordLink.kind === 'member') setSelectedMemberId(String(recordLink.memberId));
      if (recordLink.kind === 'tco-instance') setSelectedTCOInstanceId(recordLink.instanceId);
      parkRecord(recordLink);
      setActiveTab(recordLink.tab);
      setNavigationHistory([recordLink.tab]);
      persistActiveTab(recordLink.tab);
      return;
    }

    // Onboarding admin alerts (new self-signup / new org / new sandbox) deep-link
    // here via NotificationBell; map the alert paths to their SPA tabs so they
    // don't dead-end on the "page not available" state (same pattern as above).
    const ADMIN_ALERT_TABS: Record<string, string> = {
      'admin/self-signups': 'self-signup',
      'admin/sandboxes': 'sandbox-management',
    };
    if (ADMIN_ALERT_TABS[pathTab]) {
      const tab = ADMIN_ALERT_TABS[pathTab];
      setActiveTab(tab);
      setNavigationHistory([tab]);
      persistActiveTab(tab);
      return;
    }

    const savedTab = localStorage.getItem('activeTab');

    // Only restore saved tab if it exists, otherwise stay on home
    if (savedTab && savedTab !== 'home') {
      setActiveTab(savedTab);
      // Also initialize the navigation history with the restored tab
      setNavigationHistory([savedTab]);
    }
  }, []); // Only run once on mount

  // Handle browser back/forward button navigation
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      console.log('🔙 popstate event:', { state: event.state, historyLength: navigationHistory.length });

      if (event.state?.tab) {
        // Restore the tab from browser history state
        console.log('🔙 Restoring tab from history state:', event.state.tab);
        setActiveTab(event.state.tab);
        persistActiveTab(event.state.tab);
      } else {
        // If no state, use internal navigation history or fall back to dashboard/home
        if (navigationHistory.length > 1) {
          const newHistory = [...navigationHistory];
          newHistory.pop();
          const previousTab = newHistory[newHistory.length - 1];
          console.log('🔙 Using internal history, going to:', previousTab);
          setNavigationHistory(newHistory);
          setActiveTab(previousTab);
          persistActiveTab(previousTab);
        } else {
          // Fall back to dashboard if authenticated, home otherwise
          const fallbackTab = isAuthenticated ? 'dashboard' : 'home';
          console.log('🔙 No history, falling back to:', fallbackTab);
          setActiveTab(fallbackTab);
          persistActiveTab(fallbackTab);
        }
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [navigationHistory, isAuthenticated]);

  // Listen for "open the comms hub for a member" requests dispatched by
  // TypedStepCard's "Open in Comms Hub" button (and any other surface that
  // wants to route a member into the unified inbox). TRUS-709: that is the
  // member Inbox now — park the member (lib/memberInboxNavigation) and switch
  // to it; MessagingWorkspace opens the member's conversation. preventDefault()
  // tells the dispatcher it was handled in-app (no URL-reload fallback).
  useEffect(() => {
    const handler = (raw: Event) => {
      const e = raw as CustomEvent<{ member_id?: number }>;
      const memberId = e.detail && typeof e.detail === 'object' ? e.detail.member_id : undefined;
      if (memberId != null) requestOpenMemberInbox(memberId);
      e.preventDefault();
      setActiveTab(MEMBER_INBOX_TAB);
      persistActiveTab(MEMBER_INBOX_TAB);
    };
    window.addEventListener('app-open-comms-hub', handler);
    return () => window.removeEventListener('app-open-comms-hub', handler);
  }, []);

  // TRUS-709: the 'inbox' tab was the 2-Way Inbox. A saved tab, an old link or
  // a stale shortcut that lands there moves to the member Inbox.
  useEffect(() => {
    const next = redirectLegacyInboxTab(activeTab);
    if (next !== activeTab) {
      setActiveTab(next);
      persistActiveTab(next);
    }
  }, [activeTab]);

  // Listen for storage events to handle tab changes from child components
  useEffect(() => {
    const handleStorageEvent = (e: Event) => {
      // Only a change to the current page may move it (lib/popout.ts
      // shouldFollowStorageEvent): a tab-strip save or any other key written
      // in another window used to snap an open member back to the list.
      if (!shouldFollowStorageEvent(e)) return;
      const newTab = localStorage.getItem('activeTab');
      if (newTab && newTab !== activeTab) {
        console.log('📦 Storage event: switching tab to', newTab);
        setNavigationHistory(prev => [...prev, newTab]);
        setActiveTab(newTab);
        window.history.pushState({ tab: newTab }, '', window.location.href);
      }
    };

    window.addEventListener('storage', handleStorageEvent);

    return () => {
      window.removeEventListener('storage', handleStorageEvent);
    };
  }, [activeTab]);

  // Listen for custom changeTab events from child components (e.g., EmployerJourneyDashboard)
  useEffect(() => {
    const handleChangeTab = (event: CustomEvent) => {
      const newTab = event.detail;
      if (newTab && newTab !== activeTab) {
        console.log('🔄 changeTab event: switching tab to', newTab);
        setNavigationHistory(prev => [...prev, newTab]);
        setActiveTab(newTab);
        persistActiveTab(newTab);
        window.history.pushState({ tab: newTab }, '', window.location.href);
      }
    };

    window.addEventListener('changeTab', handleChangeTab as EventListener);

    // Listen for platform module navigation
    const handlePlatformNavigate = (e: CustomEvent<{ tab: string }>) => {
      const newTab = e.detail?.tab;
      if (newTab) {
        setActiveTab(newTab);
        window.history.pushState({ tab: newTab }, '', window.location.href);
      }
    };
    window.addEventListener('platform-navigate', handlePlatformNavigate as EventListener);

    // Listen for open-household event (from MemberHouseholdsTab)
    const handleOpenHousehold = (e: CustomEvent<{ householdId: number }>) => {
      const id = e.detail?.householdId;
      if (id) {
        console.log('🏠 open-household event: opening household', id);
        setSelectedHouseholdId(id);
        setNavigationHistory(prev => [...prev, 'household-view']);
        setActiveTab('household-view');
        window.history.pushState({ tab: 'household-view' }, '', window.location.href);
      }
    };
    window.addEventListener('open-household', handleOpenHousehold as EventListener);

    // Listen for open-member event (from HouseholdMembersTab)
    const handleOpenMember = (e: CustomEvent<{ memberId: number }>) => {
      const id = e.detail?.memberId;
      if (id) {
        setSelectedMemberId(id.toString());
        setNavigationHistory(prev => [...prev, 'members-view']);
        setActiveTab('members-view');
        window.history.pushState({ tab: 'members-view' }, '', window.location.href);
      }
    };
    window.addEventListener('open-member', handleOpenMember as EventListener);

    // TRUS-685: a record link from a bot card, bell or push. The router already
    // switched the tab; these two tabs also need the record in state.
    const handleOpenRecord = (e: CustomEvent<RecordLink>) => {
      const link = e.detail;
      if (link?.kind === 'member') setSelectedMemberId(String(link.memberId));
      if (link?.kind === 'tco-instance') setSelectedTCOInstanceId(link.instanceId);
    };
    window.addEventListener(OPEN_RECORD_EVENT, handleOpenRecord as EventListener);

    return () => {
      window.removeEventListener(OPEN_RECORD_EVENT, handleOpenRecord as EventListener);
      window.removeEventListener('changeTab', handleChangeTab as EventListener);
      window.removeEventListener('platform-navigate', handlePlatformNavigate as EventListener);
      window.removeEventListener('open-household', handleOpenHousehold as EventListener);
      window.removeEventListener('open-member', handleOpenMember as EventListener);
    };
  }, [activeTab]);

  // Initialize browser history state on mount and whenever activeTab changes
  useEffect(() => {
    if (activeTab && (!window.history.state?.tab || window.history.state.tab !== activeTab)) {
      // Only replace state if it's not already set to the current tab
      // This ensures browser back button works correctly
      if (!window.history.state?.tab) {
        window.history.replaceState({ tab: activeTab }, '', window.location.href);
      }
    }
  }, [activeTab]);

  // Navigation helper functions
  const navigateWithHistory = (newTab: string) => {
    // Single-experience: legacy V1 referral tabs route to the unified hub.
    if (LEGACY_REFERRAL_TAB_IDS.includes(newTab)) newTab = 'v2-referrals';
    console.log('🔍 navigateWithHistory called:', { from: activeTab, to: newTab });
    setNavigationHistory(prev => [...prev, newTab]);
    setActiveTab(newTab);
    // Push to browser history so back button works correctly
    window.history.pushState({ tab: newTab }, '', window.location.href);
    console.log('🔍 activeTab set to:', newTab);
    console.log('🔍 isAuthenticated:', isAuthenticated);
    if (newTab === 'actions' || newTab === 'milestones') {
      console.log('🔥 CRITICAL: Navigating to incentive tab', { newTab, isAuthenticated, willRender: isAuthenticated });
    }
  };

  // Expose navigateWithHistory globally so deeply-nested components (e.g.
  // CampaignsPage's "New from Template" button) can trigger a tab change
  // without prop-drilling. Cleared on unmount.
  useEffect(() => {
    (window as any).__navigateWithHistory = navigateWithHistory;
    return () => { try { delete (window as any).__navigateWithHistory; } catch {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, navigationHistory]);

  const navigateBack = () => {
    if (navigationHistory.length > 1) {
      const newHistory = [...navigationHistory];
      newHistory.pop(); // Remove current
      const previousTab = newHistory[newHistory.length - 1];
      setNavigationHistory(newHistory);
      setActiveTab(previousTab);

      // Handle special cases for back navigation
      if (previousTab === "home") {
        setShowCreateForm(false); // Show homepage, not create form
      }
    }
  };

  // Navigation handler for UserManagement → full-page views
  const handleUserManagementNavigate = (tab: string, context?: any) => {
    if (tab === 'user-permissions' && context?.userId) {
      setPermissionsViewerContext({ userId: context.userId, userName: context.userName });
    }
    navigateWithHistory(tab);
  };

  const navigateHome = () => {
    // If user is authenticated, go to dashboard instead of home
    const targetTab = isAuthenticated ? "dashboard" : "home";
    setActiveTab(targetTab);
    setShowCreateForm(false);
    setNavigationHistory([targetTab]);
  };

  const handleTabChange = (tab: string) => {
    // Single-experience: legacy V1 referral tabs route to the unified hub.
    if (LEGACY_REFERRAL_TAB_IDS.includes(tab)) tab = 'v2-referrals';
    navigateWithHistory(tab);
    // Save the current tab to localStorage
    persistActiveTab(tab);
    if (tab === "home") {
      setShowCreateForm(false);
    } else {
      setShowCreateForm(false);
    }
  };

  // THE shell navigation choke point: the sidebar, the Triveny rail, the
  // command palette and the global shortcuts all navigate through here. When
  // an iframe tab of the main window's strip is in front, the click is about
  // THAT tab, so it navigates there (lib/popout.ts 'weavein:navigate') and the
  // hidden primary page stays put. Otherwise it is the old handleTabChange.
  const shellNavigate = (tab: string) => {
    if (mainTabs.navigateActive(tab)) return;
    // A page id that cannot travel to a tab (not URL-safe): show it natively.
    if (mainTabs.activeFrame) mainTabs.activate(PRIMARY_UID);
    handleTabChange(tab);
  };
  // A clicked push notification routes here instead of reloading the window,
  // which would drop a call in progress (TRUS-668, lib/pushNavigation).
  useWebPushNavigation(isAuthenticated && !config.isTriveny, (url) => {
    const target = parsePushTarget(url, window.location.origin);
    return target ? routeStaffPushTarget(target, { navigateTab: shellNavigate, isTriveny: config.isTriveny }) : false;
  });
  /** Cmd/Ctrl+click or middle-click on a sidebar item: that page in a new in-window tab. */
  const openNavInNewTab = mainTabs.visible
    ? (tab: string) => {
        if (isValidTabId(tab)) mainTabs.addTab({ tab });
      }
    : undefined;

  // Page pop-out inside a host's iframe: the host's sidebar was clicked while
  // this tab was in front. Only our own parent, at our own origin, may ask.
  const handleTabChangeRef = React.useRef(handleTabChange);
  handleTabChangeRef.current = handleTabChange;
  useEffect(() => {
    if (!IS_PAGE_POPOUT || !isFramedDocument()) return;
    const onMessage = (e: MessageEvent) => {
      const spec = parseHostNavigateMessage(e);
      if (spec) handleTabChangeRef.current(spec.tab);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const handleCreateReferral = async (newReferral: Referral) => {
    console.log("📥 App.tsx received referral:", newReferral);
    try {
      const success = await apiCreateReferral(newReferral);
      if (success) {
        console.log("✅ Referral created successfully");
        // Only track the processing method on the first referral to avoid overwriting
        if ((referrals?.length || 0) === 0) {
          setLastFormProcessingMethod(newReferral.useMatchingEngine);
        }
        // Don't close the form or navigate yet - let the form handle multiple submissions
        return true;
      } else {
        console.error("❌ Failed to create referral");
        return false;
      }
    } catch (error) {
      console.error("❌ Error creating referral:", error);
      return false;
    }
  };

  const handleCreateReferralComplete = () => {
    console.log("🎉 All referrals created, closing form and navigating based on processing method");
    hideCreateFormHandler();
    
    // Use the tracked processing method to determine routing
    if (!lastFormProcessingMethod) {
      // Manual selection - go to unpublished referrals (finder)
      console.log("🔧 Manual selection - navigating to unpublished referrals");
      navigateWithHistory("finder");
    } else {
      // Use matching engine - go to referral queue
      console.log("🤖 Matching engine - navigating to referral queue");
      navigateWithHistory("queue");
    }
  };

  const showCreateFormHandler = () => {
    console.log('🆕 showCreateFormHandler called, setting showCreateForm to true');
    console.log('📱 isMobile:', isMobile, 'sidebarOpen:', sidebarOpen);
    setShowCreateForm(true);
    console.log('✅ showCreateForm state updated');
  };

  const hideCreateFormHandler = () => {
    setShowCreateForm(false);
  };

  const handleLogout = () => {
    console.log("🚪 Logging out user");

    // Close any open modals first to prevent orphaned overlays
    setShowUserProfile(false);
    setShowAuthModal(false);

    // Clear UI state
    hideCreateFormHandler();
    setActiveTab("home");
    setNavigationHistory(["home"]);

    // Clear any stored tab state
    localStorage.removeItem('activeTab');

    // Execute logout (clears auth tokens and user data)
    logout();
  };

  const handleClaimReferral = async (id: string | number) => {
    const success = await apiUpdateReferralStatus(id, "Matched");
    if (success) {
      // Navigate to workspace after claiming
      setSelectedReferralId(id);
      navigateWithHistory("workspace");
    }
  };

  const handleRejectReferral = async (id: string | number) => {
    const success = await apiUpdateReferralStatus(id, "Rejected");
    if (success) {
      // In a real app, you might want to remove from the list or mark as rejected
      console.log(`Referral ${id} rejected`);
    }
  };

  // Navigation handlers for the flow
  const handleNavigateToLiveFeed = () => {
    navigateWithHistory("livefeed");
  };

  const handleNavigateToWorkspace = (referralId?: string | number) => {
    if (referralId) {
      setSelectedReferralId(referralId);
      // Find the referral to get the member's Medicaid ID
      const referral = referrals?.find(r => String(r.id) === String(referralId));
      if (referral) {
        setSelectedMemberId(referral.memberMedicaidId);
      }
    }
    navigateWithHistory("workspace");
  };

  const handleNavigateToMemberWorkspace = (memberId: string) => {
    setSelectedMemberId(memberId);
    // Find the first referral for this member to show their case workspace
    const memberReferral = referrals?.find(r => r.memberMedicaidId === memberId || r.memberName.includes(memberId));
    if (memberReferral) {
      setSelectedReferralId(memberReferral.id);
    }
    navigateWithHistory("workspace");
  };

  const handleNavigateToDirectory = () => {
    // Homepage should navigate to provider-directory (no login required)
    navigateWithHistory("provider-directory");
  };

  const handleNavigateToCapacity = () => {
    navigateWithHistory("analytics");
  };

  const handleNavigateToFinder = () => {
    navigateWithHistory("finder");
  };

  const handleNavigateToSubmissions = () => {
    navigateWithHistory("submissions");
  };

  const handleNavigateToQueue = () => {
    navigateWithHistory("queue");
  };

  const handleNavigateToMatchingEngine = () => {
    navigateWithHistory("matching");
  };

  const handleNavigateToMetrics = () => {
    navigateWithHistory("metrics");
  };

  const handleNavigateToCommunications = (provider: any) => {
    try {
      console.log('🔍 handleNavigateToCommunications called with provider:', provider);
      console.log('🔍 Setting selectedProviderForComms...');
      setSelectedProviderForComms(provider);
      console.log('🔍 selectedProviderForComms set successfully');
      console.log('🔍 Navigating to provider-communications tab');
      navigateWithHistory("provider-communications");
      console.log('🔍 After navigateWithHistory, activeTab should be:', activeTab);
    } catch (error) {
      console.error('❌ Error in handleNavigateToCommunications:', error);
    }
  };

  const handleViewReferral = (referralId: string | number) => {
    setViewingReferralId(referralId);
    // View action stays in queue but shows detail view
  };

  const handleEditReferral = (referralId: string | number) => {
    setEditingReferralId(referralId);
    // Edit action shows edit form
  };

  const handleCloseReferralView = () => {
    setViewingReferralId(null);
  };

  const handleCloseReferralEdit = () => {
    setEditingReferralId(null);
  };

  const handleUpdateReferral = async (updatedReferral: Referral) => {
    try {
      // Call API to update referral using PUT method
      const response = await fetch(`${API_BASE_URL}/api/referrals/${updatedReferral.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
        },
        body: JSON.stringify({
          first_name: updatedReferral.memberName.split(' ')[0],
          last_name: updatedReferral.memberName.split(' ').slice(1).join(' '),
          phone_number: updatedReferral.phoneNumber || '',
          email: updatedReferral.email || '',
          date_of_birth: updatedReferral.dateOfBirth,
          zip_code: updatedReferral.zipCode,
          preferred_language: updatedReferral.preferredLanguage,
          street_address: updatedReferral.streetAddress || '',
          city: updatedReferral.city || '',
          state: updatedReferral.state || '',
          country: updatedReferral.country || 'USA',
          medicaid_id: updatedReferral.memberMedicaidId,
          health_plan: updatedReferral.healthPlan,
          plan_type: updatedReferral.planType,
          provider_name: updatedReferral.referringProvider.name,
          npi_number: updatedReferral.referringProvider.npi,
          organization: updatedReferral.referringProvider.organization,
          provider_phone_number: updatedReferral.referringProvider.phone,
          primary_diagnosis: updatedReferral.primaryDiagnosis,
          risk_score: updatedReferral.riskScore || 0,
          availability: updatedReferral.availability || '',
          urgency_level: updatedReferral.urgencyLevel,
          additional_notes: updatedReferral.additionalNotes || '',
          prior_authorization_required: updatedReferral.authorizationRequired,
          member_consent_obtained: updatedReferral.consentObtained,
          hipaa_compliant: updatedReferral.hipaaCompliant,
          sdoh_needs: updatedReferral.sdohNeeds,
          housing_subcategories: updatedReferral.housingSubcategories || [],
          transportation_subcategories: updatedReferral.transportationSubcategories || [],
          use_matching_engine: updatedReferral.useMatchingEngine
        })
      });
      
      if (response.ok) {
        console.log("✅ Referral updated successfully");
        setEditingReferralId(null);
        // Refresh referrals data
        if (fetchReferrals) {
          fetchReferrals();
        }
        return true;
      } else {
        console.error("❌ Failed to update referral");
        return false;
      }
    } catch (error) {
      console.error("❌ Error updating referral:", error);
      return false;
    }
  };

  const handleDeleteReferral = async (referralId: string | number) => {
    try {
      console.log(`🗑️  Attempting to delete referral ${referralId}`);

      const response = await authFetch(`${API_BASE_URL}/api/referrals/${referralId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        console.log(`✅ Referral ${referralId} deleted successfully`);
        // Refresh referrals data
        if (fetchReferrals) {
          fetchReferrals();
        }
        return true;
      } else {
        const errorData = await response.json();
        console.error(`❌ Failed to delete referral: ${errorData.message || errorData.error}`);
        alert(`Cannot delete referral: ${errorData.message || errorData.error}`);
        return false;
      }
    } catch (error) {
      console.error("❌ Error deleting referral:", error);
      alert("Error deleting referral. Please try again.");
      return false;
    }
  };

  const handleEnrollMember = async (referralId: string, memberId: number, programId: number, memberName: string) => {
    try {
      console.log(`📋 Enrolling member ${memberName} (${memberId}) in program ${programId} from referral ${referralId}`);

      // Create enrollment with minimal required data
      const enrollmentData = {
        member_id: memberId,
        program_id: programId,
        enrollment_status: 'active',
        enrollment_priority: 'medium',
        enrollment_date: new Date().toISOString().split('T')[0],
        start_date: new Date().toISOString().split('T')[0],
        end_date: null,
        assigned_to: null,
      };

      const response = await fetch(`${API_BASE_URL}/api/enrollments/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
        },
        body: JSON.stringify(enrollmentData),
      });

      if (response.ok) {
        const enrollment = await response.json();
        console.log(`✅ Member ${memberName} enrolled successfully with ID ${enrollment.id}`);
        alert(`${memberName} has been successfully enrolled in the program!`);

        // Refresh referrals data to update enrollment status
        if (fetchReferrals) {
          fetchReferrals();
        }
        return true;
      } else {
        const errorData = await response.json();
        console.error(`❌ Failed to enroll member: ${errorData.detail || errorData.error}`);
        alert(`Cannot enroll member: ${errorData.detail || errorData.error}`);
        return false;
      }
    } catch (error) {
      console.error("❌ Error enrolling member:", error);
      alert("Error enrolling member. Please try again.");
      return false;
    }
  };

  const handleSubmissionComplete = () => {
    // Navigate back to queue after submission
    navigateWithHistory("queue");
  };

  // Authentication handlers
  const handleNavigateToLogin = () => {
    // Redirect to the dedicated login page instead of modal
    window.location.href = '/login';
  };

  const handleNavigateToRailwayTest = () => {
    navigateWithHistory("railway-test");
  };

  const handleCloseAuthModal = () => {
    setShowAuthModal(false);
  };

  const handleNavigateToForgotPassword = () => {
    setShowAuthModal(false);
    navigateWithHistory("forgot-password");
  };

  const handleShowUserProfile = () => {
    setShowUserProfile(true);
  };

  const handleCloseUserProfile = () => {
    setShowUserProfile(false);
  };

  // The Triveny apex (triveny.ai / www) is the PUBLIC marketing site for
  // everyone — authenticated or not. The product lives on app.triveny.ai, so
  // an authed user who hits the apex (e.g. via the rail logo) sees the public
  // site, not the app. Returned before the auth-loading gate so no spinner.
  if (
    config.isTriveny &&
    typeof window !== 'undefined' &&
    (window.location.hostname === 'triveny.ai' || window.location.hostname === 'www.triveny.ai')
  ) {
    return <TrivenyPublicHome />;
  }

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Triveny: render-time gate so unauthenticated visitors with NO token
  // never see the WeaveIn Homepage flash.
  //
  // CRITICAL: only redirect when there's NO auth_token in localStorage.
  // If a token IS present but useAuth hasn't resolved it yet
  // (verifyToken still in flight, a transient 502 from /me, the brief
  // window right after a fresh login), bouncing them to /login creates
  // a sign-in loop — they sign in, get a token, land on /, gate sees
  // isAuthenticated=false (verify still pending), → /login again.
  if (config.isTriveny && !isAuthenticated && typeof window !== 'undefined') {
    const path = window.location.pathname;
    const hasToken = !!localStorage.getItem('auth_token');
    const publicPaths = ['/login', '/forgot-password', '/reset-password', '/signup', '/register'];
    const isPublicPath =
      publicPaths.some(p => path === p || path.startsWith(p + '/')) ||
      path.startsWith('/consent/') ||
      path === '/setup-2fa' ||
      path.startsWith('/auth/oauth-success');
    if (!isPublicPath && !hasToken) {
      window.location.replace('/login');
      return (
        <div
          style={{
            minHeight: '100vh',
            background: 'var(--v2-paper, #FAF8F4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: "'Inter', system-ui, sans-serif",
            color: 'var(--v2-ink, #1A1A1A)',
            fontSize: 13,
          }}
        >
          Redirecting to sign-in…
        </div>
      );
    }
  }


  // Show loading state for referrals only if authenticated
  if (isAuthenticated && referralsLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-lg text-gray-600">Loading referrals...</p>
        </div>
      </div>
    );
  }

  // Show error state for referrals only if authenticated
  if (isAuthenticated && referralsError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Connection Error</h2>
          <p className="text-gray-600 mb-4">Unable to connect to the backend server.</p>
          <p className="text-sm text-gray-500 mb-6">Error: {referralsError}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-teal-600 text-white px-6 py-2 rounded-lg hover:bg-teal-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Public provider referral page (no auth required) - handle before mobile/desktop views
  if (isProviderReferralPage) {
    // TRUS-757: closed-loop response page (accept / decline / schedule / seen / outcome).
    if (window.location.pathname.startsWith('/referral/respond')) {
      return (
        <React.Suspense fallback={null}>
          <ProviderRespondPage />
        </React.Suspense>
      );
    }
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        <ProviderReferralResponse />
      </div>
    );
  }

  // Public signing page (no auth required)
  if (isSigningPage && signingToken) {
    return <PublicSigningPage token={signingToken} />;
  }

  // Public invoice payment portal (no auth required)
  if (isInvoicePortalPage && invoicePortalToken) {
    return (
      <div className="min-h-screen bg-gray-50">
        <InvoicePaymentPortal token={invoicePortalToken} />
      </div>
    );
  }

  // Public form page (no auth required)
  // /public/forms/<token>/results — a survey's live results (TRUS-624).
  if (isPublicFormPage && publicFormToken && /^\/public\/forms\/[^/]+\/results\/?$/.test(window.location.pathname)) {
    return <PublicSurveyResultsPage token={publicFormToken} />;
  }
  if (isPublicFormPage && publicFormToken) {
    return <PublicFormPage token={publicFormToken} />;
  }

  // Cold-outreach prospect landing page (no auth required)
  if (isOutreachLandingPage) {
    return <OutreachLandingPage token={outreachLandingToken} />;
  }

  if (isPartnerConnectPage) {
    return <PartnerConnectPage token={partnerConnectToken} />;
  }

  // Public SMS opt-in form (A2P 10DLC, no auth required)
  if (isSmsOptInPage) {
    return <SmsOptInPage slug={smsOptInSlug} />;
  }

  // Public donation checkout (no auth required)
  if (isDonatePage && donateCboId) {
    return <DonationsPublicPage cboId={donateCboId} />;
  }

  // Public donor-event landing + RSVP (no auth required)
  if (isDonorEventPage && donorEventId) {
    return <DonorEventPublicPage eventId={donorEventId} />;
  }

  // IG-7: public survey response page (no auth required)
  if (isPublicSurveyPage && publicSurveyId) {
    return <PublicSurveyPage surveyId={publicSurveyId} />;
  }

  // IG-12: member recertification landing (mixed auth — component handles both)
  if (isMemberRecertPage) {
    return <MemberRecertificationPage />;
  }

  // TRUS-688: WeaveIn join link (mixed auth — the page handles both).
  if (isVisitJoinPage && visitJoinEventId && (isAuthenticated || authLoading || !visitJoinStaffSignIn)) {
    return <VisitJoinPage eventId={visitJoinEventId} isAuthenticated={isAuthenticated} authLoading={authLoading} />;
  }

  // Authenticated full-page form preview (no sidebar). Requires auth so the
  // formsAPI fetch carries the user's token + active cbo_id.
  if (isAuthenticated && isFormPreviewPage && formPreviewId) {
    return <FormPreviewPage templateId={formPreviewId} />;
  }

  // The savable form page. Unlike the read-only preview above this one writes,
  // so FormPage additionally checks the forms.library page capability before it
  // renders an editor.
  if (isAuthenticated && isFormPage && formPageProps) {
    return <FormPage {...formPageProps} />;
  }

  // Tabbed pop-out (no sidebar). Signed-out visitors fall through to the
  // normal login handling below.
  if (isAuthenticated && isPopoutPage) {
    return (
      <>
        <DesktopRefreshHandler />
        <AppTabsHost pages={popoutPages.pages} labelFor={popoutPages.labelFor} onActiveChange={onPopoutActiveChange} />
      </>
    );
  }

  // Web Presence v2 layered Design Studio — full-screen editor + preview. The
  // read-only view can render for anyone with the doc; the editor needs auth so
  // its craft save/load carries the token + active cbo_id.
  if (isCraftViewPage) {
    return (
      <React.Suspense fallback={<V2Loading />}>
        <CraftView />
      </React.Suspense>
    );
  }
  if (isAuthenticated && isCraftBuilderPage) {
    return (
      <React.Suspense fallback={<V2Loading />}>
        <CraftBuilder />
      </React.Suspense>
    );
  }

  // Mobile early return removed: The separate mobile routing tree was missing platform-*
  // routes (sales, marketing, contacts, etc.), causing portrait mode to only show homepage.
  // The main layout below already has proper mobile support (hamburger menu, overlay sidebar).
  if (false) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        {/* Mobile Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm">
          <div className="flex items-center justify-between px-4 py-3">
            {isAuthenticated ? (
              <>
                <button
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
                <div className="flex items-center space-x-2">
                  <img
                    src={config.isTriveny ? "/triveny/logo.svg" : "/weavin-logo.png"}
                    alt={config.app.name}
                    className="h-8 w-auto"
                    ref={(el) => {
                      if (!el || !config.isTriveny) return;
                      const isDark = document.documentElement.classList.contains('dark');
                      el.src = isDark ? '/triveny/logo-white.svg' : '/triveny/logo.svg';
                    }}
                    onError={(e) => { (e.target as HTMLImageElement).src = config.isTriveny ? '/triveny/wordmark.svg' : '/weavin-logo.png'; }}
                  />
                  <span className="font-semibold text-gray-800">{config.app.name}</span>
                </div>
                <button
                  onClick={handleShowUserProfile}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </button>
              </>
            ) : (
              <>
                <div className="flex items-center space-x-2">
                  <img
                    src={config.isTriveny ? "/triveny/logo.svg" : "/weavin-logo.png"}
                    alt={config.app.name}
                    className="h-8 w-auto"
                    ref={(el) => {
                      if (!el || !config.isTriveny) return;
                      const isDark = document.documentElement.classList.contains('dark');
                      el.src = isDark ? '/triveny/logo-white.svg' : '/triveny/logo.svg';
                    }}
                    onError={(e) => { (e.target as HTMLImageElement).src = config.isTriveny ? '/triveny/wordmark.svg' : '/weavin-logo.png'; }}
                  />
                  <span className="font-semibold text-gray-800">{config.app.name}</span>
                </div>
                <button
                  onClick={handleNavigateToLogin}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  Login
                </button>
              </>
            )}
          </div>
        </header>

        {/* Mobile Sidebar Overlay */}
        {isAuthenticated && sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Mobile Sidebar - Full width overlay on phones */}
        {isAuthenticated && (
          <div className={`fixed inset-y-0 left-0 z-50 w-full max-w-xs transform transition-transform duration-300 ease-in-out ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}>
            {config.isTriveny ? (
              <TrivenyRail
                activeTab={activeTab}
                onTabChange={(tab) => {
                  handleTabChange(tab);
                  setSidebarOpen(false);
                }}
                onHomeClick={() => {
                  handleTabChange("triveny-home");
                  setSidebarOpen(false);
                }}
                currentUser={trivenyCurrentUser}
                tenantName={config.isTriveny ? 'Triveny.ai' : ((user as any)?.tenant_name || activeOrgName || undefined)}
                userRoleLabel={orgSpecificRole || user?.role || undefined}
                onShowProfile={handleShowUserProfile}
                onLogout={logout}
                onCloseMobile={() => setSidebarOpen(false)}
              />
            ) : null /* WeaveIn mobile sidebar is V2Sidebar (rendered below) */}
          </div>
        )}

        {/* TRUS-747: always-on while a super admin is viewing as someone */}
        {isAuthenticated && <ImpersonationBanner />}

        {/* Session Timeout Warning */}
        {isAuthenticated && (
          <SessionTimeoutWarning
            timeRemaining={timeRemaining}
            warningSeconds={sessionWarningSeconds}
            onExtendSession={extendSession}
            onLogout={logout}
          />
        )}

        {/* Re-Consent Modal for existing sessions (mobile) */}
        {showConsentModal && consentStatus && user?.email && token && (
          <ReConsentModal
            consentStatus={consentStatus}
            userEmail={user.email}
            accessToken={token}
            onComplete={() => {
              console.log('✅ Re-consent completed');
              setShowConsentModal(false);
              setConsentStatus(null);
            }}
          />
        )}

        <div className="pt-16">
          {(() => {
            console.log('🔍 RENDER CYCLE:', { activeTab, isAuthenticated, timestamp: new Date().toISOString() });
            if (activeTab === 'actions' || activeTab === 'milestones') {
              console.log('🚨 ALERT: About to check incentive tab conditional', {
                activeTab,
                isAuthenticated,
                shouldRenderActions: activeTab === 'actions' && isAuthenticated,
                shouldRenderMilestones: activeTab === 'milestones' && isAuthenticated
              });
            }
            return null;
          })()}
          {activeTab === "home" ? (
            // Show EmployerJourneyDashboard for employer organizations, otherwise show Homepage
            activeEmployerId && isAuthenticated ? (
              <EmployerJourneyDashboard />
            ) : (
              <Homepage
                onNavigateToDirectory={handleNavigateToDirectory}
                onNavigateToLiveFeed={handleNavigateToLiveFeed}
                onNavigateToMatchingEngine={handleNavigateToMatchingEngine}
                onNavigateToCapacity={handleNavigateToCapacity}
                onNavigateToSubmissions={handleNavigateToSubmissions}
                onCreateNew={showCreateFormHandler}
                onNavigateToFinder={handleNavigateToFinder}
                onNavigateToQueue={handleNavigateToQueue}
                onNavigateToLogin={handleNavigateToLogin}
                onNavigateToSignup={() => navigateWithHistory('signup')}
                onNavigateToRailwayTest={handleNavigateToRailwayTest}
                totalReferrals={referrals?.length || 0}
                totalProviders={providerCount || 0}
                autoMatchedCount={referrals?.filter(r => r.useMatchingEngine).length || 0}
              />
            )
          ) : activeTab === "queue" && isAuthenticated ? (
            viewingReferralId && referrals?.find(r => String(r.id) === String(viewingReferralId)) ? (
              <ReferralDetailView
                referral={referrals.find(r => String(r.id) === String(viewingReferralId)) as FrontendReferral}
                onBack={handleCloseReferralView}
                onEdit={() => {
                  handleCloseReferralView();
                  handleEditReferral(viewingReferralId);
                }}
              />
            ) : editingReferralId && referrals?.find(r => String(r.id) === String(editingReferralId)) ? (
              <ReferralEditForm
                referral={referrals.find(r => String(r.id) === String(editingReferralId)) as FrontendReferral}
                onSubmit={handleUpdateReferral}
                onCancel={handleCloseReferralEdit}
                onBack={handleCloseReferralEdit}
              />
            ) : (
              <ReferralQueue
                referrals={referrals || []}
                onCreateNew={showCreateFormHandler}
                onNavigateToLiveFeed={handleNavigateToLiveFeed}
                onViewMatched={handleViewReferral}
                onBack={navigateHome}
                onUpdateStatus={updateReferralStatus}
                onEditReferral={handleEditReferral}
                onDeleteReferral={handleDeleteReferral}
                onEnrollMember={handleEnrollMember}
              />
            )
          ) : activeTab === "unmatched" && isAuthenticated ? (
            <CandidateFinder
              onNavigateToSubmissions={handleNavigateToSubmissions}
              onBack={navigationHistory.length > 1 ? navigateBack : undefined}
              onNavigateToDirectory={() => {
                // Store the referral context before navigating
                handleNavigateToDirectory();
              }}
              onStartManualMatching={(referral) => {
                setReferralForManualMatching(referral);
                navigateWithHistory("provider-directory");
              }}
              referrals={referrals}
              selectedReferralForMatching={referralForManualMatching}
              selectedProviderForMatching={selectedProviderForMatching}
              onRefreshReferrals={fetchReferrals}
            />
          ) : activeTab === "finder" && isAuthenticated ? (
            <CandidateFinder
              onNavigateToSubmissions={handleNavigateToSubmissions}
              onBack={navigationHistory.length > 1 ? navigateBack : undefined}
              onNavigateToDirectory={() => {
                // Store the referral context before navigating
                handleNavigateToDirectory();
              }}
              onStartManualMatching={(referral) => {
                setReferralForManualMatching(referral);
                navigateWithHistory("provider-directory");
              }}
              referrals={referrals}
              selectedReferralForMatching={referralForManualMatching}
              selectedProviderForMatching={selectedProviderForMatching}
              onRefreshReferrals={fetchReferrals}
            />
          ) : activeTab === "provider-communications" && isAuthenticated && selectedProviderForComms ? (
            (() => {
              console.log('🔍 Rendering ProviderCommunications:', {
                activeTab,
                isAuthenticated,
                selectedProviderForComms,
                provider: selectedProviderForComms
              });
              return (
                <ProviderCommunications
                  provider={selectedProviderForComms}
                  onBack={navigateBack}
                />
              );
            })()
          ) : activeTab === "directory" && isAuthenticated ? (
            <Directory
              onNavigateToCapacity={handleNavigateToCapacity}
              onBack={navigationHistory.length > 1 ? navigateBack : undefined}
              onProviderSelected={(provider) => {
                // When provider is selected from My Providers, set it for matching and navigate to unpublished referrals
                console.log('📍 Provider selected from My Providers:', provider);
                setSelectedProviderForMatching(provider);
                navigateWithHistory("finder");
              }}
              onNavigateToCommunications={handleNavigateToCommunications}
            />
          ) : activeTab === "provider-directory" ? (
            // Provider Directory - No login required (mobile view)
            <ProviderDirectory
              onBack={() => {
                if (referralForManualMatching) {
                  // If coming from manual matching, go back to finder
                  setReferralForManualMatching(null);
                  setSelectedProviderForMatching(null);
                  navigateWithHistory("finder");
                } else {
                  navigateHome();
                }
              }}
              showSelectButton={!!referralForManualMatching}
              onSelectProvider={(provider) => {
                setSelectedProviderForMatching(provider);
                // Navigate back to finder to show confirmation dialog
                navigateWithHistory("finder");
              }}
            />
          ) : activeTab === "matching" && isAuthenticated ? (
            <AutoMatchReferrals
              onBack={navigationHistory.length > 1 ? navigateBack : undefined}
            />
          ) : activeTab === "workspace" && isAuthenticated ? (
            selectedMemberId ? (
              <CaseWorkspace
                selectedReferralId={selectedReferralId}
                referrals={referrals || []}
                onNavigateToDirectory={handleNavigateToDirectory}
                onBack={() => {
                  setSelectedMemberId(null);
                  setSelectedReferralId(null);
                  navigateBack();
                }}
              />
            ) : (
              <MemberProfiles
                referrals={referrals || []}
                onNavigateToWorkspace={handleNavigateToMemberWorkspace}
                onBack={navigationHistory.length > 1 ? navigateBack : undefined}
                onDeleteReferral={handleDeleteReferral}
              />
            )
          ) : activeTab === "livefeed" ? (
            // LiveFeed - Available without login, but with masked PII
            <LiveFeed
              referrals={referrals || []}
              onClaim={handleClaimReferral}
              onReject={handleRejectReferral}
              onBack={navigateHome}
              onUpdateStatus={updateReferralStatus}
              onViewDetails={handleViewReferral}
              isAuthenticated={isAuthenticated}
            />
          ) : activeTab === "contacts" && isAuthenticated ? (
            <ContactListPage
              onViewContact={(id) => { setSelectedMemberId(id.toString()); navigateWithHistory('contacts-view'); }}
              onEditContact={(id) => { setSelectedMemberId(id.toString()); navigateWithHistory('contacts-edit'); }}
              onCreateContact={() => navigateWithHistory('contacts-new')}
              onConfigureFields={() => navigateWithHistory('member-fields')}
            />
          ) : activeTab === "contacts-view" && isAuthenticated && selectedMemberId ? (
            <ContactDetailPage
              contactId={parseInt(selectedMemberId)}
              onBack={() => navigateWithHistory('contacts')}
              onEdit={(id) => { setSelectedMemberId(id.toString()); navigateWithHistory('contacts-edit'); }}
              onArchived={() => navigateWithHistory('contacts')}
              onEnrolled={(id) => { setSelectedMemberId(id.toString()); navigateWithHistory('members-view'); }}
            />
          ) : activeTab === "contacts-new" && isAuthenticated ? (
            <AddContactForm
              onClose={() => navigateWithHistory('contacts')}
              onCreated={(id) => { setSelectedMemberId(id.toString()); navigateWithHistory('contacts-view'); }}
            />
          ) : activeTab === "contacts-edit" && isAuthenticated && selectedMemberId ? (
            <AddContactForm
              contactId={parseInt(selectedMemberId)}
              onClose={() => { navigateWithHistory('contacts-view'); }}
              onCreated={() => { navigateWithHistory('contacts-view'); }}
            />
          ) : activeTab === "members" && isAuthenticated ? (
            <MemberList
              onNavigate={(tab, memberId) => {
                if (memberId) setSelectedMemberId(memberId.toString());
                navigateWithHistory(tab);
              }}
              favoriteIds={isV2Enabled ? v2FavoriteIds : undefined}
              onToggleFavorites={isV2Enabled ? () => {
                if (v2FavoriteIds) { setV2FavoriteIds(null); return; }
                // Absolute prod base URL — a relative /api/* on weavein.ai is
                // proxied to the DEV backend (vercel.json), which rejects the
                // prod-issued JWT with "Signature verification failed" (401).
                authFetch(`${config.weaveinApi.baseUrl}/api/members/favorites/list`)
                  .then(r => r.json())
                  .then(data => {
                    // /api/members/favorites/list returns { favorite_member_ids, favorites },
                    // not a bare array — parse both shapes so the filter actually populates.
                    let ids: number[] = [];
                    if (Array.isArray(data)) {
                      ids = data.map((f: { member_id?: number; id?: number }) => f.member_id || f.id).filter(Boolean) as number[];
                    } else if (data?.favorite_member_ids && Array.isArray(data.favorite_member_ids)) {
                      ids = data.favorite_member_ids;
                    } else if (data?.favorites && Array.isArray(data.favorites)) {
                      ids = data.favorites.map((f: { member_id?: number; id?: number }) => f.member_id || f.id).filter(Boolean) as number[];
                    }
                    setV2FavoriteIds(ids);
                  })
                  .catch(() => setV2FavoriteIds([]));
              } : undefined}
            />
          ) : activeTab === "members-duplicates" && isAuthenticated ? (
            <MemberDuplicatesPanel
              onOpenMember={(id) => {
                setSelectedMemberId(id.toString());
                navigateWithHistory("members-view");
              }}
            />
          ) : activeTab === "members-new" && isAuthenticated ? (
            <MemberProfile onNavigate={(tab, id) => {
              if (id) setSelectedMemberId(id.toString());
              navigateWithHistory(tab);
            }} />
          ) : activeTab === "members-edit" && isAuthenticated && selectedMemberId ? (
            <AddEditMember memberId={parseInt(selectedMemberId)} onNavigate={(tab, memberId) => {
              if (memberId) setSelectedMemberId(memberId.toString());
              navigateWithHistory(tab);
            }} />
          ) : activeTab === "members-view" && isAuthenticated && selectedMemberId ? (
            <MemberProfile memberId={parseInt(selectedMemberId)} onNavigate={(tab, id) => {
              if (tab === 'household-view' && id) {
                setSelectedHouseholdId(id);
              } else if (id) {
                setSelectedMemberId(id.toString());
              }
              navigateWithHistory(tab);
            }} />
          ) : activeTab === "household-view" && isAuthenticated && selectedHouseholdId ? (
            <SectionErrorBoundary name="Household Detail">
              <HouseholdDetailView
                householdId={selectedHouseholdId}
                onBack={() => navigateWithHistory("members-view")}
              />
            </SectionErrorBoundary>
          ) : activeTab === "member-fields" && isAuthenticated && canAccessPlatform ? (
            <MemberFieldConfigManager />
          ) : activeTab === "staff-profiles" && isAuthenticated && canAccessPlatform ? (
            <StaffProfilesPage />
          ) : activeTab === "filter-chips" && isAuthenticated && canAccessPlatform ? (
            <FilterChipsManager />
          ) : activeTab === "field-mapping" && isAuthenticated && canAccessPlatform ? (
            <FieldMappingManager />
          ) : activeTab === "config-migration" && isAuthenticated ? (
            <ConfigMigrationPage onNavigate={navigateWithHistory} />
          ) : activeTab === "programs" && isAuthenticated ? (
            <ProgramManagement onNavigate={navigateWithHistory} />
          ) : activeTab === "contact-types" && isAuthenticated ? (
            <ContactTypesPage />
          ) : activeTab === "enrollments-view" && isAuthenticated && activeOrgId === 350 && selectedEnrollmentId ? (
            <EnrollmentDetail enrollmentId={parseInt(selectedEnrollmentId)} onBack={() => { setSelectedEnrollmentId(null); handleTabChange('members-view'); }} />
          ) : activeTab === "enrollments-view" && isAuthenticated && activeOrgId === 350 ? ( // LCC only for now (TODO: productize via nav registry)
            // Members-by-program enrollments roster (LCC-scoped entry via palette /
            // program drill-in). Row click sets the selected id → detail branch above.
            <EnrollmentListNew
              onNavigate={(tab, enrollmentId) => {
                if (enrollmentId != null) {
                  setSelectedEnrollmentId(String(enrollmentId));
                  navigateWithHistory('enrollments-view');
                } else {
                  navigateWithHistory(tab);
                }
              }}
            />
          ) : activeTab === "enrollment-new" && isAuthenticated ? (
            <CreateEnrollmentPage onBack={() => navigateBack()} />
          ) : activeTab === "eligibility-screening" && isAuthenticated ? (
            <EligibilityPreScreenForm />
          ) : activeTab === "eligibility-verification" && isAuthenticated ? (
            <EligibilityVerificationPanel />
          ) : activeTab === "ffs-transition-cohort" && isAuthenticated ? (
            ffsTransitionLoading ? (
              <V2Loading />
            ) : ffsTransitionAllowed ? (
              <SectionErrorBoundary name="FFS Transition">
                <FfsTransitionCohortPage />
              </SectionErrorBoundary>
            ) : (
              <PopoutNoAccess />
            )
          ) : activeTab === "verify" && isAuthenticated ? (
            verifyLoading ? (
              <V2Loading />
            ) : verifyAllowed ? (
              <SectionErrorBoundary name="Verify">
                <VerifyPage />
              </SectionErrorBoundary>
            ) : (
              <PopoutNoAccess />
            )
          ) : activeTab === "eligibility-direct" && isAuthenticated ? (
            <DirectVerificationPanel />
          ) : activeTab === "eligibility-credentials" && isAuthenticated ? (
            <CredentialSetupWizard />
          ) : activeTab === "actions" && isAuthenticated ? (
            <ActionTracker />
          ) : activeTab === "program-milestone-config" && isAuthenticated ? (
            <ProgramMilestoneConfiguration />
          ) : activeTab === "member-milestone-progress" && isAuthenticated ? (
            <MemberMilestoneProgressSelector />
          ) : activeTab === "pending-milestone-approvals" && isAuthenticated ? (
            <PendingMilestoneApprovals />
          ) : activeTab === "milestone-management" && isAuthenticated ? (
            <MilestoneManagement />
          ) : activeTab === "sponsor-dashboard" && isAuthenticated ? (
            <SponsorDashboard />
          ) : activeTab === "member-retention" && isAuthenticated ? (
            <MemberRetentionDashboard cboId={activeOrgId || 1} />
          ) : activeTab === "outreach-activities" && isAuthenticated ? (
            <OutreachActivitiesPage />
          ) : activeTab === "event-tracking" && isAuthenticated ? (
            <EventTrackingPage onNavigate={(tab) => navigateWithHistory(tab)} />
          ) : activeTab === "my-forms" && isAuthenticated ? (
            <MyFormsInbox />
          ) : activeTab === "activity-reports" && isAuthenticated ? (
            <ActivityReportsPage />
          ) : (activeTab === "admin/observability" || activeTab === "observability") && isAuthenticated && (user?.role === 'super_admin' || user?.role === 'ops_admin') ? (
            // Observability is a tab inside the Agents page. The alert deep-link
            // (/admin/observability?issue=N) opens the Agents page on that tab.
            <AgentControlPage
              product="weavein"
              initialTab="issues"
              focusIssueId={(() => { const m = window.location.search.match(/[?&]issue=(\d+)/); return m ? Number(m[1]) : null; })()}
            />
          ) : activeTab === "geographic-risk-map" && isAuthenticated ? (
            <GeographicRiskMap cboId={activeOrgId || 1} />
          ) : activeTab === "emergency-dashboard" && isAuthenticated && (user?.role === 'super_admin' || user?.role === 'ops_admin' || user?.role === 'org_admin' || user?.role === 'manager') ? (
            <EmergencySituationRoom
              incidentId={selectedIncidentId || 0}
              onBack={() => navigateWithHistory('emergency-incidents')}
              onLaunchCampaign={(id, name) => { setSelectedIncidentId(id); setSelectedIncidentName(name); navigateWithHistory('emergency-create-incident'); }}
            />
          ) : activeTab === "emergency-incidents" && isAuthenticated && (user?.role === 'super_admin' || user?.role === 'ops_admin' || user?.role === 'org_admin' || user?.role === 'manager') ? (
            <EmergencyIncidentList
              onSelectIncident={(id, name) => { setSelectedIncidentId(id); setSelectedIncidentName(name || ''); navigateWithHistory('emergency-dashboard'); }}
              onCreateIncident={() => navigateWithHistory('emergency-create-incident')}
              onViewAlerts={() => navigateWithHistory('emergency-alerts')}
            />
          ) : activeTab === "emergency-alerts" && isAuthenticated && (user?.role === 'super_admin' || user?.role === 'ops_admin' || user?.role === 'org_admin' || user?.role === 'manager') ? (
            <ExternalAlertFeed
              onIncidentCreated={(id) => { setSelectedIncidentId(id); setSelectedIncidentName(''); navigateWithHistory('emergency-dashboard'); }}
              onBack={() => navigateWithHistory('emergency-incidents')}
            />
          ) : activeTab === "emergency-create-incident" && isAuthenticated && (user?.role === 'super_admin' || user?.role === 'ops_admin' || user?.role === 'org_admin' || user?.role === 'manager') ? (
            <EmergencyCampaignWizard
              incidentId={selectedIncidentId || 0}
              incidentName={selectedIncidentName || 'Emergency Incident'}
              onComplete={() => navigateWithHistory('emergency-dashboard')}
              onCancel={() => navigateWithHistory(selectedIncidentId ? 'emergency-dashboard' : 'emergency-incidents')}
            />
          ) : activeTab === "communication-features" && isAuthenticated ? (
            <CommunicationFeaturesPanel memberId={selectedMemberId || 0} />
          ) : activeTab === "cbo-data-entry" && isAuthenticated && activeOrgId ? (
            <CBODataEntry memberId={selectedMemberId || 0} cboId={activeOrgId} onDataSubmit={() => {}} />
          ) : activeTab === "case-notes-analysis" && isAuthenticated ? (
            <CaseNotesAnalysis memberId={selectedMemberId || 0} cboId={activeOrgId || undefined} />
          ) : activeTab === "employee-retention" && isAuthenticated ? (
            <EmployerBenefitsAnalyticsDashboard employerId={activeEmployerId ? parseInt(activeEmployerId) : 0} />
          ) : activeTab === "employer-workflow-orchestration" && isAuthenticated ? (
            <EmployerWorkflowOrchestration
              employerId={activeEmployerId ? parseInt(activeEmployerId) : 0}
              onNavigate={(tab) => navigateWithHistory(tab)}
            />
          ) : activeTab === "employer-onboarding" && isAuthenticated ? (
            <EmployerOnboardingWizard
              employerId={activeEmployerId ? parseInt(activeEmployerId) : 0}
              onComplete={() => navigateWithHistory('home')}
            />
          ) : activeTab === "employer-quick-wins" && isAuthenticated ? (
            <EmployerQuickWins
              employerId={activeEmployerId ? parseInt(activeEmployerId) : 0}
              onNavigate={(tab) => navigateWithHistory(tab)}
            />
          ) : activeTab === "employer-benchmarking" && isAuthenticated ? (
            <EmployerBenchmarking
              employerId={activeEmployerId ? parseInt(activeEmployerId) : 0}
            />
          ) : activeTab === "employer-alerts" && isAuthenticated ? (
            <EmployerAlertsCenter
              employerId={activeEmployerId ? parseInt(activeEmployerId) : 0}
            />
          ) : activeTab === "employer-cost-transparency" && isAuthenticated ? (
            <EmployerCostTransparency
              employerId={activeEmployerId ? parseInt(activeEmployerId) : 0}
            />
          ) : activeTab === "workforce-dashboard" && isAuthenticated ? (
            <SectionErrorBoundary name="Workforce Dashboard">
              <WorkforceDashboard />
            </SectionErrorBoundary>
          ) : activeTab === "workforce-candidates" && isAuthenticated ? (
            <SectionErrorBoundary name="Workforce Candidates">
              <WorkforceCandidates />
            </SectionErrorBoundary>
          ) : activeTab === "workforce-roles" && isAuthenticated ? (
            <SectionErrorBoundary name="Workforce Roles">
              <WorkforceRoles />
            </SectionErrorBoundary>
          ) : activeTab === "workforce-certifications" && isAuthenticated ? (
            <SectionErrorBoundary name="Workforce Certifications">
              <WorkforceCertifications />
            </SectionErrorBoundary>
          ) : activeTab === "workforce-curricula" && isAuthenticated ? (
            <SectionErrorBoundary name="Workforce Curricula">
              <WorkforceCurricula />
            </SectionErrorBoundary>
          ) : activeTab === "workforce-learning-hub" && isAuthenticated ? (
            <SectionErrorBoundary name="Learning Hub">
              <LearningHubPage />
            </SectionErrorBoundary>
          ) : activeTab === "workforce-training-hub" && isAuthenticated ? (
            <SectionErrorBoundary name="Training Hub">
              <TrainingHubPage />
            </SectionErrorBoundary>
          ) : activeTab === "workforce-courses" && isAuthenticated ? (
            <SectionErrorBoundary name="Classroom Courses">
              <LmsCoursesAdminPage />
            </SectionErrorBoundary>
          ) : activeTab === "workforce-learn" && isAuthenticated ? (
            <SectionErrorBoundary name="My Learning">
              <StaffLearnPage />
            </SectionErrorBoundary>
          ) : activeTab === "workforce-mentoring" && isAuthenticated ? (
            <SectionErrorBoundary name="Workforce Mentoring">
              <WorkforceMentoring />
            </SectionErrorBoundary>
          ) : activeTab === "workforce-training-schedule" && isAuthenticated ? (
            <SectionErrorBoundary name="Workforce Training Schedule">
              <WorkforceTrainingSchedule />
            </SectionErrorBoundary>
          ) : activeTab === "workforce-roster" && isAuthenticated ? (
            <SectionErrorBoundary name="Workforce Roster">
              <WorkforceRoster />
            </SectionErrorBoundary>
          ) : activeTab === "workforce-skills-gap" && isAuthenticated ? (
            <SectionErrorBoundary name="Workforce Skills Gap">
              <WorkforceSkillsGap />
            </SectionErrorBoundary>
          ) : activeTab === "workforce-cases" && isAuthenticated ? (
            <SectionErrorBoundary name="Workforce Cases">
              <WorkforceCases />
            </SectionErrorBoundary>
          ) : activeTab === "workforce-ai-assistant" && isAuthenticated ? (
            <SectionErrorBoundary name="Workforce AI Assistant">
              <WorkforceAIAssistant />
            </SectionErrorBoundary>
          ) : activeTab === "workforce-unified-dashboard" && isAuthenticated ? (
            <SectionErrorBoundary name="Workforce Unified Dashboard">
              <WorkforceUnifiedDashboard />
            </SectionErrorBoundary>
          ) : activeTab === "workforce-assessments" && isAuthenticated ? (
            <SectionErrorBoundary name="Workforce Assessments">
              <WorkforceAssessments />
            </SectionErrorBoundary>
          ) : activeTab === "behavioral-assessment" && isAuthenticated && behavioralAssessmentAllowed ? (
            <SectionErrorBoundary name="Behavioral Assessment">
              <BehavioralAssessmentPage />
            </SectionErrorBoundary>
          ) : activeTab === "social-needs-screener" && isAuthenticated && socialNeedsScreenerAllowed ? (
            <SectionErrorBoundary name="Social Needs Screener">
              <SocialNeedsScreenerPage />
            </SectionErrorBoundary>
          ) : activeTab === "cch-care-management" && isAuthenticated ? (
            <SectionErrorBoundary name="CCH Care Management">
              <CCHCareManagementPage onNavigate={(tab: string) => setActiveTab(tab)} />
            </SectionErrorBoundary>
          ) : activeTab === "cch-hub-admin" && isAuthenticated ? (
            <SectionErrorBoundary name="CCH Hub Admin">
              <CCHHubAdminPage onNavigate={(tab: string) => setActiveTab(tab)} />
            </SectionErrorBoundary>
          ) : activeTab === "cch-reporting" && isAuthenticated ? (
            <SectionErrorBoundary name="CCH Reporting">
              <CCHReportingPage onNavigate={(tab: string) => setActiveTab(tab)} />
            </SectionErrorBoundary>
          ) : activeTab?.startsWith("cch-") && isAuthenticated ? (
            <SectionErrorBoundary name="CCH Module">
              <CCHModulePage module={activeTab} onNavigate={(tab: string) => setActiveTab(tab)} />
            </SectionErrorBoundary>
          ) : activeTab === "care-plan-driven" && isAuthenticated ? (
            <SectionErrorBoundary name="Care-Plan-driven referrals">
              <CarePlanDrivenReferrals
                cboId={Number(localStorage.getItem('active_cbo_id') || 0)}
                onOpenReferral={(referralId: number) => {
                  setSelectedTCOReferralId(referralId);
                  setActiveTab("tco-referral-detail");
                }}
              />
            </SectionErrorBoundary>
          ) : activeTab === "care-coordination-hub" && isAuthenticated ? (
            <SectionErrorBoundary name="Care Coordination Hub">
              <CareCoordinationHub
                onViewInstance={(instanceId: number) => {
                  setSelectedTCOInstanceId(instanceId);
                  setActiveTab("tco-workflow-instance");
                }}
                onViewReferral={(referralId: number) => {
                  setSelectedTCOReferralId(referralId);
                  setActiveTab("tco-referral-detail");
                }}
                onEditTemplate={(templateId: number) => {
                  setSelectedTCOTemplateId(templateId);
                  setActiveTab("tco-workflow-template-edit");
                }}
                onCreateTemplate={() => setActiveTab("tco-workflow-template-create")}
                onCreateReferral={() => setActiveTab("tco-create-referral")}
              />
            </SectionErrorBoundary>
          ) : activeTab === "tco-workflow-instance" && isAuthenticated && selectedTCOInstanceId ? (
            <SectionErrorBoundary name="Workflow Instance Detail">
              <WorkflowInstanceDetail
                instanceId={selectedTCOInstanceId}
                onBack={() => setActiveTab("care-coordination-hub")}
                onViewReferral={(referralId: number) => {
                  setSelectedTCOReferralId(referralId);
                  setActiveTab("tco-referral-detail");
                }}
              />
            </SectionErrorBoundary>
          ) : activeTab === "tco-dashboard" && isAuthenticated ? (
            <SectionErrorBoundary name="TCO Care Dashboard">
              <TCOWorkflowDashboard
                onViewInstance={(instanceId: number) => {
                  setSelectedTCOInstanceId(instanceId);
                  setActiveTab("tco-workflow-instance");
                }}
                onEditTemplate={(templateId: number) => {
                  setSelectedTCOTemplateId(templateId);
                  setActiveTab("tco-workflow-template-edit");
                }}
                onCreateTemplate={() => setActiveTab("tco-workflow-template-create")}
              />
            </SectionErrorBoundary>
          ) : activeTab === "tco-service-referrals" && isAuthenticated ? (
            <SectionErrorBoundary name="TCO Service Referrals">
              <TCOServiceReferralsList
                onSelectReferral={(referralId: number) => {
                  setSelectedTCOReferralId(referralId);
                  setActiveTab("tco-referral-detail");
                }}
                onCreateNew={() => setActiveTab("tco-create-referral")}
              />
            </SectionErrorBoundary>
          ) : activeTab === "tco-create-referral" && isAuthenticated ? (
            <SectionErrorBoundary name="TCO Create Referral">
              <TCOReferralCreateForm
                open={true}
                onClose={() => setActiveTab("tco-service-referrals")}
                onSuccess={(referralId: number) => {
                  setSelectedTCOReferralId(referralId);
                  setActiveTab("tco-referral-detail");
                }}
              />
            </SectionErrorBoundary>
          ) : activeTab === "tco-workflow-template-create" && isAuthenticated ? (
            <SectionErrorBoundary name="TCO Workflow Template Create">
              <NewWorkflowTemplatePage
                onBack={() => setActiveTab("care-coordination-hub")}
                onSuccess={() => setActiveTab("care-coordination-hub")}
              />
            </SectionErrorBoundary>
          ) : activeTab === "tco-workflow-template-edit" && isAuthenticated && selectedTCOTemplateId ? (
            <SectionErrorBoundary name="TCO Workflow Template Edit">
              <EditWorkflowTemplatePage
                templateId={selectedTCOTemplateId}
                onBack={() => setActiveTab("care-coordination-hub")}
              />
            </SectionErrorBoundary>
          ) : activeTab === "tco-referral-detail" && isAuthenticated && selectedTCOReferralId ? (
            <SectionErrorBoundary name="TCO Referral Detail">
              <ServiceReferralWorkspace
                referralId={selectedTCOReferralId}
                onBack={() => setActiveTab("care-coordination-hub")}
              />
            </SectionErrorBoundary>
          ) : activeTab === "tco-workflows" && isAuthenticated ? (
            <SectionErrorBoundary name="TCO Care Workflows">
              <TCOWorkflowDashboard
                onViewInstance={(instanceId: number) => {
                  setSelectedTCOInstanceId(instanceId);
                  setActiveTab("tco-workflow-instance");
                }}
                onEditTemplate={(templateId: number) => {
                  setSelectedTCOTemplateId(templateId);
                  setActiveTab("tco-workflow-template-edit");
                }}
                onCreateTemplate={() => setActiveTab("tco-workflow-template-create")}
              />
            </SectionErrorBoundary>
          ) : activeTab === "tco-workflow-templates" && isAuthenticated ? (
            <SectionErrorBoundary name="TCO Workflow Templates">
              <TCOWorkflowDashboard
                onViewInstance={(instanceId: number) => {
                  setSelectedTCOInstanceId(instanceId);
                  setActiveTab("tco-workflow-instance");
                }}
                onEditTemplate={(templateId: number) => {
                  setSelectedTCOTemplateId(templateId);
                  setActiveTab("tco-workflow-template-edit");
                }}
                onCreateTemplate={() => setActiveTab("tco-workflow-template-create")}
              />
            </SectionErrorBoundary>
          ) : activeTab === "sponsor-retention" && isAuthenticated ? (
            <SponsorRetentionDashboard sponsorId={activeSponsorId || 0} />
          ) : activeTab === "program-marketplace" && isAuthenticated ? (
            activeSponsorId ? (
              <CBODiscovery sponsorId={activeSponsorId} programId={63} />
            ) : (
              <ProgramMarketplace cboId={activeOrgId || 1} />
            )
          ) : activeTab === "program-intelligence" && isAuthenticated ? (
            <ProgramIntelligenceCatalog cboId={activeOrgId || 1} />
          ) : activeTab === "fund-deposits" && isAuthenticated && canAccessPlatform ? (
            <FundDeposits />
          ) : activeTab === "member-payouts" && isAuthenticated && canAccessPlatform ? (
            <MemberPayouts />
          ) : activeTab === "reconciliation" && isAuthenticated && canAccessPlatform ? (
            <ReconciliationDashboard />
          ) : activeTab === "card-management" && isAuthenticated && canAccessPlatform ? (
            <CardManagement cboId={activeOrgId || undefined} />
          ) : activeTab === "transactions" && isAuthenticated ? (
            <TransactionHistory />
          ) : activeTab === "my-card" && isAuthenticated ? (
            <MemberCardPortal memberId={selectedMemberId ? parseInt(selectedMemberId, 10) : (typeof user?.id === 'number' ? user.id : parseInt(String(user?.id || 0), 10))} />
          ) : activeTab === "my-phone" && isAuthenticated ? (
            <MyPhoneNumberCard
              userId={typeof user?.id === 'number' ? user.id : parseInt(String(user?.id || 0), 10)}
              organizationId={(activeOrgId as number) || 0}
            />
          ) : activeTab === "sponsor-terms" && isAuthenticated ? (
            <SponsorTerms sponsorId={activeSponsorId || undefined} />
          ) : activeTab === "network-overview" && isAuthenticated ? (
            <NetworkOverview sponsorId={activeSponsorId || undefined} />
          ) : activeTab === "workflows" && isAuthenticated ? (
            <WorkflowTemplateList />
          ) : activeTab === "workflow-instances" && isAuthenticated ? (
            <WorkflowInstanceTracker />
          ) : activeTab === "automation-rules" && isAuthenticated ? (
            <AutomationRules />
          ) : activeTab === "form-templates" && isAuthenticated ? (
            <FormTemplateBuilder />
          ) : activeTab === "public-links" && isAuthenticated ? (
            <PublicLinksPage />
          ) : activeTab === "form-library" && isAuthenticated ? (
            <FormLibrary
              enrollmentId={selectedEnrollmentId ? parseInt(selectedEnrollmentId) : undefined}
              memberId={selectedMemberId ? parseInt(selectedMemberId) : undefined}
              organizationId={parseInt(localStorage.getItem('active_cbo_id') || '0') || undefined}
              onNavigate={(tab, memberId) => {
                if (memberId) setSelectedMemberId(memberId.toString());
                navigateWithHistory(tab);
              }}
            />
          ) : activeTab === "form-analytics" && isAuthenticated ? (
            <SectionErrorBoundary name="Form Analytics">
              <FormAnalyticsDashboard />
            </SectionErrorBoundary>
          ) : activeTab === "analytics" && isAuthenticated ? (
            <SectionErrorBoundary name="Analytics">
              <AnalyticsDashboard />
            </SectionErrorBoundary>
          ) : activeTab === "dashboard" && isAuthenticated ? (
            <SectionErrorBoundary name="Analytics">
              <AnalyticsDashboard />
            </SectionErrorBoundary>
          ) : activeTab === "integrations" && isAuthenticated ? (
            <SectionErrorBoundary name="Integrations">
              <IntegrationsDashboard />
            </SectionErrorBoundary>
          ) : activeTab === "rag-admin" && isAuthenticated && canAccessPlatform ? (
            <SectionErrorBoundary name="RAG Administration">
              <RAGAdminDashboard />
            </SectionErrorBoundary>
          ) : activeTab === "developer-portal" && isAuthenticated && canAccessPlatform ? (
            <SectionErrorBoundary name="Developer Portal">
              <DeveloperPortalPage />
            </SectionErrorBoundary>
          ) : activeTab === "sandbox-management" && isAuthenticated && (user?.role === 'super_admin' || user?.role === 'ops_admin') ? (
            <SectionErrorBoundary name="Sandbox Management">
              <SandboxManagementPage />
            </SectionErrorBoundary>
          ) : activeTab === "test-runner" && isAuthenticated && canAccessPlatform ? (
            <SectionErrorBoundary name="Test Runner">
              <TestRunnerDashboard />
            </SectionErrorBoundary>
          ) : activeTab === "calendar" && isAuthenticated ? (
            <SectionErrorBoundary name="Calendar">
              <CalendarPage onNavigate={(tab) => navigateWithHistory(tab)} />
            </SectionErrorBoundary>
          ) : activeTab === "telehealth" && isAuthenticated ? (
            <SectionErrorBoundary name="Telehealth">
              <TelehealthDashboard onNavigate={(tab) => navigateWithHistory(tab)} />
            </SectionErrorBoundary>
          ) : activeTab === "telehealth-reports" && isAuthenticated ? (
            <SectionErrorBoundary name="Analytics">
              <AnalyticsDashboard initialTab="telehealth" />
            </SectionErrorBoundary>
          ) : activeTab === "telehealth-waiting" && isAuthenticated ? (
            <SectionErrorBoundary name="Telehealth Waiting Room">
              <TelehealthWaitingRoom
                sessionId={parseInt(localStorage.getItem('telehealth_session_id') || '0', 10)}
                onJoinCall={() => navigateWithHistory('telehealth-session')}
                onCancel={() => navigateWithHistory('telehealth')}
                onMarkNoShow={() => navigateWithHistory('telehealth')}
              />
            </SectionErrorBoundary>
          ) : activeTab === "telehealth-session" && isAuthenticated ? (
            <SectionErrorBoundary name="Telehealth Session">
              <TelehealthVideoRoomWrapper
                sessionId={parseInt(localStorage.getItem('telehealth_session_id') || '0', 10)}
                onEndCall={() => navigateWithHistory('telehealth')}
                onBackToDashboard={() => navigateWithHistory('telehealth')}
              />
            </SectionErrorBoundary>
          ) : activeTab === "telehealth-encounters" && isAuthenticated ? (
            <SectionErrorBoundary name="Telehealth Encounters">
              <TelehealthEncountersList onBack={() => navigateWithHistory('telehealth')} />
            </SectionErrorBoundary>
          ) : activeTab === "telehealth-by-member" && isAuthenticated ? (
            <SectionErrorBoundary name="Telehealth By Member">
              <TelehealthByMember onBack={() => navigateWithHistory('telehealth')} />
            </SectionErrorBoundary>
          ) : activeTab === "oauth-integrations" && isAuthenticated ? (
            <SectionErrorBoundary name="OAuth Integrations">
              <OAuthIntegrations />
            </SectionErrorBoundary>
          ) : activeTab === "ai-orchestration" && isAuthenticated ? (
            <SectionErrorBoundary name="AI Orchestration">
              <AIOrchestrationDashboard />
            </SectionErrorBoundary>
          ) : activeTab === "external-services" && isAuthenticated ? (
            <SectionErrorBoundary name="External Services Catalog">
              <ExternalServicesCatalog />
            </SectionErrorBoundary>
          ) : activeTab === "groups" && isAuthenticated ? (
            <GroupsPage onSendMessage={(groupId) => {
              // Navigate to bulk communications with the selected group
              navigateWithHistory("bulk-communications");
              // Store the selected group ID for the communications page
              localStorage.setItem('selected_group_id', groupId);
            }} />
          ) : activeTab === "bulk-communications" && isAuthenticated ? (
            <BulkCommunicationsPage />
          ) : activeTab === "campaigns" && isAuthenticated ? (
            <CampaignsPage />
          ) : activeTab === "chatflows" && isAuthenticated && activeOrgId ? (
            <ChatflowBuilder organizationId={activeOrgId} />
          ) : activeTab === "communication-history" && isAuthenticated ? (
            <CommunicationHistoryPage />
          ) : activeTab === "workspace-messaging" && isAuthenticated ? (
            <div className="h-[calc(100dvh-3.5rem)] min-h-0 overflow-hidden">
              <MessagingWorkspace />
            </div>
          ) : activeTab === "inbox" && isAuthenticated ? (
            // TRUS-709: the retired 2-Way Inbox; redirected to the member Inbox (effect below).
            <div className="h-[calc(100dvh-3.5rem)] min-h-0 overflow-hidden">
              <MessagingWorkspace initialView="members" />
            </div>
          ) : activeTab === "csr-inbox" && isAuthenticated ? (
            <CSRInboxPage
              organizationId={activeOrgId || 303}
              currentUserId={typeof user?.id === 'number' ? user.id : parseInt(String(user?.id || '0'), 10)}
            />
          ) : activeTab === "template-library" && isAuthenticated ? (
            <CommunicationTemplatesHub
              cboId={activeOrgId || 303}
              currentUserId={typeof user?.id === 'number' ? user.id : parseInt(String(user?.id || '0'), 10)}
              initialTab="universal"
              onBack={navigationHistory.length > 1 ? navigateBack : undefined}
            />
          ) : activeTab === "referral-automation" && isAuthenticated ? (
            <div className="p-6">
              <ReferralAutomationRuleEditor cboId={activeOrgId || 303} />
            </div>
          ) : activeTab === "data-ingestion" && isAuthenticated ? (
            <div className="space-y-6 p-6">
              <div>
                <h1 className="mb-4 text-xl font-semibold">Nightly Data Ingestion</h1>
                <UUHPDataIngestionPanel cboId={activeOrgId || 303} />
              </div>
              <div className="border-t pt-6">
                <h2 className="mb-3 text-base font-semibold">Audit Export</h2>
                <p className="mb-3 text-sm text-gray-500">Export HIPAA-compliant audit log entries for any date range.</p>
                <AuditExportButton cboId={activeOrgId || 303} />
              </div>
            </div>
          ) : activeTab === "campaign-progress" && isAuthenticated ? (
            (() => {
              // Campaign Progress now lives as a tab inside the Reports
              // distribution page so it shares the MCO picker, the
              // schedule-report config, and the approval queue. Redirect.
              if (typeof window !== 'undefined') {
                window.location.hash = '#campaign-progress';
                setTimeout(() => navigateWithHistory('report-distribution'), 0);
              }
              return <div className="p-6 text-sm text-gray-500">Opening Reports → Campaign Progress…</div>;
            })()
          ) : activeTab === "journey-builder" && isAuthenticated ? (
            <div className="p-6">
              <h1 className="mb-4 text-xl font-semibold">Campaign Builder</h1>
              <UUHPJourneyBuilderPage cboId={activeOrgId || 303} />
            </div>
          ) : activeTab === "campaign-setup" && isAuthenticated ? (
            <SectionErrorBoundary name="Campaign Set Up">
              <CampaignSetUpPage cboId={activeOrgId || 303} />
            </SectionErrorBoundary>
          ) : activeTab === "social-media-connections" && isAuthenticated && activeOrgId ? (
            <SocialMediaSettings organizationId={activeOrgId} isAdmin={user?.role === 'super_admin' || user?.role === 'org_admin'} />
          ) : activeTab === "whatsapp-templates" && isAuthenticated ? (
            <WhatsAppTemplateManager />
          ) : activeTab === "whatsapp-business" && isAuthenticated ? (
            <WhatsAppBusinessSettings />
          ) : activeTab === "phone-management" && isAuthenticated ? (
            <PhoneNumberManagement />
          ) : activeTab === "sender-assignments" && isAuthenticated ? (
            <SenderNumberAssignments />
          ) : activeTab === "sender-verification" && isAuthenticated ? (
            <SenderVerificationWizard />
          ) : activeTab === "media-library" && isAuthenticated ? (
            <MediaLibrary />
          ) : activeTab === "interactive-content" && isAuthenticated ? (
            <InteractiveContentBuilder />
          ) : activeTab === "grant-calendar" && isAuthenticated ? (
            <SectionErrorBoundary name="Grant Calendar">
              <GrantCalendarDashboard />
            </SectionErrorBoundary>
          ) : activeTab === "donations" && isAuthenticated ? (
            <SectionErrorBoundary name="Donations">
              <DonationsPage />
            </SectionErrorBoundary>
          ) : activeTab === "donor-pipeline" && isAuthenticated ? (
            <SectionErrorBoundary name="Donor Pipeline">
              <DonorPipelineBoard />
            </SectionErrorBoundary>
          ) : activeTab === "donor-events" && isAuthenticated ? (
            <SectionErrorBoundary name="Donor Events">
              <DonorEventsPage />
            </SectionErrorBoundary>
          ) : activeTab === "donor-cohorts" && isAuthenticated ? (
            <SectionErrorBoundary name="Donor Cohorts">
              <CohortBuilder />
            </SectionErrorBoundary>
          ) : activeTab === "sponsor-cohort" && isAuthenticated ? (
            <SectionErrorBoundary name="Our Cohort">
              <SponsorCohort />
            </SectionErrorBoundary>
          ) : activeTab === "doulas" && isAuthenticated ? (
            <SectionErrorBoundary name="Doulas">
              <DoulasPage />
            </SectionErrorBoundary>
          ) : activeTab === "provider-referral-inbox" && isAuthenticated ? (
            // TRUS-728: referrals other orgs sent to the active org (nav: care.provider_referral_inbox).
            <SectionErrorBoundary name="Referral Inbox">
              <ProviderAcceptancePortal providerName={activeOrgName} />
            </SectionErrorBoundary>
          ) : activeTab === "sponsor-funded" && isAuthenticated ? (
            <SectionErrorBoundary name="What We Funded">
              <SponsorFundedPayments />
            </SectionErrorBoundary>
          ) : activeTab === "fundraising-home" && isAuthenticated ? (
            <SectionErrorBoundary name="Fundraising">
              <FundraisingHome />
            </SectionErrorBoundary>
          ) : activeTab === "mutual-aid" && isAuthenticated ? (
            <SectionErrorBoundary name="Mutual Aid">
              <MutualAidBoard />
            </SectionErrorBoundary>
          ) : activeTab === "donor-pipeline" && isAuthenticated ? (
            <SectionErrorBoundary name="Donor Pipeline">
              <DonorPipelineBoard />
            </SectionErrorBoundary>
          ) : activeTab === "org-settings" && isAuthenticated && config.isTriveny ? (
            // Triveny: the "Org page" is the operator's combined Organizations
            // (sign-up directory) + Team surface, opened on Organizations.
            <TrivenyWorkspaceAdmin initialTab="organizations" />
          ) : activeTab === "client-billing-admin" && isAuthenticated ? (
            // Independent Client Billing & Payments page scoped to one org,
            // opened via the $ icon in CBOManagement (org-admin only).
            (billingOrgId || activeOrgId) ? (
              <div>
                <button
                  onClick={() => { setBillingOrgId(null); navigateWithHistory('cbo-management'); }}
                  className="mx-auto max-w-7xl px-6 pt-4 flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
                >
                  ← Back to organizations
                </button>
                {billingOrgName && (
                  <div className="mx-auto max-w-7xl px-6 pt-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {billingOrgName} — Billing &amp; Payments
                  </div>
                )}
                <ClientBillingPage organizationId={(billingOrgId || activeOrgId) as number} />
              </div>
            ) : (
              <div className="p-12 text-center text-gray-500">Loading client billing…</div>
            )
          ) : activeTab === "org-settings" && isAuthenticated ? (
            // Route ALWAYS matches when activeTab=org-settings + authenticated.
            // settingsOrgId is set when the user clicks an org row in
            // CBOManagement; the command palette / direct nav skip that
            // step, so resolve to activeOrgId at render. Previous `&&
            // settingsOrgId` gate caused fall-through to AnalyticsDashboard.
            (settingsOrgId || activeOrgId) ? (
              <OrganizationSettingsPage
                organizationId={(settingsOrgId || activeOrgId) as number}
                organizationName={settingsOrgName || activeOrgName}
                onBack={() => { setSettingsOrgId(null); navigateWithHistory('cbo-management'); }}
                initialTab="billing"
              />
            ) : (
              <div className="p-12 text-center text-gray-500">Loading organization settings…</div>
            )
          ) : activeTab === "cbo-management" && isAuthenticated ? (
            <>
              <CBOManagement
                onEditOrganization={(org) => {
                  setSelectedOrg(org);
                  setCboFormOpen(true);
                }}
                onCreateOrganization={() => {
                  setSelectedOrg(null);
                  setCboFormOpen(true);
                }}
                onNavigateToSettings={(orgId, orgName) => {
                  setSettingsOrgId(orgId);
                  setSettingsOrgName(orgName);
                  navigateWithHistory('org-settings');
                }}
                onOpenClientBilling={(orgId, orgName) => {
                  setBillingOrgId(orgId);
                  setBillingOrgName(orgName);
                  navigateWithHistory('client-billing-admin');
                }}
                onNavigateToUsers={(orgId, orgName) => {
                  // Switch the active org context so UserManagement loads
                  // users for the row the admin clicked (relevant for
                  // multi-org WeaveIn admins; harmless on Triveny which
                  // only ever has one row).
                  setActiveOrgId(orgId);
                  setActiveOrgName(orgName);
                  navigateWithHistory('user-management');
                }}
                refreshTrigger={cboRefreshTrigger}
                currentUserRole={user?.role}
                currentUserCboId={activeOrgId || undefined}
              />
              <CBOForm
                organization={selectedOrg}
                isOpen={cboFormOpen}
                onClose={() => {
                  setCboFormOpen(false);
                  setSelectedOrg(null);
                }}
                onSuccess={() => {
                  console.log('✅ Organization form success - triggering refresh');
                  setCboFormOpen(false);
                  setSelectedOrg(null);
                  setCboRefreshTrigger(prev => prev + 1);
                }}
              />
            </>
          ) : activeTab === "organization-setup-wizard" && isAuthenticated ? (
            <OrganizationSetupWizard
              cboId={activeOrgId || undefined}
              onComplete={() => navigateWithHistory('cbo-management')}
              onCancel={() => navigateWithHistory('cbo-management')}
            />
          ) : activeTab === "user-management" && isAuthenticated ? (
            <UserManagement
              cboId={activeOrgId || 1}
              // TRUS-513: the privilege gate needs to tell "org 1" apart from
              // "org not resolved yet", which `|| 1` above destroys. TRUS-435
              // leaves active_cbo_id UNSET for a super_admin whose token has no
              // cbo_id, so that fallback silently reads as the platform org.
              resolvedOrgId={activeOrgId ?? null}
              organizationName={activeOrgName}
              currentUserRole={user?.role || "viewer"}
              onNavigate={handleUserManagementNavigate}
            />
          ) : activeTab === "user-permissions" && isAuthenticated && permissionsViewerContext ? (
            <EffectivePermissionsViewer
              userId={permissionsViewerContext.userId}
              userName={permissionsViewerContext.userName}
              cboId={activeOrgId || 1}
              onBack={() => navigateWithHistory('user-management')}
              onNavigate={navigateWithHistory}
            />
          ) : activeTab === "platform-roles" && isAuthenticated && config.isTriveny ? (
            <PlatformRolesPage />
          ) : activeTab === "platform-roles" && isAuthenticated && activeOrgId && isOrgAdmin ? (
            // WeaveIn: platform-roles is the Triveny twin of access-control. If a
            // user lands here (e.g. via "Go to Page"), render the WeaveIn Roles &
            // Permissions instead of a blank screen.
            <UnifiedAccessControl
              organizationId={activeOrgId}
              organizationName={activeOrgName}
            />
          ) : activeTab === "access-control" && isAuthenticated && activeOrgId && isOrgAdmin ? (
            <UnifiedAccessControl
              organizationId={activeOrgId}
              organizationName={activeOrgName}
            />
          ) : activeTab === "organization-roles" && isAuthenticated && activeOrgId && isOrgAdmin ? (
            <UnifiedAccessControl
              organizationId={activeOrgId}
              organizationName={activeOrgName}
            />
          ) : activeTab === "feature-capabilities" && isAuthenticated && activeOrgId && isOrgAdmin ? (
            <UnifiedAccessControl
              organizationId={activeOrgId}
              organizationName={activeOrgName}
            />
          ) : activeTab === "page-capabilities" && isAuthenticated && activeOrgId && isOrgAdmin ? (
            <UnifiedAccessControl
              organizationId={activeOrgId}
              organizationName={activeOrgName}
            />
          ) : activeTab === "role-page-access" && isAuthenticated && activeOrgId && isOrgAdmin ? (
            <UnifiedAccessControl
              organizationId={activeOrgId}
              organizationName={activeOrgName}
            />
          ) : activeTab === "nav-layout" && isAuthenticated && activeOrgId && isOrgAdmin ? (
            <NavigationLayoutAdmin
              organizationId={activeOrgId}
              organizationName={activeOrgName}
            />
          ) : activeTab === "data-privacy-settings" && isAuthenticated && activeOrgId && isOrgAdmin ? (
            <DataPrivacySettings
              organizationId={activeOrgId}
              organizationName={activeOrgName}
            />
          ) : activeTab === "security-compliance" && isAuthenticated && activeOrgId && isOrgAdmin ? (
            <SecurityComplianceDashboard
              organizationId={activeOrgId}
              organizationName={activeOrgName}
              onNavigate={(tab) => setActiveTab(tab)}
            />
          ) : activeTab === "break-glass-management" && isAuthenticated && activeOrgId && isOrgAdmin ? (
            <BreakGlassManagement
              organizationId={activeOrgId}
              organizationName={activeOrgName}
            />
          ) : activeTab === "external-systems" && isAuthenticated && activeOrgId && isOrgAdmin ? (
            <ExternalSystemsManagement
              organizationId={activeOrgId}
              organizationName={activeOrgName}
            />
          ) : activeTab === "field-sensitivity-settings" && isAuthenticated && activeOrgId && isOrgAdmin ? (
            <FieldSensitivitySettings
              organizationId={activeOrgId}
              organizationName={activeOrgName}
            />
          ) : activeTab === "ocr-template-settings" && isAuthenticated && activeOrgId && isOrgAdmin ? (
            <OCRTemplateSettings
              organizationId={activeOrgId}
              organizationName={activeOrgName}
            />
          ) : activeTab === "user-preferences" && isAuthenticated && config.isTriveny ? (
            // Triveny: WeaveIn UserPreferences depends on /api/user/preferences
            // (healthcare endpoint that 401s for platform JWTs). Use the
            // Triveny-aware version which keeps theme/language/accessibility
            // client-side and pulls 2FA status from the platform endpoint.
            <TrivenyUserPreferences
              defaultTab={(() => { const t = localStorage.getItem('preferences_default_tab'); if (t) localStorage.removeItem('preferences_default_tab'); return t || 'general'; })()}
              onNavigate={(tab) => navigateWithHistory(tab)}
            />
          ) : activeTab === "user-preferences" && isAuthenticated ? (
            <UserPreferences
              defaultTab={(() => { const t = localStorage.getItem('preferences_default_tab'); if (t) localStorage.removeItem('preferences_default_tab'); return t || 'general'; })()}
              onNavigate={(tab) => navigateWithHistory(tab)}
            />
          ) : activeTab === "translation-management" && isAuthenticated && (user?.role === "super_admin" || user?.role === "org_admin") ? (
            <TranslationManagement />
          ) : activeTab === "communication-templates" && isAuthenticated ? (
            <CommunicationTemplatesHub
              cboId={activeOrgId || 303}
              currentUserId={typeof user?.id === 'number' ? user.id : parseInt(String(user?.id || '0'), 10)}
              initialTab="my-org"
              onBack={navigationHistory.length > 1 ? navigateBack : undefined}
            />
          ) : activeTab === "template-variables" && isAuthenticated ? (
            <TemplateVariablesPage />
          ) : activeTab === "campaign-templates" && isAuthenticated ? (
            <CampaignTemplates
              onBack={navigationHistory.length > 1 ? navigateBack : undefined}
            />
          ) : activeTab === "letter-templates" && isAuthenticated ? (
            <LetterTemplatesPage
              onBack={navigationHistory.length > 1 ? navigateBack : undefined}
            />
          ) : activeTab === "register" ? (
            <RegisterWithInvite />
          ) : activeTab === "signup" ? (
            <SelfServiceSignup onSuccess={() => {
              // After successful signup, reload to show viewer dashboard
              window.location.reload();
            }} />
          ) : activeTab === "forgot-password" ? (
            <ForgotPassword />
          ) : activeTab === "reset-password" ? (
            <ResetPassword />
          ) : activeTab === "otfrtf" && isAuthenticated ? (
            <OTFRTFSubmission
              onSubmit={handleSubmissionComplete}
              onBack={navigationHistory.length > 1 ? navigateBack : undefined}
            />
          ) : activeTab === "billing-claims" && isAuthenticated && canAccessPlatform ? (
            <ClaimsPage onNavigate={(tab: string) => navigateWithHistory(tab)} />
          ) : activeTab === "document-processing" && isAuthenticated && canAccessPlatform ? (
            <DocumentProcessingPage onNavigate={(tab: string) => navigateWithHistory(tab)} />
          ) : activeTab === "billing-claims-bulk" && isAuthenticated && canAccessPlatform ? (
            <BulkClaimEntryPage onNavigate={(tab: string) => navigateWithHistory(tab)} />
          ) : activeTab === "billing-claims-new" && isAuthenticated && canAccessPlatform ? (
            <NewClaimPage onNavigate={(tab: string) => navigateWithHistory(tab)} />
          ) : activeTab === "billing-analytics" && isAuthenticated ? (
            <BillingAnalyticsPage />
          ) : activeTab === "program-billing" && isAuthenticated ? (
            <ProgramBillingPage />
          ) : activeTab === "pmpm-billing" && isAuthenticated ? (
            <PMPMBillingPage />
          ) : activeTab === "pdf-templates" && isAuthenticated ? (
            <PDFTemplatesPage />
          ) : activeTab === "scheduled-reports" && isAuthenticated ? (
            <ScheduledReportsPage />
          ) : activeTab === "report-distribution" && isAuthenticated ? (
            <ReportDistributionPage />
          ) : activeTab === "ecm-progress-notes-report" && isAuthenticated ? (
            <EcmProgressNotesReportPage />
          ) : activeTab === "clearinghouse-settings" && isAuthenticated ? (
            <ClearinghousePage />
          ) : activeTab === "mcp-billing-rules" && isAuthenticated ? (
            <McpBillingRulesPage />
          ) : activeTab === "billing-ai" && isAuthenticated ? (
            <BillingAIPage />
          ) : activeTab === "saml-sso-config" && isAuthenticated ? (
            <SAMLSSOConfigPage />
          ) : activeTab === "hl7-config" && isAuthenticated ? (
            <HL7ConfigPage />
          ) : activeTab === "ihe-config" && isAuthenticated ? (
            <IHEConfigPage />
          ) : activeTab === "employer-benefits-management" && isAuthenticated ? (
            <EmployerBenefitsManagementPage />
          ) : activeTab === "work-compliance-dashboard" && isAuthenticated && selectedMemberId ? (
            <MemberWorkComplianceDashboard
              memberId={parseInt(selectedMemberId, 10)}
            />
          ) : activeTab === "work-compliance-submit" && isAuthenticated && selectedMemberId ? (
            <WorkActivitySubmissionForm
              memberId={parseInt(selectedMemberId, 10)}
              enrollmentId={0}
              onSuccess={() => navigateWithHistory('work-compliance-dashboard')}
            />
          ) : activeTab === "work-compliance-upload" && isAuthenticated && selectedMemberId ? (
            <WorkDocumentUploader
              workActivityLogId={0}
              enrollmentId={0}
              onUploadComplete={() => navigateWithHistory('work-compliance-dashboard')}
            />
          ) : activeTab === "work-compliance-history" && isAuthenticated && selectedMemberId ? (
            <MemberComplianceHistory
              memberId={parseInt(selectedMemberId, 10)}
              enrollmentId={0}
            />
          ) : activeTab === "cbo-compliance-dashboard" && isAuthenticated ? (
            <CBOComplianceDashboard
              cboId={(user as any)?.organization_id || 1}
            />
          ) : activeTab === "ecm-compliance" && isAuthenticated && canAccessPlatform ? (
            <ECMComplianceDashboard
              onNavigate={navigateWithHistory}
            />
          ) : activeTab === "service-referrals" && isAuthenticated ? (
            <ServiceReferralsList
              employerId={activeEmployerId ? parseInt(activeEmployerId) : undefined}
              onSelectReferral={(referralId) => {
                setSelectedReferralId(referralId.toString());
                navigateWithHistory('service-referral-workspace');
              }}
              onCreateNew={() => navigateWithHistory('create-referral')}
            />
          ) : activeTab === "help-line" && isAuthenticated ? (
          <HelpLineQueue
            onOpenReferral={(referralId) => {
              setSelectedReferralId(referralId.toString());
              navigateWithHistory('service-referral-workspace');
            }}
          />
        ) : activeTab === "service-referral-workspace" && isAuthenticated && selectedReferralId ? (
            <ServiceReferralWorkspace
              referralId={parseInt(selectedReferralId, 10)}
              onClose={() => navigateWithHistory('service-referrals')}
              onStatusChange={() => {
                // Refresh referrals after status change
              }}
            />
          ) : activeTab === "cbo-reimbursement" && isAuthenticated ? (
            <CBOReimbursementDashboard />
          ) : activeTab === "outreach-billing" && isAuthenticated ? (
            <OutreachBillingPage onNavigate={(tab: string) => navigateWithHistory(tab)} />
          ) : activeTab === "outreach-worklist" && isAuthenticated ? (
            <OutreachWorklistPage onNavigate={(tab: string) => navigateWithHistory(tab)} />
          ) : activeTab === "outreach-type-config" && isAuthenticated ? (
            <OutreachTypesPage />
          ) : activeTab === "client-billing" && isAuthenticated && canAccessPlatform ? (
            <ClientBillingPage />
          ) : activeTab === "railway-test" ? (
            <RailwayTest />
          ) : activeTab === "health-plans" && isAuthenticated ? (
            <HealthPlansPage
              onSelectPlan={(planId) => {
                setSelectedHealthPlanId(planId.toString());
                navigateWithHistory("health-plan-detail");
              }}
            />
          ) : activeTab === "health-plan-detail" && isAuthenticated && selectedHealthPlanId ? (
            <HealthPlanDetail
              planId={parseInt(selectedHealthPlanId)}
              onBack={() => handleTabChange('health-plans')}
            />
          ) : activeTab === "employer-plans" && isAuthenticated && selectedEmployerId ? (
            <EmployerPlansManager
              employerId={parseInt(selectedEmployerId)}
              onBack={() => navigateBack()}
            />
          ) : activeTab === "employer-benefits" && isAuthenticated ? (
            <EmployerBenefitsDashboard
              employerId={(user as any)?.employer_id}
              employerName={(user as any)?.employer_name}
              onBack={() => handleTabChange('health-plans')}
            />
          ) : !isAuthenticated && activeTab !== "home" ? (
            // Redirect non-authenticated users trying to access protected pages
            <Homepage
              onNavigateToDirectory={handleNavigateToDirectory}
              onNavigateToLiveFeed={handleNavigateToLiveFeed}
              onNavigateToMatchingEngine={handleNavigateToMatchingEngine}
              onNavigateToCapacity={handleNavigateToCapacity}
              onNavigateToSubmissions={handleNavigateToSubmissions}
              onCreateNew={showCreateFormHandler}
              onNavigateToFinder={handleNavigateToFinder}
              onNavigateToQueue={handleNavigateToQueue}
              onNavigateToLogin={handleNavigateToLogin}
              onNavigateToSignup={() => navigateWithHistory('signup')}
              onNavigateToRailwayTest={handleNavigateToRailwayTest}
              totalReferrals={referrals?.length || 0}
              totalProviders={providerCount || 0}
              autoMatchedCount={referrals?.filter(r => r.useMatchingEngine).length || 0}
            />
          ) : (
            <Homepage
              onNavigateToDirectory={handleNavigateToDirectory}
              onNavigateToLiveFeed={handleNavigateToLiveFeed}
              onNavigateToMatchingEngine={handleNavigateToMatchingEngine}
              onNavigateToCapacity={handleNavigateToCapacity}
              onNavigateToSubmissions={handleNavigateToSubmissions}
              onCreateNew={showCreateFormHandler}
              onNavigateToFinder={handleNavigateToFinder}
              onNavigateToQueue={handleNavigateToQueue}
              onNavigateToLogin={handleNavigateToLogin}
              onNavigateToSignup={() => navigateWithHistory('signup')}
              onNavigateToRailwayTest={handleNavigateToRailwayTest}
              totalReferrals={referrals?.length || 0}
              totalProviders={providerCount || 0}
              autoMatchedCount={referrals?.filter(r => r.useMatchingEngine).length || 0}
            />
          )}
        </div>

        {/* NOTE: Legacy FeedbackWidget removed - using native support-widget.js from index.html */}

        {/* Authentication Modal */}
        <AuthModal
          isOpen={showAuthModal}
          onClose={handleCloseAuthModal}
          onNavigateToForgotPassword={handleNavigateToForgotPassword}
        />
      </div>
    );
  }

  // Triveny.ai full-bleed home (landing surface). Returned BEFORE the desktop
  // layout so the sidebar/topbar chrome is hidden — the home page owns the
  // whole viewport for the launcher experience. Clicking any pillar launcher
  // or the My Day / Inbox top-nav buttons calls setActiveTab(...) which moves
  // off 'triveny-home' and re-renders into the desktop layout (sidebar back).
  // The sidebar's own "Home" item routes back to 'triveny-home' which fires
  // this early-return again, hiding the rail. Command palette + global
  // shortcuts still mount so ⌘K and the sidebar-restoring tabs stay reachable.
  if (config.isTriveny && isAuthenticated && activeTab === 'triveny-home') {
    // Open the home launcher on the pillar the user just came from (the most
    // recent pillar-mapped tab in history) instead of always defaulting to
    // Design. Operate has no home pillar, so it falls back to Design.
    const lastPillarTab = [...navigationHistory].reverse().find((t) => pillarForTab(t));
    const lastPillar = lastPillarTab ? pillarForTab(lastPillarTab) : null;
    const homeInitialPillar =
      lastPillar && lastPillar !== 'Operate' ? lastPillar : undefined;
    return (
      <>
        <GlobalShortcuts
          onNavigate={handleTabChange}
          onOpenAISearch={() => {
            window.dispatchEvent(new CustomEvent('open-ai-search'));
          }}
          onOpenCopilot={() => openCopilot()}
          onOpenCommandPalette={() => setIsCommandPaletteOpen(true)}
          onOpenGoToPage={() => {
            setIsCommandPaletteOpen(true);
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('command-palette-mode', { detail: 'navigation' }));
            }, 50);
          }}
          onOpenOrgSwitcher={() => {
            setIsCommandPaletteOpen(true);
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('command-palette-mode', { detail: 'orgs' }));
            }, 50);
          }}
        />
        <CommandPalette
          isOpen={isCommandPaletteOpen}
          onClose={() => setIsCommandPaletteOpen(false)}
          onNavigate={handleTabChange}
          onOpenAISearch={() => {
            window.dispatchEvent(new CustomEvent('open-ai-search'));
          }}
          onOpenCopilot={openCopilot}
          userRole={user?.role}
        />
        <TrivenyHome
          onNavigate={handleTabChange}
          onShowProfile={handleShowUserProfile}
          onLogout={logout}
          userName={user?.name || user?.email || null}
          tenantName={config.isTriveny ? 'Triveny.ai' : ((user as any)?.tenant_name || activeOrgName || null)}
          initialPillar={homeInitialPillar}
        />
        {/* First-login consent gate (Triveny only) — overlays the home if
            the tenant_user hasn't accepted the current versions of Terms /
            Privacy / AI Disclaimers. Self-closes once status is current. */}
        <TrivenyConsentGate userName={user?.name || (user as any)?.first_name || null} />
        {config.isTriveny && isAuthenticated && <OrgSwitcher />}
      </>
    );
  }

  // Desktop view. A page pop-out renders the same page without the shell
  // chrome: no sidebar / rail / pillar bar / mobile app bar / footer.
  const chromeless = IS_PAGE_POPOUT;
  // Under a tab strip (main window tab 1, or an iframe tab) the tab names the page.
  const pageUnderTab = mainTabs.visible || (chromeless && isFramedDocument());
  const fullHeightPage = isAuthenticated && FULL_HEIGHT_TABS.has(activeTab);
  // An iframe tab in front fills the same fixed-height area as a full-height page.
  const fixedHeightMain = fullHeightPage || !!mainTabs.activeFrame;
  return (
    <div className={`min-h-screen ${isV2Enabled ? 'v2-theme bg-paper' : config.isTriveny ? 'bg-[var(--v2-paper)]' : 'bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900'}`}>
      {/* RFP Feature: Skip to Content (Accessibility) */}
      <SkipToContent />

      {/* Triveny first-login consent gate — overlays inner tabs too. Self-
          renders only when /api/consent/status reports any *_needs_update. */}
      {config.isTriveny && isAuthenticated && (
        <TrivenyConsentGate userName={user?.name || (user as any)?.first_name || null} />
      )}
      {config.isTriveny && isAuthenticated && !chromeless && <OrgSwitcher />}

      {/* Global Keyboard Shortcuts & Command Palette */}
      {isAuthenticated && (
        <>
          <GlobalShortcuts
            onNavigate={shellNavigate}
            onOpenAISearch={() => {
              // Dispatch event that SidebarNavigation listens for to open AI search
              window.dispatchEvent(new CustomEvent('open-ai-search'));
            }}
            onOpenCopilot={() => openCopilot()}
            onOpenCommandPalette={() => setIsCommandPaletteOpen(true)}
            onOpenGoToPage={() => {
              setIsCommandPaletteOpen(true);
              setTimeout(() => {
                window.dispatchEvent(new CustomEvent('command-palette-mode', { detail: 'navigation' }));
              }, 50);
            }}
            onOpenOrgSwitcher={() => {
              setIsCommandPaletteOpen(true);
              // Small delay so palette mounts before switching mode
              setTimeout(() => {
                window.dispatchEvent(new CustomEvent('command-palette-mode', { detail: 'orgs' }));
              }, 50);
            }}
          />
          <CommandPalette
            isOpen={isCommandPaletteOpen}
            onClose={() => setIsCommandPaletteOpen(false)}
            onNavigate={shellNavigate}
            onOpenAISearch={() => {
              window.dispatchEvent(new CustomEvent('open-ai-search'));
            }}
            onOpenCopilot={openCopilot}
            userRole={user?.role}
          />
        </>
      )}

      {/* Copilot — mounted once for the whole app rather than inside either
          product branch, so it survives a branch switch and only one instance
          ever holds a stream. It self-hides when the backend says the caller
          has no tools (Triveny tenants, member-portal accounts). */}
      {isAuthenticated && !config.isTriveny && (
        <CopilotPanel
          isOpen={isCopilotOpen}
          onClose={() => setIsCopilotOpen(false)}
          initialQuestion={copilotSeed}
          pageContext={activeTab}
        />
      )}

      {/* RFP Feature: Offline Banner - always enabled (no capability gate since it needs to work when offline) */}
      {isAuthenticated && <OfflineBanner />}
      {/* Desktop update control — user-initiated install, shows only when an update is available */}
      <DesktopUpdateBanner />
      {/* Desktop View-menu refresh/reload handler (no UI) */}
      <DesktopRefreshHandler />
      {isAuthenticated && <SandboxBanner />}
      {/* TRUS-668: in-app calls — incoming-call banner + the open call's panel. Hidden unless the org has messaging_calls. */}
      {isAuthenticated && !config.isTriveny && <CallsHost />}
      {/* TRUS-668: desktop notifications for new messages (and other bell rows). Always mounted, so a collapsed sidebar (no bell) does not stop them. */}
      {isAuthenticated && !config.isTriveny && !chromeless && <StaffNotificationsHost onNavigate={shellNavigate} />}
      {isAuthenticated && !chromeless && <SandboxSplash />}
      {isAuthenticated && !chromeless && <SandboxDisclosure />}

      {/* RFP Feature: Guided Tour Overlay (capability-gated) */}
      {/* Healthcare-only: the capability endpoint sits behind get_current_user
          which only accepts healthcare JWTs. A Triveny platform JWT decodes
          with PLATFORM_JWT_SECRET (different secret), so jwt.InvalidTokenError
          → 401 "Invalid token" → useCapability error spam every 10s. The gate
          itself isn't useful on Triveny (no tours / accessibility panel for
          the platform product) so skip it entirely. */}
      {isAuthenticated && activeOrgId && !config.isTriveny && (
        <GuidedTourGate organizationId={activeOrgId} behavior="hide">
          <TourOverlay />
        </GuidedTourGate>
      )}

      {/* RFP Feature: Accessibility Settings Panel (capability-gated) — same
          reasoning as GuidedTourGate above. */}
      {isAuthenticated && activeOrgId && !config.isTriveny && (
        <AccessibilityGate organizationId={activeOrgId} behavior="hide">
          <AccessibilitySettingsPanel />
        </AccessibilityGate>
      )}

      {/* TRUS-747: always-on while a super admin is viewing as someone */}
      {isAuthenticated && <ImpersonationBanner />}

      {/* Session Timeout Warning */}
      {isAuthenticated && (
        <SessionTimeoutWarning
          timeRemaining={timeRemaining}
          warningSeconds={sessionWarningSeconds}
          onExtendSession={extendSession}
          onLogout={logout}
        />
      )}

      {/* Re-Consent Modal for existing sessions */}
      {showConsentModal && consentStatus && user?.email && token && (
        <ReConsentModal
          consentStatus={consentStatus}
          userEmail={user.email}
          accessToken={token}
          onComplete={() => {
            console.log('✅ Re-consent completed');
            setShowConsentModal(false);
            setConsentStatus(null);
          }}
        />
      )}

      <div className="flex min-h-screen w-full overflow-x-hidden">
        {/* Mobile Header - signed-in app chrome, phones only (< 768px).
            Gated on isAuthenticated: the public marketing pages render their
            own MarketingHeader, and this bar used to sit on top of it, so a
            phone visitor to the home page got two stacked headers — the first
            with no Trust lockup and an off-brand blue Login. */}
        {isMobile && !isTablet && isAuthenticated && !chromeless && (
          <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 dark:bg-gray-800/95 backdrop-blur-md border-b border-gray-200 dark:border-gray-700 shadow-sm md:hidden">
            <div className="flex items-center justify-between px-3 py-2.5">
                <button
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-900 dark:text-gray-100"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
                <div className="flex items-center space-x-2 min-w-0">
                  <img
                    src={branding?.brand_logo_url || (config.isTriveny ? "/triveny/logo.svg" : "/weavin-logo.png")}
                    alt={branding?.brand_name || (config.isTriveny ? "Triveny" : "WeaveIn")}
                    className="h-7 w-auto max-w-[120px] object-contain"
                    ref={(el) => {
                      if (!el || !config.isTriveny || branding?.brand_logo_url) return;
                      const isDark = document.documentElement.classList.contains('dark');
                      el.src = isDark ? '/triveny/logo-white.svg' : '/triveny/logo.svg';
                    }}
                    onError={(e) => { (e.target as HTMLImageElement).src = config.isTriveny ? '/triveny/wordmark.svg' : '/weavin-logo.png'; }}
                  />
                  <span className="font-semibold text-gray-800 dark:text-gray-100 text-sm truncate">
                    {branding?.brand_name || (config.isTriveny ? 'Triveny' : 'WeaveIn')}
                  </span>
                </div>
                <button
                  onClick={handleShowUserProfile}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-900 dark:text-gray-100"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </button>
            </div>
          </header>
        )}

        {/* Mobile Sidebar Overlay - only on phones (< 768px), not tablets */}
        {isAuthenticated && isMobile && !isTablet && sidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar - always visible on tablets (md+) and desktop (lg+), slide-in on phones */}
        {isAuthenticated && !isV2Enabled && !chromeless && (
          <div
            className={`fixed inset-y-0 left-0 z-50 transform transition-all duration-300 ease-in-out ${
              // On tablets and desktop: always visible
              // On phones: slide in/out based on sidebarOpen
              isTablet || !isMobile
                ? 'translate-x-0'
                : (sidebarOpen ? 'translate-x-0' : '-translate-x-full')
            } ${
              // Full width on phones, normal width on tablet/desktop
              isMobile && !isTablet ? 'w-full max-w-xs' : ''
            }`}
            style={
              !isMobile || isTablet
                ? config.isTriveny
                  ? { width: sidebarCollapsed ? '64px' : '210px' }  // Triveny rail collapses to icon rail
                  : { width: sidebarCollapsed ? '80px' : `${sidebarWidth}px` }
                : undefined
            }
          >
            {config.isTriveny ? (
              <TrivenyRail
                activeTab={shellActiveTab}
                onTabChange={(tab) => {
                  shellNavigate(tab);
                  if (isMobile && !isTablet) setSidebarOpen(false);
                }}
                onHomeClick={() => {
                  // Home is the full-bleed launcher, which has no strip: always the app's own page.
                  if (mainTabs.activeFrame) mainTabs.activate(PRIMARY_UID);
                  handleTabChange("triveny-home");
                  if (isMobile && !isTablet) setSidebarOpen(false);
                }}
                onOpenInNewTab={openNavInNewTab}
                currentUser={trivenyCurrentUser}
                tenantName={config.isTriveny ? 'Triveny.ai' : ((user as any)?.tenant_name || activeOrgName || undefined)}
                userRoleLabel={orgSpecificRole || user?.role || undefined}
                onShowProfile={handleShowUserProfile}
                onLogout={logout}
                onCloseMobile={() => setSidebarOpen(false)}
                collapsed={sidebarCollapsed}
                onToggleCollapse={() => setSidebarCollapsed((v) => !v)}
              />
            ) : null /* WeaveIn uses V2Sidebar (rendered below); this branch is Triveny-only */}
          </div>
        )}

        {/* Staff v2 Sidebar — shown when staff_v2_redesign capability is enabled */}
        {/* Phone backdrop overlay: tap to close */}
        {isAuthenticated && isV2Enabled && !chromeless && isMobile && !isTablet && sidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/50"
            onClick={() => setSidebarOpen(false)}
          />
        )}
        {isAuthenticated && isV2Enabled && !chromeless && (
          <div
            className={`fixed inset-y-0 left-0 z-50 transform transition-transform duration-200 ease-in-out ${
              // Tablet (md) and desktop (lg+): always on-screen.
              // Phone (< md): slide in/out based on sidebarOpen.
              isTablet || !isMobile
                ? 'translate-x-0'
                : (sidebarOpen ? 'translate-x-0' : '-translate-x-full')
            }`}
          >
            <V2Sidebar
              activeTab={shellActiveTab}
              organizationId={activeOrgId}
              orgType={activeEmployerId ? 'employer' : activeSponsorId ? 'sponsor' : 'cbo'}
              onTabChange={(tab) => {
                shellNavigate(tab);
                if (isMobile && !isTablet) setSidebarOpen(false);
              }}
              onOpenInNewTab={openNavInNewTab}
              userName={currentUser || undefined}
              userRole={orgSpecificRole || user?.role || 'Staff'}
              orgName={activeOrgName}
              userInitials={currentUser ? currentUser.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() : 'U'}
              onShowProfile={handleShowUserProfile}
              // Absent when the backend says this caller has no tools, so the
              // sidebar renders no launcher rather than one that shrugs.
              onOpenCopilot={copilotLauncher.enabled ? () => openCopilot() : undefined}
              assistantName={copilotLauncher.name}
              onLogout={logout}
              onCollapsedChange={setV2SidebarCollapsed}
              previewRole={v2PreviewRole}
              previewRoleId={
                v2PreviewRole
                  ? (v2PreviewRoles.find((r) => r.value === v2PreviewRole)?.id ?? null)
                  : null
              }
              onPreviewRoleChange={setV2PreviewRole}
              availableRoles={
                (orgSpecificRole === 'super_admin' || orgSpecificRole === 'ops_admin' || user?.role === 'super_admin' || user?.role === 'ops_admin')
                  ? v2PreviewRoles
                  : undefined
              }
              // "Open in…": this page in a new tab / window / the tab view.
              // Not on phones, where a second window is not a thing.
              headerActions={isMobile && !isTablet ? undefined : <OpenInMenu getSpec={() => currentPageRef.current} />}
            />
          </div>
        )}

        <main
          className={`flex-1 transition-all duration-300 ${
            fixedHeightMain ? 'flex h-[100dvh] min-h-0 flex-col overflow-hidden' : 'min-h-screen overflow-x-hidden'
          } ${
            isV2Enabled && isAuthenticated
              ? `v2-theme bg-paper text-ink ${isMobile && !isTablet && !chromeless ? 'pt-14 md:pt-0' : ''}`
              : `text-gray-900 dark:text-gray-100 ${
                  // Matches the fixed mobile app bar above, which only renders
                  // when signed in; public marketing pages must not be pushed down.
                  chromeless
                    ? ''
                    : isMobile && !isTablet && isAuthenticated
                    ? 'pt-14 md:pt-0'
                    : isAuthenticated && !config.isTriveny
                      ? 'md:pt-6 lg:pt-6'
                      : ''
                }`
          }`}
          style={
            isAuthenticated
              ? {
                  // Desktop (lg+): always apply sidebar margin
                  // Tablet (md): apply sidebar margin since sidebar is visible
                  // Phone (sm): no margin, sidebar is overlay
                  marginLeft: chromeless
                    ? '0'
                    : !isMobile || isTablet
                    ? (isV2Enabled
                        ? (v2SidebarCollapsed ? '60px' : '240px')
                        : config.isTriveny
                          ? (sidebarCollapsed ? '64px' : '210px')
                          : (sidebarCollapsed ? '80px' : `${sidebarWidth}px`))
                    : '0'
                }
              : undefined
          }
        >
        {/* "Turn on notifications" (TRUS-668): browser only, main window only, every page load / sign-in until registered. */}
        {isAuthenticated && !config.isTriveny && !chromeless && !isImpersonating() && <WebPushBanner portal="staff" />}
        {/* Main-window tab strip: tab 1 is this page; "+" opens more pages as tabs. */}
        {mainTabs.visible && !chromeless && (
          <MainTabStrip
            api={mainTabs}
            pages={popoutPages.pages}
            labelFor={popoutPages.labelFor}
            isDesktop={config.isDesktop}
          />
        )}
        {/* The app's own page (the primary tab). Kept mounted, only hidden, while an iframe tab is in front. */}
        <div
          className={mainTabs.activeFrame ? 'hidden' : fullHeightPage ? 'flex min-h-0 flex-1 flex-col' : 'contents'}
          data-testid="main-tab-native"
        >
        {/* Under a tab strip the tab names the page, so page headers drop their titles. */}
        <TabbedChrome.Provider value={pageUnderTab}>
        <V2LayoutShell activeTab={activeTab} isV2Enabled={isV2Enabled && isAuthenticated}>
        {/* Triveny: slim pillar context bar sits above all pillar pages
            (Design / Market / Engage / Operate). Renders nothing for
            tabs that don't map to a pillar (settings, user-management,
            home launcher, etc.). To revert: delete this line + the
            TvPillarBar import + the component file. */}
        {config.isTriveny && isAuthenticated && !chromeless && (
          <TvPillarBar
            activeTab={activeTab}
            userName={user?.name || user?.email || null}
            onShowProfile={handleShowUserProfile}
            onLogout={logout}
            actions={
              isMobile && !isTablet ? undefined : (
                <OpenInMenu
                  getSpec={() => currentPageRef.current}
                  className="inline-flex h-[26px] w-[26px] items-center justify-center rounded-md text-[var(--v2-ink-500,#6b7280)] hover:bg-black/5"
                />
              )
            }
          />
        )}
        {IS_PAGE_POPOUT && !config.isTriveny && pageAccess.loading ? (
          <V2Loading />
        ) : pagePopoutBlocked ? (
          <PopoutNoAccess />
        ) : showCreateForm || activeTab === "create-referral" ? (
          <CreateReferralForm
            onSubmit={handleCreateReferral}
            onCancel={() => {
              setShowCreateForm(false);
              if (activeTab === "create-referral") {
                navigateBack();
              }
            }}
            onBack={navigationHistory.length > 1 ? navigateBack : undefined}
            onComplete={handleCreateReferralComplete}
          />
        ) : activeTab === "home" ? (
          // Triveny: 'home' is the WeaveIn marketing Homepage — Triveny users
          // should always see the Triveny launcher instead.
          config.isTriveny && isAuthenticated ? (
            <TrivenyHome
              onNavigate={handleTabChange}
              onShowProfile={handleShowUserProfile}
              onLogout={logout}
              userName={user?.name || user?.email || null}
              tenantName={config.isTriveny ? 'Triveny.ai' : ((user as any)?.tenant_name || activeOrgName || null)}
            />
          ) : activeEmployerId && isAuthenticated ? (
            // Show EmployerJourneyDashboard for employer organizations
            <EmployerJourneyDashboard />
          ) : (
            <Homepage
              onNavigateToDirectory={handleNavigateToDirectory}
              onNavigateToLiveFeed={handleNavigateToLiveFeed}
              onNavigateToMatchingEngine={handleNavigateToMatchingEngine}
              onNavigateToCapacity={handleNavigateToCapacity}
              onNavigateToSubmissions={handleNavigateToSubmissions}
              onCreateNew={showCreateFormHandler}
              onNavigateToFinder={handleNavigateToFinder}
              onNavigateToQueue={handleNavigateToQueue}
              onNavigateToLogin={handleNavigateToLogin}
              onNavigateToSignup={() => navigateWithHistory('signup')}
              onNavigateToRailwayTest={handleNavigateToRailwayTest}
              totalReferrals={referrals?.length || 0}
              totalProviders={providerCount || 0}
              autoMatchedCount={referrals?.filter(r => r.useMatchingEngine).length || 0}
            />
          )
        ) : activeTab === "workflows" && isAuthenticated ? (
          <SectionErrorBoundary name="Workflow Templates">
            <WorkflowTemplateList />
          </SectionErrorBoundary>
        ) : activeTab === "workflow-instances" && isAuthenticated ? (
          <WorkflowInstanceTracker />
        ) : activeTab === "automation-rules" && isAuthenticated ? (
          <AutomationRules />
        ) : activeTab === "queue" && isAuthenticated ? (
          viewingReferralId && referrals?.find(r => String(r.id) === String(viewingReferralId)) ? (
            <ReferralDetailView
              referral={referrals.find(r => String(r.id) === String(viewingReferralId)) as FrontendReferral}
              onBack={handleCloseReferralView}
              onEdit={() => {
                handleCloseReferralView();
                handleEditReferral(viewingReferralId);
              }}
            />
          ) : editingReferralId && referrals?.find(r => String(r.id) === String(editingReferralId)) ? (
            <ReferralEditForm
              referral={referrals.find(r => String(r.id) === String(editingReferralId)) as FrontendReferral}
              onSubmit={handleUpdateReferral}
              onCancel={handleCloseReferralEdit}
              onBack={handleCloseReferralEdit}
            />
          ) : (
            <ReferralQueue
              referrals={referrals || []}
              onCreateNew={showCreateFormHandler}
              onNavigateToLiveFeed={handleNavigateToLiveFeed}
              onViewMatched={handleViewReferral}
              onBack={navigationHistory.length > 1 ? navigateBack : undefined}
              onUpdateStatus={updateReferralStatus}
              onEditReferral={handleEditReferral}
              onDeleteReferral={handleDeleteReferral}
              onEnrollMember={handleEnrollMember}
            />
          )
        ) : activeTab === "unmatched" && isAuthenticated ? (
          <CandidateFinder
            onNavigateToSubmissions={handleNavigateToSubmissions}
            onBack={navigationHistory.length > 1 ? navigateBack : undefined}
            onNavigateToDirectory={() => {
              // Store the referral context before navigating
              handleNavigateToDirectory();
            }}
            onStartManualMatching={(referral) => {
              setReferralForManualMatching(referral);
              navigateWithHistory("provider-directory");
            }}
            referrals={referrals}
            selectedReferralForMatching={referralForManualMatching}
            selectedProviderForMatching={selectedProviderForMatching}
            onRefreshReferrals={fetchReferrals}
          />
        ) : activeTab === "finder" && isAuthenticated ? (
          <CandidateFinder
            onNavigateToSubmissions={handleNavigateToSubmissions}
            onBack={navigationHistory.length > 1 ? navigateBack : undefined}
            referrals={referrals || []}
            onMatchReferral={async (referralId, providerId) => {
              // Update referral status to matched
              const success = await apiUpdateReferralStatus(referralId, "Matched");
              if (success) {
                console.log(`Matched referral ${referralId} to provider ${providerId}`);
              }
            }}
            onNavigateToDirectory={handleNavigateToDirectory}
            onStartManualMatching={(referral) => {
              setReferralForManualMatching(referral);
              navigateWithHistory("provider-directory");
            }}
            selectedReferralForMatching={referralForManualMatching}
            selectedProviderForMatching={selectedProviderForMatching}
            onRefreshReferrals={fetchReferrals}
          />
        ) : activeTab === "provider-communications" && isAuthenticated && selectedProviderForComms ? (
          (() => {
            console.log('🔍 [MAIN] Rendering ProviderCommunications:', {
              activeTab,
              isAuthenticated,
              selectedProviderForComms
            });
            return (
              <ProviderCommunications
                provider={selectedProviderForComms}
                onBack={navigateBack}
              />
            );
          })()
        ) : activeTab === "directory" && isAuthenticated ? (
          <Directory
            onNavigateToCapacity={handleNavigateToCapacity}
            onBack={navigationHistory.length > 1 ? navigateBack : undefined}
            onProviderSelected={(provider) => {
              console.log('📍 Provider selected from My Providers:', provider);
              setSelectedProviderForMatching(provider);
              navigateWithHistory("finder");
            }}
            onNavigateToCommunications={handleNavigateToCommunications}
          />
        ) : activeTab === "provider-directory" ? (
          // Provider Directory - No login required
          <ProviderDirectory
            onBack={() => {
              if (referralForManualMatching) {
                setReferralForManualMatching(null);
                setSelectedProviderForMatching(null);
                navigateWithHistory("finder");
              } else {
                navigateHome();
              }
            }}
            showSelectButton={!!referralForManualMatching}
            onSelectProvider={(provider) => {
              setSelectedProviderForMatching(provider);
              navigateWithHistory("finder");
            }}
          />
        ) : activeTab === "matching" && isAuthenticated ? (
          <AutoMatchReferrals
            onBack={navigationHistory.length > 1 ? navigateBack : undefined}
          />
        ) : activeTab === "workspace" && isAuthenticated ? (
          selectedMemberId ? (
            <CaseWorkspace 
              selectedReferralId={selectedReferralId}
              referrals={referrals || []}
              onNavigateToDirectory={handleNavigateToDirectory}
              onBack={() => {
                setSelectedMemberId(null);
                setSelectedReferralId(null);
                navigateBack();
              }}
            />
          ) : (
            <MemberProfiles
              referrals={referrals || []}
              onNavigateToWorkspace={handleNavigateToMemberWorkspace}
              onBack={navigationHistory.length > 1 ? navigateBack : undefined}
              onDeleteReferral={handleDeleteReferral}
            />
          )
        ) : activeTab === "livefeed" ? (
          // LiveFeed - Available without login, but with masked PII (desktop view)
          <LiveFeed
            referrals={referrals || []}
            onClaim={handleClaimReferral}
            onReject={handleRejectReferral}
            onBack={navigationHistory.length > 1 ? navigateBack : undefined}
            onUpdateStatus={updateReferralStatus}
            onViewDetails={handleViewReferral}
            isAuthenticated={isAuthenticated}
          />
        ) : activeTab === "admin" && isAuthenticated ? (
          <ImportManagerEnhanced />
        ) : activeTab === "self-signup" && isAuthenticated && (user?.role === 'super_admin' || user?.role === 'ops_admin') && activeOrgId === 1 ? (
          <SelfSignupDashboard />
        ) : activeTab === "compliance-documents" && isAuthenticated && (user?.role === 'super_admin' || user?.role === 'ops_admin') ? (
          <ComplianceDocumentsPage />
        ) : activeTab === "document-config" && isAuthenticated && canAccessPlatform ? (
          <div className="p-6 max-w-6xl mx-auto">
            <DocumentCategoryManager />
          </div>
        ) : activeTab === "hard-copy-tracking" && isAuthenticated ? (
          <div className="p-6 max-w-6xl mx-auto space-y-6">
            {canAccessPlatform && (
              <OrgTrackingSettings />
            )}
            <HardCopyTrackingList showCreate />
          </div>
        ) : activeTab === "approvals" && isAuthenticated ? (
          <div className="p-6 max-w-5xl mx-auto">
            <SignatureCenter />
          </div>
        ) : activeTab === "viewer-capabilities" && isAuthenticated && user?.role === 'viewer' ? (
          <ViewerCapabilities user={user} />
        ) : activeTab === "webinar-management" && isAuthenticated && (user?.role === 'super_admin' || user?.role === 'ops_admin') && activeOrgId === 1 ? (
          <WebinarManagementPage />
        ) : activeTab === "event-registrations" && isAuthenticated && (user?.role === 'super_admin' || user?.role === 'ops_admin') && activeOrgId === 1 ? (
          <EventRegistrationsPage />
        ) : (activeTab === "public-site-content" || activeTab === "resources-management") && isAuthenticated && (user?.role === 'super_admin' || user?.role === 'ops_admin') && activeOrgId === 1 ? (
          // public-site-content is the canonical tab id (migration 366).
          // resources-management is kept for backward-compat with any
          // deep links pinned before the sidebar was split.
          // Gated to super_admin / ops_admin on Trust Care Org (org 1) —
          // this surface only manages weavein.ai public-site assets.
          <ResourcesManagementPage />
        ) : activeTab === "platform-dashboard" && isAuthenticated && config.isTriveny ? (
          // Triveny.ai operator "My day" home. Reached from TrivenyHome's
          // top nav button. Single-tenant workspace.
          <TrivenyMyDay />
        ) : activeTab === "platform-dashboard" && isAuthenticated && canAccessPlatform ? (
          <PlatformDashboard />
        ) : activeTab === "platform-web-presence" && isAuthenticated && canAccessPlatform ? (
          <WebPresencePage />
        ) : activeTab === "platform-marketing" && isAuthenticated && canAccessPlatform ? (
          <MarketingAutomationPage />
        ) : activeTab === "platform-marketing-analytics" && isAuthenticated && canAccessPlatform ? (
          // Sidebar "Analytics" entry routes here; reuse the Marketing page
          // and let it open its internal Analytics sub-tab on mount.
          <MarketingAutomationPage initialTab="analytics" />
        ) : activeTab === "platform-contacts" && isAuthenticated && canAccessPlatform ? (
          <ContactDirectoryPage />
        ) : activeTab === "platform-sales" && isAuthenticated && canAccessInternalOps ? (
          <SalesPipelinePage />
        ) : activeTab === "platform-revops" && isAuthenticated && canAccessInternalOps ? (
          <RevOpsPage />
        ) : activeTab === "platform-agents" && isAuthenticated && canAccessPlatform ? (
          <AgentControlPage />
        ) : activeTab === "platform-team" && isAuthenticated ? (
          // Combined workspace admin (Team + Organizations), opened on Team.
          <TrivenyWorkspaceAdmin initialTab="team" />
        ) : activeTab === "weavein-agents" && isAuthenticated && activeOrgId === 1 && (user?.role === 'super_admin' || user?.role === 'org_admin') ? (
          // WeaveIn Agents: Trust Care org (1) + super/org admin only — mirrors
          // the nav gating in migration 451 so a stray tab can't render elsewhere.
          <AgentControlPage product="weavein" />
        ) : activeTab === "platform-invoices" && isAuthenticated && canAccessInternalOps ? (
          <InvoiceManagement
            organizationId={activeOrgId || parseInt(localStorage.getItem('active_cbo_id') || '0')}
            organizationName={activeOrgName}
          />
        ) : activeTab === "platform-support" && isAuthenticated && canAccessPlatform ? (
          <CustomerSupportPage />
        ) : activeTab === "platform-intelligence" && isAuthenticated && canAccessPlatform ? (
          <IntentIntelligencePage onNavigate={handleTabChange} />
        ) : activeTab === "platform-outreach" && isAuthenticated && canAccessPlatform ? (
          // Triveny-only unified Outreach workspace — tabs across Visitor ID,
          // Lead Builder, Cold Outreach, and the cross-channel Funnel. The
          // three sub-routes below remain reachable for deep links; on
          // Triveny they redirect into the matching workspace tab.
          <OutreachWorkspace />
        ) : activeTab === "platform-visitor-id" && isAuthenticated && canAccessPlatform ? (
          config.isTriveny ? <OutreachWorkspace initialTab="visitors" /> : <VisitorIdentificationPage />
        ) : activeTab === "platform-cold-outreach" && isAuthenticated && canAccessPlatform ? (
          config.isTriveny ? <OutreachWorkspace initialTab="sequences" /> : <ColdOutreachPage />
        ) : activeTab === "platform-lead-builder" && isAuthenticated && canAccessPlatform ? (
          config.isTriveny ? <OutreachWorkspace initialTab="leadbuilder" /> : <LeadBuilderPage />
        ) : activeTab === "platform-marketplace" && isAuthenticated && canAccessPlatform ? (
          <MarketplaceIntegrationsPage />
        ) : activeTab?.startsWith("v2-") && isAuthenticated && isV2Enabled ? (
          // Staff v2 pages — lazy loaded with Suspense
          <React.Suspense fallback={<V2Loading />}>
            {activeTab === "v2-home" ? (
              <CmHome
                userName={currentUser?.split(' ')[0] || undefined}
                userRole={v2PreviewRole || orgSpecificRole || user?.role || 'case_manager'}
                userId={typeof user?.id === 'number' ? user.id : parseInt(String(user?.id || '0'), 10)}
                organizationId={activeOrgId}
                orgName={activeOrgName}
                onNavigate={(tab) => navigateWithHistory(tab)}
              />
            ) : activeTab === "v2-referrals" ? (
              <V2ReferralHub
                referrals={referrals || []}
                onNavigate={(tab, id) => {
                  // Route the id by destination so the right state slot fills.
                  if (id) {
                    if (tab === 'tco-workflow-template-edit') setSelectedTCOTemplateId(parseInt(id, 10));
                    else if (tab === 'tco-workflow-instance')   setSelectedTCOInstanceId(parseInt(id, 10));
                    else if (tab === 'tco-referral-detail')     setSelectedTCOReferralId(parseInt(id, 10));
                    else                                         setSelectedMemberId(id);
                  }
                  navigateWithHistory(tab);
                }}
                selectedMemberId={selectedMemberId || undefined}
                selectedReferralId={viewingReferralId || selectedReferralId || null}
                onSetSelectedMemberId={setSelectedMemberId}
                onSetSelectedReferralId={(id) => setSelectedReferralId(id as string | number)}
                onStartManualMatching={(referral) => {
                  setReferralForManualMatching(referral as Referral);
                  // My Providers — has per-provider Select + Messages actions
                  navigateWithHistory("directory");
                }}
                selectedReferralForMatching={referralForManualMatching}
                selectedProviderForMatching={selectedProviderForMatching}
                onClearMatchingSelection={() => {
                  setReferralForManualMatching(null);
                  setSelectedProviderForMatching(null);
                }}
                onRefreshReferrals={fetchReferrals}
                onUpdateStatus={updateReferralStatus}
                onDeleteReferral={handleDeleteReferral}
                onEnrollMember={handleEnrollMember}
                onUpdateReferral={handleUpdateReferral}
              />
            ) : activeTab === "v2-calendar" ? (
              <V2CalendarHub onNavigate={(tab) => navigateWithHistory(tab)} />
            ) : activeTab === "v2-comms" ? (
              <V2CommsHub onNavigate={(tab) => navigateWithHistory(tab)} />
            ) : activeTab === "v2-supervisor" ? (
              <V2Supervisor
                onNavigate={(tab) => navigateWithHistory(tab)}
                userId={typeof user?.id === 'number' ? user.id : parseInt(String(user?.id || '0'), 10)}
                organizationId={activeOrgId}
              />
            ) : activeTab === "v2-access" ? (
              <V2AdminAccess onNavigate={(tab) => navigateWithHistory(tab)} />
            ) : null}
          </React.Suspense>
        ) : activeTab === "terminology-glossary" && isAuthenticated && activeOrgId ? (
          // RFP Feature: Terminology Glossary (capability-gated)
          <RFPCapabilityGate
            organizationId={activeOrgId}
            capability={RFP_CAPABILITIES.TERMINOLOGY_GLOSSARY}
            behavior="show-upgrade"
          >
            <TerminologyGlossary />
          </RFPCapabilityGate>
        ) : activeTab === "role-terminology" && isAuthenticated && activeOrgId ? (
          // RFP Feature: Role Terminology Manager (capability-gated)
          <RFPCapabilityGate
            organizationId={activeOrgId}
            capability={RFP_CAPABILITIES.ROLE_BASED_DASHBOARDS}
            behavior="show-upgrade"
          >
            <RoleTerminologyManager organizationId={activeOrgId} />
          </RFPCapabilityGate>
        ) : activeTab === "team-assignments" && isAuthenticated && activeOrgId ? (
          // RFP Feature: Team Assignment Manager (capability-gated)
          <RFPCapabilityGate
            organizationId={activeOrgId}
            capability={RFP_CAPABILITIES.ROLE_BASED_DASHBOARDS}
            behavior="show-upgrade"
          >
            <TeamAssignmentManager organizationId={activeOrgId} />
          </RFPCapabilityGate>
        ) : activeTab === "user-goals" && isAuthenticated && activeOrgId ? (
          // RFP Feature: User Goals Manager (capability-gated)
          <RFPCapabilityGate
            organizationId={activeOrgId}
            capability={RFP_CAPABILITIES.ROLE_BASED_DASHBOARDS}
            behavior="show-upgrade"
          >
            <UserGoalsManager organizationId={activeOrgId} />
          </RFPCapabilityGate>
        ) : activeTab === "contacts" && isAuthenticated ? (
          <ContactListPage
            onViewContact={(id) => { setSelectedMemberId(id.toString()); navigateWithHistory('contacts-view'); }}
            onEditContact={(id) => { setSelectedMemberId(id.toString()); navigateWithHistory('contacts-edit'); }}
            onCreateContact={() => navigateWithHistory('contacts-new')}
            onConfigureFields={() => navigateWithHistory('member-fields')}
          />
        ) : activeTab === "contacts-view" && isAuthenticated && selectedMemberId ? (
          <ContactDetailPage
            contactId={parseInt(selectedMemberId)}
            onBack={() => navigateWithHistory('contacts')}
            onEdit={(id) => { setSelectedMemberId(id.toString()); navigateWithHistory('contacts-edit'); }}
            onArchived={() => navigateWithHistory('contacts')}
            onEnrolled={(id) => { setSelectedMemberId(id.toString()); navigateWithHistory('members-view'); }}
          />
        ) : activeTab === "contacts-new" && isAuthenticated ? (
          <AddContactForm
            onClose={() => navigateWithHistory('contacts')}
            onCreated={(id) => { setSelectedMemberId(id.toString()); navigateWithHistory('contacts-view'); }}
          />
        ) : activeTab === "contacts-edit" && isAuthenticated && selectedMemberId ? (
          <AddContactForm
            contactId={parseInt(selectedMemberId)}
            onClose={() => { navigateWithHistory('contacts-view'); }}
            onCreated={() => { navigateWithHistory('contacts-view'); }}
          />
        ) : activeTab === "members" && isAuthenticated ? (
          <MemberList
            onNavigate={(tab, memberId) => {
              if (memberId) setSelectedMemberId(memberId.toString());
              navigateWithHistory(tab);
            }}
            favoriteIds={isV2Enabled ? v2FavoriteIds : undefined}
            onToggleFavorites={isV2Enabled ? () => {
              if (v2FavoriteIds) { setV2FavoriteIds(null); return; }
              // Absolute prod base URL — a relative /api/* on weavein.ai is
              // proxied to the DEV backend (vercel.json), which rejects the
              // prod-issued JWT with "Signature verification failed" (401).
              authFetch(`${config.weaveinApi.baseUrl}/api/members/favorites/list`)
                .then(r => r.json())
                .then(data => {
                  let ids: number[] = [];
                  if (Array.isArray(data)) {
                    ids = data.map((f: { member_id?: number; id?: number }) => f.member_id || f.id).filter(Boolean) as number[];
                  } else if (data?.favorite_member_ids && Array.isArray(data.favorite_member_ids)) {
                    ids = data.favorite_member_ids;
                  } else if (data?.favorites && Array.isArray(data.favorites)) {
                    ids = data.favorites.map((f: { member_id?: number; id?: number }) => f.member_id || f.id).filter(Boolean) as number[];
                  }
                  setV2FavoriteIds(ids);
                })
                .catch(() => setV2FavoriteIds([]));
            } : undefined}
          />
        ) : activeTab === "members-duplicates" && isAuthenticated ? (
          <MemberDuplicatesPanel
            onOpenMember={(id) => {
              setSelectedMemberId(id.toString());
              navigateWithHistory("members-view");
            }}
          />
        ) : activeTab === "members-new" && isAuthenticated ? (
          <MemberProfile onNavigate={(tab, id) => {
            if (id) setSelectedMemberId(id.toString());
            navigateWithHistory(tab);
          }} />
        ) : activeTab === "members-edit" && isAuthenticated && selectedMemberId ? (
          <AddEditMember memberId={parseInt(selectedMemberId)} onNavigate={(tab, memberId) => {
            if (memberId) setSelectedMemberId(memberId.toString());
            navigateWithHistory(tab);
          }} />
        ) : activeTab === "members-view" && isAuthenticated && selectedMemberId ? (
          <MemberProfile memberId={parseInt(selectedMemberId)} onNavigate={(tab, id) => {
            if (tab === 'household-view' && id) {
              setSelectedHouseholdId(id);
            } else if (id) {
              setSelectedMemberId(id.toString());
            }
            navigateWithHistory(tab);
          }} />
        ) : activeTab === "household-view" && isAuthenticated && selectedHouseholdId ? (
          <SectionErrorBoundary name="Household Detail">
            <HouseholdDetailView
              householdId={selectedHouseholdId}
              onBack={() => navigateWithHistory("members-view")}
            />
          </SectionErrorBoundary>
        ) : activeTab === "member-fields" && isAuthenticated && canAccessPlatform ? (
          <MemberFieldConfigManager />
        ) : activeTab === "staff-profiles" && isAuthenticated && canAccessPlatform ? (
          <StaffProfilesPage />
        ) : activeTab === "filter-chips" && isAuthenticated && canAccessPlatform ? (
          <FilterChipsManager />
        ) : activeTab === "field-mapping" && isAuthenticated && canAccessPlatform ? (
          <FieldMappingManager />
        ) : activeTab === "config-migration" && isAuthenticated ? (
          <ConfigMigrationPage onNavigate={navigateWithHistory} />
        ) : activeTab === "programs" && isAuthenticated ? (
          <ProgramManagement onNavigate={navigateWithHistory} />
        ) : activeTab === "contact-types" && isAuthenticated ? (
          <ContactTypesPage />
        ) : activeTab === "enrollments-view" && isAuthenticated && activeOrgId === 350 && selectedEnrollmentId ? (
          <EnrollmentDetail enrollmentId={parseInt(selectedEnrollmentId)} onBack={() => { setSelectedEnrollmentId(null); handleTabChange('members-view'); }} />
        ) : activeTab === "enrollments-view" && isAuthenticated && activeOrgId === 350 ? ( // LCC only for now (TODO: productize via nav registry)
          // Members-by-program enrollments roster (LCC-scoped entry via palette /
          // program drill-in). Row click sets the selected id → detail branch above.
          <EnrollmentListNew
            onNavigate={(tab, enrollmentId) => {
              if (enrollmentId != null) {
                setSelectedEnrollmentId(String(enrollmentId));
                navigateWithHistory('enrollments-view');
              } else {
                navigateWithHistory(tab);
              }
            }}
          />
        ) : activeTab === "enrollment-new" && isAuthenticated ? (
          <CreateEnrollmentPage onBack={() => navigateBack()} />
        ) : activeTab === "eligibility-screening" && isAuthenticated ? (
          <EligibilityPreScreenForm />
        ) : activeTab === "eligibility-verification" && isAuthenticated ? (
          <EligibilityVerificationPanel />
        ) : activeTab === "ffs-transition-cohort" && isAuthenticated ? (
          ffsTransitionLoading ? (
            <V2Loading />
          ) : ffsTransitionAllowed ? (
            <SectionErrorBoundary name="FFS Transition">
              <FfsTransitionCohortPage />
            </SectionErrorBoundary>
          ) : (
            <PopoutNoAccess />
          )
        ) : activeTab === "verify" && isAuthenticated ? (
          verifyLoading ? (
            <V2Loading />
          ) : verifyAllowed ? (
            <SectionErrorBoundary name="Verify">
              <VerifyPage />
            </SectionErrorBoundary>
          ) : (
            <PopoutNoAccess />
          )
        ) : activeTab === "eligibility-direct" && isAuthenticated ? (
          <DirectVerificationPanel />
        ) : activeTab === "eligibility-credentials" && isAuthenticated ? (
          <CredentialSetupWizard />
        ) : activeTab === "actions" && isAuthenticated ? (
          <ActionTracker />
        ) : activeTab === "program-milestone-config" && isAuthenticated ? (
          <ProgramMilestoneConfiguration />
        ) : activeTab === "member-milestone-progress" && isAuthenticated ? (
          <MemberMilestoneProgressSelector />
        ) : activeTab === "pending-milestone-approvals" && isAuthenticated ? (
          <PendingMilestoneApprovals />
        ) : activeTab === "milestone-management" && isAuthenticated ? (
          <MilestoneManagement />
        ) : activeTab === "sponsor-dashboard" && isAuthenticated ? (
          <SponsorDashboard />
        ) : activeTab === "member-retention" && isAuthenticated ? (
          <MemberRetentionDashboard cboId={activeOrgId || 1} />
        ) : activeTab === "outreach-activities" && isAuthenticated ? (
          <OutreachActivitiesPage />
        ) : activeTab === "event-tracking" && isAuthenticated ? (
          <EventTrackingPage onNavigate={(tab) => navigateWithHistory(tab)} />
        ) : activeTab === "my-forms" && isAuthenticated ? (
          <MyFormsInbox />
        ) : activeTab === "activity-reports" && isAuthenticated ? (
          <ActivityReportsPage />
        ) : (activeTab === "admin/observability" || activeTab === "observability") && isAuthenticated && (user?.role === 'super_admin' || user?.role === 'ops_admin') ? (
          // Observability is a tab inside the Agents page. The alert deep-link
          // (/admin/observability?issue=N) opens the Agents page on that tab.
          <AgentControlPage
            product="weavein"
            initialTab="issues"
            focusIssueId={(() => { const m = window.location.search.match(/[?&]issue=(\d+)/); return m ? Number(m[1]) : null; })()}
          />
        ) : activeTab === "geographic-risk-map" && isAuthenticated ? (
          <GeographicRiskMap cboId={activeOrgId || 1} />
        ) : activeTab === "emergency-dashboard" && isAuthenticated ? (
          <EmergencySituationRoom
            incidentId={selectedIncidentId || 0}
            onBack={() => navigateWithHistory('emergency-incidents')}
            onLaunchCampaign={(id, name) => { setSelectedIncidentId(id); setSelectedIncidentName(name); navigateWithHistory('emergency-create-incident'); }}
          />
        ) : activeTab === "emergency-incidents" && isAuthenticated ? (
          <EmergencyIncidentList
            onSelectIncident={(id, name) => { setSelectedIncidentId(id); setSelectedIncidentName(name || ''); navigateWithHistory('emergency-dashboard'); }}
            onCreateIncident={() => navigateWithHistory('emergency-create-incident')}
            onViewAlerts={() => navigateWithHistory('emergency-alerts')}
          />
        ) : activeTab === "emergency-alerts" && isAuthenticated ? (
          <ExternalAlertFeed
            onIncidentCreated={(id) => { setSelectedIncidentId(id); setSelectedIncidentName(''); navigateWithHistory('emergency-dashboard'); }}
            onBack={() => navigateWithHistory('emergency-incidents')}
          />
        ) : activeTab === "emergency-create-incident" && isAuthenticated ? (
          <EmergencyCampaignWizard
            incidentId={selectedIncidentId || 0}
            incidentName="New Emergency"
            onComplete={() => navigateWithHistory('emergency-incidents')}
            onCancel={() => navigateWithHistory('emergency-incidents')}
          />
        ) : activeTab === "communication-features" && isAuthenticated ? (
          <CommunicationFeaturesPanel memberId={selectedMemberId || 0} />
        ) : activeTab === "cbo-data-entry" && isAuthenticated && activeOrgId ? (
          <CBODataEntry memberId={selectedMemberId || 0} cboId={activeOrgId} onDataSubmit={() => {}} />
        ) : activeTab === "case-notes-analysis" && isAuthenticated ? (
          <CaseNotesAnalysis memberId={selectedMemberId || 0} cboId={activeOrgId || undefined} />
        ) : activeTab === "employee-retention" && isAuthenticated ? (
          <EmployerBenefitsAnalyticsDashboard employerId={activeEmployerId ? parseInt(activeEmployerId) : 0} />
        ) : activeTab === "employer-workflow-orchestration" && isAuthenticated ? (
          <EmployerWorkflowOrchestration
            employerId={activeEmployerId ? parseInt(activeEmployerId) : 0}
            onNavigate={(tab) => navigateWithHistory(tab)}
          />
        ) : activeTab === "employer-onboarding" && isAuthenticated ? (
          <EmployerOnboardingWizard
            employerId={activeEmployerId ? parseInt(activeEmployerId) : 0}
            onComplete={() => navigateWithHistory('home')}
          />
        ) : activeTab === "employer-quick-wins" && isAuthenticated ? (
          <EmployerQuickWins
            employerId={activeEmployerId ? parseInt(activeEmployerId) : 0}
            onNavigate={(tab) => navigateWithHistory(tab)}
          />
        ) : activeTab === "employer-benchmarking" && isAuthenticated ? (
          <EmployerBenchmarking
            employerId={activeEmployerId ? parseInt(activeEmployerId) : 0}
          />
        ) : activeTab === "employer-alerts" && isAuthenticated ? (
          <EmployerAlertsCenter
            employerId={activeEmployerId ? parseInt(activeEmployerId) : 0}
          />
        ) : activeTab === "employer-cost-transparency" && isAuthenticated ? (
          <EmployerCostTransparency
            employerId={activeEmployerId ? parseInt(activeEmployerId) : 0}
          />
        ) : activeTab === "workforce-dashboard" && isAuthenticated ? (
          <WorkforceDashboard />
        ) : activeTab === "workforce-candidates" && isAuthenticated ? (
          <WorkforceCandidates />
        ) : activeTab === "workforce-roles" && isAuthenticated ? (
          <WorkforceRoles />
        ) : activeTab === "workforce-certifications" && isAuthenticated ? (
          <WorkforceCertifications />
        ) : activeTab === "workforce-curricula" && isAuthenticated ? (
          <WorkforceCurricula />
        ) : activeTab === "workforce-learning-hub" && isAuthenticated ? (
          <LearningHubPage />
        ) : activeTab === "workforce-training-hub" && isAuthenticated ? (
          <TrainingHubPage />
        ) : activeTab === "workforce-courses" && isAuthenticated ? (
          <LmsCoursesAdminPage />
        ) : activeTab === "workforce-learn" && isAuthenticated ? (
          <StaffLearnPage />
        ) : activeTab === "workforce-mentoring" && isAuthenticated ? (
          <WorkforceMentoring />
        ) : activeTab === "workforce-training-schedule" && isAuthenticated ? (
          <WorkforceTrainingSchedule />
        ) : activeTab === "workforce-roster" && isAuthenticated ? (
          <WorkforceRoster />
        ) : activeTab === "workforce-skills-gap" && isAuthenticated ? (
          <WorkforceSkillsGap />
        ) : activeTab === "workforce-cases" && isAuthenticated ? (
          <SectionErrorBoundary name="Workforce Cases">
            <WorkforceCases />
          </SectionErrorBoundary>
        ) : activeTab === "workforce-ai-assistant" && isAuthenticated ? (
          <SectionErrorBoundary name="Workforce AI Assistant">
            <WorkforceAIAssistant />
          </SectionErrorBoundary>
        ) : activeTab === "workforce-unified-dashboard" && isAuthenticated ? (
          <SectionErrorBoundary name="Workforce Unified Dashboard">
            <WorkforceUnifiedDashboard />
          </SectionErrorBoundary>
        ) : activeTab === "workforce-assessments" && isAuthenticated ? (
          <SectionErrorBoundary name="Workforce Assessments">
            <WorkforceAssessments />
          </SectionErrorBoundary>
        ) : activeTab === "behavioral-assessment" && isAuthenticated && behavioralAssessmentAllowed ? (
          <SectionErrorBoundary name="Behavioral Assessment">
            <BehavioralAssessmentPage />
          </SectionErrorBoundary>
        ) : activeTab === "social-needs-screener" && isAuthenticated && socialNeedsScreenerAllowed ? (
          <SectionErrorBoundary name="Social Needs Screener">
            <SocialNeedsScreenerPage />
          </SectionErrorBoundary>
        ) : activeTab === "cch-care-management" && isAuthenticated ? (
          <SectionErrorBoundary name="CCH Care Management">
            <CCHCareManagementPage onNavigate={(tab: string) => setActiveTab(tab)} />
          </SectionErrorBoundary>
        ) : activeTab === "cch-hub-admin" && isAuthenticated ? (
          <SectionErrorBoundary name="CCH Hub Admin">
            <CCHHubAdminPage onNavigate={(tab: string) => setActiveTab(tab)} />
          </SectionErrorBoundary>
        ) : activeTab === "cch-reporting" && isAuthenticated ? (
          <SectionErrorBoundary name="CCH Reporting">
            <CCHReportingPage onNavigate={(tab: string) => setActiveTab(tab)} />
          </SectionErrorBoundary>
        ) : activeTab?.startsWith("cch-") && isAuthenticated ? (
          <SectionErrorBoundary name="CCH Module">
            <CCHModulePage module={activeTab} onNavigate={(tab: string) => setActiveTab(tab)} />
          </SectionErrorBoundary>
        ) : activeTab === "care-plan-driven" && isAuthenticated ? (
          <SectionErrorBoundary name="Care-Plan-driven referrals">
            <CarePlanDrivenReferrals
              cboId={Number(localStorage.getItem('active_cbo_id') || 0)}
              onOpenReferral={(referralId: number) => {
                setSelectedTCOReferralId(referralId);
                setActiveTab("tco-referral-detail");
              }}
            />
          </SectionErrorBoundary>
        ) : activeTab === "care-coordination-hub" && isAuthenticated ? (
          <CareCoordinationHub
            onViewInstance={(instanceId: number) => {
              setSelectedTCOInstanceId(instanceId);
              setActiveTab("tco-workflow-instance");
            }}
            onViewReferral={(referralId: number) => {
              setSelectedTCOReferralId(referralId);
              setActiveTab("tco-referral-detail");
            }}
            onEditTemplate={(templateId: number) => {
              setSelectedTCOTemplateId(templateId);
              setActiveTab("tco-workflow-template-edit");
            }}
            onCreateTemplate={() => setActiveTab("tco-workflow-template-create")}
            onCreateReferral={() => setActiveTab("tco-create-referral")}
          />
        ) : activeTab === "tco-workflow-instance" && isAuthenticated && selectedTCOInstanceId ? (
          <WorkflowInstanceDetail
            instanceId={selectedTCOInstanceId}
            onBack={() => setActiveTab("care-coordination-hub")}
            onViewReferral={(referralId: number) => {
              setSelectedTCOReferralId(referralId);
              setActiveTab("tco-referral-detail");
            }}
          />
        ) : activeTab === "tco-create-referral" && isAuthenticated ? (
          <TCOReferralCreateForm
            open={true}
            onClose={() => setActiveTab("care-coordination-hub")}
            onSuccess={(referralId: number) => {
              setSelectedTCOReferralId(referralId);
              setActiveTab("tco-referral-detail");
            }}
          />
        ) : activeTab === "tco-workflow-template-create" && isAuthenticated ? (
          <NewWorkflowTemplatePage
            onBack={() => setActiveTab("care-coordination-hub")}
            onSuccess={() => setActiveTab("care-coordination-hub")}
          />
        ) : activeTab === "tco-workflow-template-edit" && isAuthenticated && selectedTCOTemplateId ? (
          <EditWorkflowTemplatePage
            templateId={selectedTCOTemplateId}
            onBack={() => setActiveTab("care-coordination-hub")}
          />
        ) : activeTab === "tco-referral-detail" && isAuthenticated && selectedTCOReferralId ? (
          <ServiceReferralWorkspace
            referralId={selectedTCOReferralId}
            onBack={() => setActiveTab("care-coordination-hub")}
          />
        ) : activeTab === "tco-workflows" && isAuthenticated ? (
          <TCOWorkflowDashboard
            onViewInstance={(instanceId: number) => {
              setSelectedTCOInstanceId(instanceId);
              setActiveTab("tco-workflow-instance");
            }}
            onEditTemplate={(templateId: number) => {
              setSelectedTCOTemplateId(templateId);
              setActiveTab("tco-workflow-template-edit");
            }}
            onCreateTemplate={() => setActiveTab("tco-workflow-template-create")}
          />
        ) : activeTab === "tco-workflow-templates" && isAuthenticated ? (
          <TCOWorkflowDashboard
            onViewInstance={(instanceId: number) => {
              setSelectedTCOInstanceId(instanceId);
              setActiveTab("tco-workflow-instance");
            }}
            onEditTemplate={(templateId: number) => {
              setSelectedTCOTemplateId(templateId);
              setActiveTab("tco-workflow-template-edit");
            }}
            onCreateTemplate={() => setActiveTab("tco-workflow-template-create")}
          />
        ) : activeTab === "sponsor-retention" && isAuthenticated ? (
          <SponsorRetentionDashboard sponsorId={activeSponsorId || 0} />
        ) : activeTab === "program-marketplace" && isAuthenticated ? (
          activeSponsorId ? (
            <CBODiscovery sponsorId={activeSponsorId} programId={63} />
          ) : (
            <ProgramMarketplace cboId={activeOrgId || 1} />
          )
        ) : activeTab === "program-intelligence" && isAuthenticated ? (
          <ProgramIntelligenceCatalog cboId={activeOrgId || 1} />
        ) : activeTab === "fund-deposits" && isAuthenticated && canAccessPlatform ? (
          <FundDeposits />
        ) : activeTab === "member-payouts" && isAuthenticated && canAccessPlatform ? (
          <MemberPayouts />
        ) : activeTab === "reconciliation" && isAuthenticated && canAccessPlatform ? (
          <ReconciliationDashboard />
        ) : activeTab === "card-management" && isAuthenticated && canAccessPlatform ? (
          <CardManagement cboId={activeOrgId || undefined} />
        ) : activeTab === "transactions" && isAuthenticated ? (
          <TransactionHistory />
        ) : activeTab === "my-card" && isAuthenticated ? (
          <MemberCardPortal memberId={selectedMemberId ? parseInt(selectedMemberId, 10) : (typeof user?.id === 'number' ? user.id : parseInt(String(user?.id || 0), 10))} />
        ) : activeTab === "my-phone" && isAuthenticated ? (
          <MyPhoneNumberCard
            userId={typeof user?.id === 'number' ? user.id : parseInt(String(user?.id || 0), 10)}
            organizationId={(activeOrgId as number) || 0}
          />
        ) : activeTab === "sponsor-terms" && isAuthenticated ? (
          <SponsorTerms sponsorId={activeSponsorId || undefined} />
        ) : activeTab === "network-overview" && isAuthenticated ? (
          <NetworkOverview sponsorId={activeSponsorId || undefined} />
        ) : activeTab === "form-templates" && isAuthenticated ? (
          <FormTemplateBuilder />
        ) : activeTab === "public-links" && isAuthenticated ? (
          <PublicLinksPage />
        ) : activeTab === "form-library" && isAuthenticated ? (
          <FormLibrary
            enrollmentId={selectedEnrollmentId ? parseInt(selectedEnrollmentId) : undefined}
            memberId={selectedMemberId ? parseInt(selectedMemberId) : undefined}
            organizationId={parseInt(localStorage.getItem('active_cbo_id') || '0') || undefined}
            programId={localStorage.getItem('active_program_id') ? parseInt(localStorage.getItem('active_program_id')!) : undefined}
            onNavigate={(tab, memberId) => {
              if (memberId) setSelectedMemberId(memberId.toString());
              navigateWithHistory(tab);
            }}
          />
        ) : activeTab === "form-analytics" && isAuthenticated ? (
          <SectionErrorBoundary name="Form Analytics">
            <FormAnalyticsDashboard />
          </SectionErrorBoundary>
        ) : activeTab === "analytics" && isAuthenticated ? (
          <SectionErrorBoundary name="Analytics">
            <AnalyticsDashboard />
          </SectionErrorBoundary>
        ) : activeTab === "integrations" && isAuthenticated ? (
          <SectionErrorBoundary name="Integrations">
            <IntegrationsDashboard />
          </SectionErrorBoundary>
        ) : activeTab === "rag-admin" && isAuthenticated && canAccessPlatform ? (
          <SectionErrorBoundary name="RAG Administration">
            <RAGAdminDashboard />
          </SectionErrorBoundary>
        ) : activeTab === "developer-portal" && isAuthenticated ? (
          <SectionErrorBoundary name="Developer Portal">
            <DeveloperPortalPage />
          </SectionErrorBoundary>
        ) : activeTab === "sandbox-management" && isAuthenticated ? (
          <SectionErrorBoundary name="Sandbox Management">
            <SandboxManagementPage />
          </SectionErrorBoundary>
        ) : activeTab === "test-runner" && isAuthenticated && canAccessPlatform ? (
          <SectionErrorBoundary name="Test Runner">
            <TestRunnerDashboard />
          </SectionErrorBoundary>
        ) : activeTab === "calendar" && isAuthenticated ? (
          <SectionErrorBoundary name="Calendar">
            <CalendarPage onNavigate={(tab) => navigateWithHistory(tab)} />
          </SectionErrorBoundary>
        ) : activeTab === "telehealth" && isAuthenticated ? (
          <SectionErrorBoundary name="Telehealth">
            <TelehealthDashboard onNavigate={(tab) => navigateWithHistory(tab)} />
          </SectionErrorBoundary>
        ) : activeTab === "telehealth-reports" && isAuthenticated ? (
          <SectionErrorBoundary name="Telehealth Reports">
            <TelehealthReporting />
          </SectionErrorBoundary>
        ) : activeTab === "telehealth-waiting" && isAuthenticated ? (
          <SectionErrorBoundary name="Telehealth Waiting Room">
            <TelehealthWaitingRoom
              sessionId={parseInt(localStorage.getItem('telehealth_session_id') || '0', 10)}
              onJoinCall={() => navigateWithHistory('telehealth-session')}
              onCancel={() => navigateWithHistory('telehealth')}
              onMarkNoShow={() => navigateWithHistory('telehealth')}
            />
          </SectionErrorBoundary>
        ) : activeTab === "telehealth-session" && isAuthenticated ? (
          <SectionErrorBoundary name="Telehealth Session">
            <TelehealthVideoRoomWrapper
              sessionId={parseInt(localStorage.getItem('telehealth_session_id') || '0', 10)}
              onEndCall={() => navigateWithHistory('telehealth')}
              onBackToDashboard={() => navigateWithHistory('telehealth')}
            />
          </SectionErrorBoundary>
        ) : activeTab === "telehealth-encounters" && isAuthenticated ? (
          <SectionErrorBoundary name="Telehealth Encounters">
            <TelehealthEncountersList onBack={() => navigateWithHistory('telehealth')} />
          </SectionErrorBoundary>
        ) : activeTab === "telehealth-by-member" && isAuthenticated ? (
          <SectionErrorBoundary name="Telehealth By Member">
            <TelehealthByMember onBack={() => navigateWithHistory('telehealth')} />
          </SectionErrorBoundary>
        ) : activeTab === "oauth-integrations" && isAuthenticated ? (
          <SectionErrorBoundary name="OAuth Integrations">
            <OAuthIntegrations />
          </SectionErrorBoundary>
        ) : activeTab === "ai-orchestration" && isAuthenticated ? (
          <SectionErrorBoundary name="AI Orchestration">
            <AIOrchestrationDashboard />
          </SectionErrorBoundary>
        ) : activeTab === "external-services" && isAuthenticated ? (
          <SectionErrorBoundary name="External Services Catalog">
            <ExternalServicesCatalog />
          </SectionErrorBoundary>
        ) : activeTab === "groups" && isAuthenticated ? (
          <GroupsPage onSendMessage={(groupId) => {
            // Navigate to bulk communications with the selected group
            navigateWithHistory("bulk-communications");
            // Store the selected group ID for the communications page
            localStorage.setItem('selected_group_id', groupId);
          }} />
        ) : activeTab === "bulk-communications" && isAuthenticated ? (
          <BulkCommunicationsPage />
        ) : activeTab === "campaigns" && isAuthenticated ? (
          <CampaignsPage />
        ) : activeTab === "chatflows" && isAuthenticated && activeOrgId ? (
          <ChatflowBuilder organizationId={activeOrgId} />
        ) : activeTab === "communication-history" && isAuthenticated ? (
          <CommunicationHistoryPage />
        ) : activeTab === "workspace-messaging" && isAuthenticated ? (
          <div className="relative h-full min-h-0 flex-1 overflow-hidden">
            <MessagingWorkspace
              // A page pop-out opens straight onto the view / channel it was
              // asked for (Mailbox, DMs, a channel) and names its tab after
              // the open channel.
              {...(IS_PAGE_POPOUT
                ? {
                    initialView: (['members', 'email', 'dm-management', 'channels'] as const).find((v) => v === pagePopoutParams?.view),
                    defaultChannelId: pagePopoutParams?.channel ? Number(pagePopoutParams.channel) : undefined,
                    // TRUS-668: a call popped out of the main window joins it here.
                    initialCallId: PAGE_POPOUT?.params?.call ? Number(PAGE_POPOUT.params.call) : undefined,
                    initialCallMode: PAGE_POPOUT?.params?.mode === 'audio' ? 'audio' : 'video',
                    // v3: an invite without channel access pops out the call alone.
                    initialCallStandalone: PAGE_POPOUT?.params?.call_only === '1',
                    onSelectedChannelChange: onPopoutChannelChange,
                  }
                : {})}
              headerActions={isV2Enabled ? (
                <button
                  onClick={() => {
                    const el = document.getElementById('v2-inbox-composer');
                    if (el) el.classList.toggle('hidden');
                  }}
                  style={{ height: 30, borderRadius: 7, background: '#14171c', color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '0 12px', fontSize: 12, fontWeight: 500, fontFamily: 'Inter, sans-serif' }}
                  title="New message"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/></svg>
                  New message
                </button>
              ) : undefined}
            />
            {isV2Enabled && (
              <div id="v2-inbox-composer" className="hidden" style={{ position: 'absolute', top: 48, right: 16, zIndex: 9999, width: 420, borderRadius: 14, border: '1px solid #e6e8ec', background: '#fff', boxShadow: '0 8px 24px -10px rgba(20,23,28,0.3)' }}>
                <React.Suspense fallback={<div style={{ padding: 32, textAlign: 'center', color: '#9aa3af' }}>Loading…</div>}>
                  <V2InboxComposer onClose={() => document.getElementById('v2-inbox-composer')?.classList.add('hidden')} />
                </React.Suspense>
              </div>
            )}
          </div>
        ) : activeTab === "inbox" && isAuthenticated ? (
          // TRUS-709: the retired 2-Way Inbox; redirected to the member Inbox (effect below).
          <div className="relative h-full min-h-0 flex-1 overflow-hidden">
            <MessagingWorkspace initialView="members" />
          </div>
        ) : activeTab === "csr-inbox" && isAuthenticated ? (
          <CSRInboxPage
            organizationId={activeOrgId || 303}
            currentUserId={typeof user?.id === 'number' ? user.id : parseInt(String(user?.id || '0'), 10)}
          />
        ) : activeTab === "template-library" && isAuthenticated ? (
          <CommunicationTemplatesHub
            cboId={activeOrgId || 303}
            currentUserId={typeof user?.id === 'number' ? user.id : parseInt(String(user?.id || '0'), 10)}
            initialTab="universal"
            onBack={navigationHistory.length > 1 ? navigateBack : undefined}
          />
        ) : activeTab === "referral-automation" && isAuthenticated ? (
          <div className="p-6">
            <ReferralAutomationRuleEditor cboId={activeOrgId || 303} />
          </div>
        ) : activeTab === "data-ingestion" && isAuthenticated ? (
          <div className="space-y-6 p-6">
            <div>
              {!pageUnderTab && <h1 className="mb-4 text-xl font-semibold">Nightly Data Ingestion</h1>}
              <UUHPDataIngestionPanel cboId={activeOrgId || 303} />
            </div>
            <div className="border-t pt-6">
              <h2 className="mb-3 text-base font-semibold">Audit Export</h2>
              <p className="mb-3 text-sm text-gray-500">Export HIPAA-compliant audit log entries for any date range.</p>
              <AuditExportButton cboId={activeOrgId || 303} />
            </div>
          </div>
        ) : activeTab === "campaign-progress" && isAuthenticated ? (
          <div className="p-6">
            {!pageUnderTab && <h1 className="mb-4 text-xl font-semibold">Campaign Progress</h1>}
            <UUHPCampaignProgressPage cboId={activeOrgId || 303} />
          </div>
        ) : activeTab === "journey-builder" && isAuthenticated ? (
          <div className="p-6">
            {!pageUnderTab && <h1 className="mb-4 text-xl font-semibold">Campaign Builder</h1>}
            <UUHPJourneyBuilderPage cboId={activeOrgId || 303} />
          </div>
        ) : activeTab === "social-media-connections" && isAuthenticated && activeOrgId ? (
          <SocialMediaSettings organizationId={activeOrgId} isAdmin={user?.role === 'super_admin' || user?.role === 'org_admin'} />
        ) : activeTab === "whatsapp-templates" && isAuthenticated ? (
          <WhatsAppTemplateManager />
        ) : activeTab === "whatsapp-business" && isAuthenticated ? (
          <WhatsAppBusinessSettings />
        ) : activeTab === "phone-management" && isAuthenticated ? (
          <PhoneNumberManagement />
        ) : activeTab === "sender-assignments" && isAuthenticated ? (
          <SenderNumberAssignments />
        ) : activeTab === "sender-verification" && isAuthenticated ? (
          <SenderVerificationWizard />
        ) : activeTab === "media-library" && isAuthenticated ? (
          <MediaLibrary />
        ) : activeTab === "interactive-content" && isAuthenticated ? (
          <InteractiveContentBuilder />
        ) : activeTab === "grant-calendar" && isAuthenticated ? (
          <SectionErrorBoundary name="Grant Calendar">
            <GrantCalendarDashboard />
          </SectionErrorBoundary>
        ) : activeTab === "donations" && isAuthenticated ? (
          <SectionErrorBoundary name="Donations">
            <DonationsPage />
          </SectionErrorBoundary>
        ) : activeTab === "donor-pipeline" && isAuthenticated ? (
          <SectionErrorBoundary name="Donor Pipeline">
            <DonorPipelineBoard />
          </SectionErrorBoundary>
        ) : activeTab === "donor-events" && isAuthenticated ? (
          <SectionErrorBoundary name="Donor Events">
            <DonorEventsPage />
          </SectionErrorBoundary>
        ) : activeTab === "donor-cohorts" && isAuthenticated ? (
          <SectionErrorBoundary name="Donor Cohorts">
            <CohortBuilder />
          </SectionErrorBoundary>
        ) : activeTab === "sponsor-cohort" && isAuthenticated ? (
          <SectionErrorBoundary name="Our Cohort">
            <SponsorCohort />
          </SectionErrorBoundary>
        ) : activeTab === "doulas" && isAuthenticated ? (
          <SectionErrorBoundary name="Doulas">
            <DoulasPage />
          </SectionErrorBoundary>
        ) : activeTab === "provider-referral-inbox" && isAuthenticated ? (
          // TRUS-728: referrals other orgs sent to the active org (nav: care.provider_referral_inbox).
          <SectionErrorBoundary name="Referral Inbox">
            <ProviderAcceptancePortal providerName={activeOrgName} />
          </SectionErrorBoundary>
        ) : activeTab === "sponsor-funded" && isAuthenticated ? (
          <SectionErrorBoundary name="What We Funded">
            <SponsorFundedPayments />
          </SectionErrorBoundary>
        ) : activeTab === "fundraising-home" && isAuthenticated ? (
          <SectionErrorBoundary name="Fundraising">
            <FundraisingHome />
          </SectionErrorBoundary>
        ) : activeTab === "mutual-aid" && isAuthenticated ? (
          <SectionErrorBoundary name="Mutual Aid">
            <MutualAidBoard />
          </SectionErrorBoundary>
        ) : activeTab === "donor-pipeline" && isAuthenticated ? (
          <SectionErrorBoundary name="Donor Pipeline">
            <DonorPipelineBoard />
          </SectionErrorBoundary>
        ) : activeTab === "client-billing-admin" && isAuthenticated ? (
          // Independent Client Billing & Payments page scoped to one org,
          // opened via the $ icon in CBOManagement (org-admin only).
          (billingOrgId || activeOrgId) ? (
            <div>
              <button
                onClick={() => { setBillingOrgId(null); navigateWithHistory('cbo-management'); }}
                className="mx-auto max-w-7xl px-6 pt-4 flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
              >
                ← Back to organizations
              </button>
              {billingOrgName && (
                <div className="mx-auto max-w-7xl px-6 pt-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {billingOrgName} — Billing &amp; Payments
                </div>
              )}
              <ClientBillingPage organizationId={(billingOrgId || activeOrgId) as number} />
            </div>
          ) : (
            <div className="p-12 text-center text-gray-500">Loading client billing…</div>
          )
        ) : activeTab === "org-settings" && isAuthenticated && config.isTriveny ? (
          // Triveny: the "Org page" is ALWAYS the unified operator directory
          // (Organizations sign-ups + Team) — never the healthcare
          // OrganizationSettingsPage (branding/billing/phones), whose WeaveIn
          // tagline + single-org view was leaking onto Triveny via this branch.
          <TrivenyWorkspaceAdmin initialTab="organizations" />
        ) : activeTab === "org-settings" && isAuthenticated ? (
          // settingsOrgId is set by clicking an org row in CBOManagement;
          // command palette / direct nav skip that step, so resolve to
          // activeOrgId at render time. The route ALWAYS matches on
          // org-settings + authenticated — the previous `&& settingsOrgId`
          // gate caused fall-through to the analytics dashboard
          // (#report=custom) while the useEffect raced to fill settingsOrgId.
          (settingsOrgId || activeOrgId) ? (
            <OrganizationSettingsPage
              organizationId={(settingsOrgId || activeOrgId) as number}
              organizationName={settingsOrgName || activeOrgName}
              onBack={() => { setSettingsOrgId(null); navigateWithHistory('cbo-management'); }}
              initialTab="billing"
            />
          ) : (
            <div className="p-12 text-center text-gray-500">Loading organization settings…</div>
          )
        ) : activeTab === "cbo-management" && isAuthenticated ? (
          <>
            <CBOManagement
              onEditOrganization={(org) => {
                setSelectedOrg(org);
                setCboFormOpen(true);
              }}
              onCreateOrganization={() => {
                setSelectedOrg(null);
                setCboFormOpen(true);
              }}
              onOpenClientBilling={(orgId, orgName) => {
                setBillingOrgId(orgId);
                setBillingOrgName(orgName);
                navigateWithHistory('client-billing-admin');
              }}
              onNavigateToSettings={(orgId, orgName) => {
                setSettingsOrgId(orgId);
                setSettingsOrgName(orgName);
                navigateWithHistory('org-settings');
              }}
              onNavigateToUsers={(orgId, orgName) => {
                setActiveOrgId(orgId);
                setActiveOrgName(orgName);
                navigateWithHistory('user-management');
              }}
              refreshTrigger={cboRefreshTrigger}
              currentUserRole={user?.role}
              currentUserCboId={activeOrgId || undefined}
            />
            <CBOForm
              organization={selectedOrg}
              isOpen={cboFormOpen}
              onClose={() => {
                setCboFormOpen(false);
                setSelectedOrg(null);
              }}
              onSuccess={() => {
                console.log('✅ Organization form success - triggering refresh');
                setCboFormOpen(false);
                setSelectedOrg(null);
                setCboRefreshTrigger(prev => prev + 1);
              }}
            />
          </>
        ) : activeTab === "organization-setup-wizard" && isAuthenticated ? (
          <OrganizationSetupWizard
            cboId={activeOrgId || undefined}
            onComplete={() => navigateWithHistory('cbo-management')}
            onCancel={() => navigateWithHistory('cbo-management')}
          />
        ) : activeTab === "user-management" && isAuthenticated ? (
          <UserManagement
            cboId={activeOrgId || 1}
            // See the note on the other UserManagement instance (TRUS-513).
            resolvedOrgId={activeOrgId ?? null}
            organizationName={activeOrgName}
            currentUserRole={user?.role || "viewer"}
            onNavigate={handleUserManagementNavigate}
          />
        ) : activeTab === "user-permissions" && isAuthenticated && permissionsViewerContext ? (
          <EffectivePermissionsViewer
            userId={permissionsViewerContext.userId}
            userName={permissionsViewerContext.userName}
            cboId={activeOrgId || 1}
            onBack={() => navigateWithHistory('user-management')}
            onNavigate={navigateWithHistory}
          />
        ) : activeTab === "platform-roles" && isAuthenticated && config.isTriveny ? (
          <PlatformRolesPage />
        ) : activeTab === "platform-roles" && isAuthenticated && activeOrgId && isOrgAdmin ? (
          // WeaveIn fallback for the Triveny twin of access-control (no blank screen).
          <UnifiedAccessControl
            organizationId={activeOrgId}
            organizationName={activeOrgName}
          />
        ) : activeTab === "access-control" && isAuthenticated && activeOrgId && isOrgAdmin ? (
          <UnifiedAccessControl
            organizationId={activeOrgId}
            organizationName={activeOrgName}
          />
        ) : activeTab === "organization-roles" && isAuthenticated && activeOrgId && isOrgAdmin ? (
          <UnifiedAccessControl
            organizationId={activeOrgId}
            organizationName={activeOrgName}
          />
        ) : activeTab === "feature-capabilities" && isAuthenticated && activeOrgId && isOrgAdmin ? (
          <UnifiedAccessControl
            organizationId={activeOrgId}
            organizationName={activeOrgName}
          />
        ) : activeTab === "page-capabilities" && isAuthenticated && activeOrgId && isOrgAdmin ? (
          <UnifiedAccessControl
            organizationId={activeOrgId}
            organizationName={activeOrgName}
          />
        ) : activeTab === "role-page-access" && isAuthenticated && activeOrgId && isOrgAdmin ? (
          <UnifiedAccessControl
            organizationId={activeOrgId}
            organizationName={activeOrgName}
          />
        ) : activeTab === "nav-layout" && isAuthenticated && activeOrgId && isOrgAdmin ? (
          <NavigationLayoutAdmin
            organizationId={activeOrgId}
            organizationName={activeOrgName}
          />
        ) : activeTab === "data-privacy-settings" && isAuthenticated && activeOrgId && isOrgAdmin ? (
          <DataPrivacySettings
            organizationId={activeOrgId}
            organizationName={activeOrgName}
          />
        ) : activeTab === "security-compliance" && isAuthenticated && activeOrgId && isOrgAdmin ? (
          <SecurityComplianceDashboard
            organizationId={activeOrgId}
            organizationName={activeOrgName}
            onNavigate={(tab) => setActiveTab(tab)}
          />
        ) : activeTab === "break-glass-management" && isAuthenticated && activeOrgId && isOrgAdmin ? (
          <BreakGlassManagement
            organizationId={activeOrgId}
            organizationName={activeOrgName}
          />
        ) : activeTab === "external-systems" && isAuthenticated && activeOrgId && isOrgAdmin ? (
          <ExternalSystemsManagement
            organizationId={activeOrgId}
            organizationName={activeOrgName}
          />
        ) : activeTab === "field-sensitivity-settings" && isAuthenticated && activeOrgId && isOrgAdmin ? (
          <FieldSensitivitySettings
            organizationId={activeOrgId}
            organizationName={activeOrgName}
          />
        ) : activeTab === "ocr-template-settings" && isAuthenticated && activeOrgId && isOrgAdmin ? (
          <OCRTemplateSettings
            organizationId={activeOrgId}
            organizationName={activeOrgName}
          />
        ) : activeTab === "user-preferences" && isAuthenticated && config.isTriveny ? (
          <TrivenyUserPreferences
            defaultTab={(() => { const t = localStorage.getItem('preferences_default_tab'); if (t) localStorage.removeItem('preferences_default_tab'); return t || 'general'; })()}
            onNavigate={(tab) => navigateWithHistory(tab)}
          />
        ) : activeTab === "user-preferences" && isAuthenticated ? (
          <UserPreferences
            defaultTab={(() => { const t = localStorage.getItem('preferences_default_tab'); if (t) localStorage.removeItem('preferences_default_tab'); return t || 'general'; })()}
            onNavigate={(tab) => navigateWithHistory(tab)}
          />
        ) : activeTab === "translation-management" && isAuthenticated && (user?.role === "super_admin" || user?.role === "org_admin") ? (
          <TranslationManagement />
        ) : activeTab === "communication-templates" && isAuthenticated ? (
          <CommunicationTemplatesHub
            cboId={activeOrgId || 303}
            currentUserId={typeof user?.id === 'number' ? user.id : parseInt(String(user?.id || '0'), 10)}
            initialTab="my-org"
            onBack={navigationHistory.length > 1 ? navigateBack : undefined}
          />
        ) : activeTab === "template-variables" && isAuthenticated ? (
          <TemplateVariablesPage />
        ) : activeTab === "campaign-templates" && isAuthenticated ? (
          <CampaignTemplates
            onBack={navigationHistory.length > 1 ? navigateBack : undefined}
          />
        ) : activeTab === "letter-templates" && isAuthenticated ? (
          <LetterTemplatesPage
            onBack={navigationHistory.length > 1 ? navigateBack : undefined}
          />
        ) : activeTab === "register" ? (
          <RegisterWithInvite />
        ) : activeTab === "signup" ? (
          <SelfServiceSignup onSuccess={() => {
            // After successful signup, reload to show viewer dashboard
            window.location.reload();
          }} />
        ) : activeTab === "forgot-password" ? (
          <ForgotPassword />
        ) : activeTab === "reset-password" ? (
          <ResetPassword />
        ) : activeTab === "otfrtf" && isAuthenticated ? (
          <OTFRTFSubmission
            onSubmit={handleSubmissionComplete}
            onBack={navigationHistory.length > 1 ? navigateBack : undefined}
          />
        ) : activeTab === "billing-claims" && isAuthenticated && canAccessPlatform ? (
          <ClaimsPage onNavigate={(tab: string) => navigateWithHistory(tab)} />
        ) : activeTab === "document-processing" && isAuthenticated && canAccessPlatform ? (
          <DocumentProcessingPage onNavigate={(tab: string) => navigateWithHistory(tab)} />
        ) : activeTab === "billing-claims-bulk" && isAuthenticated && canAccessPlatform ? (
          <BulkClaimEntryPage onNavigate={(tab: string) => navigateWithHistory(tab)} />
        ) : activeTab === "billing-claims-new" && isAuthenticated && canAccessPlatform ? (
          <NewClaimPage onNavigate={(tab: string) => navigateWithHistory(tab)} />
        ) : activeTab === "billing-analytics" && isAuthenticated ? (
          <BillingAnalyticsPage />
        ) : activeTab === "program-billing" && isAuthenticated ? (
          <ProgramBillingPage />
        ) : activeTab === "pmpm-billing" && isAuthenticated ? (
          <PMPMBillingPage />
        ) : activeTab === "pdf-templates" && isAuthenticated ? (
          <PDFTemplatesPage />
        ) : activeTab === "scheduled-reports" && isAuthenticated ? (
          <ScheduledReportsPage />
        ) : activeTab === "report-distribution" && isAuthenticated ? (
          <ReportDistributionPage />
        ) : activeTab === "ecm-progress-notes-report" && isAuthenticated ? (
          <EcmProgressNotesReportPage />
        ) : activeTab === "clearinghouse-settings" && isAuthenticated ? (
          <ClearinghousePage />
        ) : activeTab === "mcp-billing-rules" && isAuthenticated ? (
          <McpBillingRulesPage />
        ) : activeTab === "billing-ai" && isAuthenticated ? (
          <BillingAIPage />
        ) : activeTab === "billing-data-sources" && isAuthenticated ? (
          <BillingDataSourcesPage />
        ) : activeTab === "billing-ml" && isAuthenticated ? (
          <BillingMlPage />
        ) : activeTab === "billing-ml-synthetic-pretrain" && isAuthenticated ? (
          <SyntheticPretrainPage />
        ) : activeTab === "saml-sso-config" && isAuthenticated ? (
          <SAMLSSOConfigPage />
        ) : activeTab === "hl7-config" && isAuthenticated ? (
          <HL7ConfigPage />
        ) : activeTab === "ihe-config" && isAuthenticated ? (
          <IHEConfigPage />
        ) : activeTab === "employer-benefits-management" && isAuthenticated ? (
          <EmployerBenefitsManagementPage />
        ) : activeTab === "work-compliance-dashboard" && isAuthenticated && selectedMemberId ? (
          <MemberWorkComplianceDashboard
            memberId={parseInt(selectedMemberId, 10)}
          />
        ) : activeTab === "work-compliance-submit" && isAuthenticated && selectedMemberId ? (
          <WorkActivitySubmissionForm
            memberId={parseInt(selectedMemberId, 10)}
            enrollmentId={0}
            onSuccess={() => navigateWithHistory('work-compliance-dashboard')}
          />
        ) : activeTab === "work-compliance-upload" && isAuthenticated && selectedMemberId ? (
          <WorkDocumentUploader
            workActivityLogId={0}
            enrollmentId={0}
            onUploadComplete={() => navigateWithHistory('work-compliance-dashboard')}
          />
        ) : activeTab === "work-compliance-history" && isAuthenticated && selectedMemberId ? (
          <MemberComplianceHistory
            memberId={parseInt(selectedMemberId, 10)}
            enrollmentId={0}
          />
        ) : activeTab === "cbo-compliance-dashboard" && isAuthenticated ? (
          <CBOComplianceDashboard
            cboId={(user as any)?.organization_id || 1}
          />
        ) : activeTab === "ecm-compliance" && isAuthenticated ? (
          <ECMComplianceDashboard
            onNavigate={navigateWithHistory}
          />
        ) : activeTab === "service-referrals" && isAuthenticated ? (
          <ServiceReferralsList
            employerId={activeEmployerId ? parseInt(activeEmployerId) : undefined}
            onSelectReferral={(referralId) => {
              setSelectedReferralId(referralId.toString());
              navigateWithHistory('service-referral-workspace');
            }}
            onCreateNew={() => navigateWithHistory('create-referral')}
          />
        ) : activeTab === "service-referral-workspace" && isAuthenticated && selectedReferralId ? (
          <ServiceReferralWorkspace
            referralId={parseInt(selectedReferralId, 10)}
            onClose={() => navigateWithHistory('service-referrals')}
            onStatusChange={() => {
              // Refresh referrals after status change
            }}
          />
        ) : activeTab === "cbo-reimbursement" && isAuthenticated ? (
          <CBOReimbursementDashboard />
        ) : activeTab === "outreach-billing" && isAuthenticated ? (
          <OutreachBillingPage onNavigate={(tab: string) => navigateWithHistory(tab)} />
        ) : activeTab === "outreach-worklist" && isAuthenticated ? (
          <OutreachWorklistPage onNavigate={(tab: string) => navigateWithHistory(tab)} />
        ) : activeTab === "outreach-type-config" && isAuthenticated ? (
          <OutreachTypesPage />
        ) : activeTab === "client-billing" && isAuthenticated && canAccessPlatform ? (
          <ClientBillingPage />
        ) : activeTab === "railway-test" ? (
          <RailwayTest />
        ) : activeTab === "health-plans" && isAuthenticated ? (
          <HealthPlansPage
            onSelectPlan={(planId) => {
              setSelectedHealthPlanId(planId.toString());
              navigateWithHistory("health-plan-detail");
            }}
          />
        ) : activeTab === "health-plan-detail" && isAuthenticated && selectedHealthPlanId ? (
          <HealthPlanDetail
            planId={parseInt(selectedHealthPlanId)}
            onBack={() => handleTabChange('health-plans')}
          />
        ) : activeTab === "employer-plans" && isAuthenticated && selectedEmployerId ? (
          <EmployerPlansManager
            employerId={parseInt(selectedEmployerId)}
            onBack={() => navigateBack()}
          />
        ) : activeTab === "employer-benefits" && isAuthenticated ? (
          <EmployerBenefitsDashboard
            employerId={(user as any)?.employer_id}
            employerName={(user as any)?.employer_name}
            onBack={() => handleTabChange('health-plans')}
          />
        ) : activeTab === "health-connect" && isAuthenticated && activeOrgId ? (
          // Gating is enforced server-side: /api/health/me/* returns 403 unless the
          // deep_integration capability is enabled for the org (the page renders
          // that disabled state). Discoverable via the command palette.
          <SectionErrorBoundary name="Health Connect">
            <HealthConnect
              organizationId={activeOrgId}
              organizationName={activeOrgName}
              userId={typeof user?.id === 'number' ? user.id : parseInt(String(user?.id || '0'), 10)}
            />
          </SectionErrorBoundary>
        ) : !isAuthenticated && activeTab !== "home" ? (
          // Redirect non-authenticated users trying to access protected pages
          <Homepage
            onNavigateToDirectory={handleNavigateToDirectory}
            onNavigateToLiveFeed={handleNavigateToLiveFeed}
            onNavigateToMatchingEngine={handleNavigateToMatchingEngine}
            onNavigateToCapacity={handleNavigateToCapacity}
            onNavigateToSubmissions={handleNavigateToSubmissions}
            onCreateNew={showCreateFormHandler}
            onNavigateToFinder={handleNavigateToFinder}
            onNavigateToQueue={handleNavigateToQueue}
            onNavigateToLogin={handleNavigateToLogin}
            onNavigateToSignup={() => navigateWithHistory('signup')}
            onNavigateToRailwayTest={handleNavigateToRailwayTest}
            totalReferrals={referrals?.length || 0}
            totalProviders={providerCount || 0}
            autoMatchedCount={referrals?.filter(r => r.useMatchingEngine).length || 0}
          />
        ) : activeTab === "dashboard" && isAuthenticated ? (
          <SectionErrorBoundary name="Analytics">
            <AnalyticsDashboard />
          </SectionErrorBoundary>
        ) : isAuthenticated ? (
          // No tab matched: show an explanatory empty-state instead of
          // silently redirecting to My Day. Several legacy tabs are
          // gated to Trust org_id=1 at the render site (e.g.
          // webinar-management, event-registrations) — clicking them
          // from a non-Trust org used to dump the user on My Day with
          // no signal. This makes the failure mode legible.
          (() => {
            console.warn(
              '⚠️ No tab matched. activeTab:',
              activeTab,
              'activeOrgId:',
              activeOrgId,
              'role:',
              user?.role,
            );
            const target = isV2Enabled ? 'v2-home' : 'dashboard';
            return (
              <div className="max-w-2xl mx-auto p-8 text-center space-y-3">
                <div className="text-amber-600 text-3xl">⚠️</div>
                <h2 className="text-xl font-semibold">This page isn't available</h2>
                <p className="text-sm text-gray-600">
                  The <code className="bg-gray-100 px-1 rounded text-xs">{activeTab}</code> page
                  doesn't exist or isn't enabled for your organization. If you reached this from
                  the sidebar, it may be a legacy feature gated to specific orgs.
                </p>
                <button
                  type="button"
                  className="mt-2 text-sm bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded"
                  onClick={() => {
                    setActiveTab(target);
                    localStorage.setItem('activeTab', target);
                  }}
                >
                  Go to {isV2Enabled ? 'My Day' : 'Dashboard'}
                </button>
              </div>
            );
          })()
        ) : null}

        {/* NOTE: Legacy FeedbackWidget and SupportChat removed - using native support-widget.js from index.html */}

        </V2LayoutShell>
        </TabbedChrome.Provider>
        {/* Footer - hidden on homepage which has its own full footer */}
        {activeTab !== "home" && !chromeless && !fullHeightPage && <Footer />}
        </div>
        {mainTabs.visible && !chromeless && (
          <MainTabPanels api={mainTabs} labelFor={popoutPages.labelFor} />
        )}
        </main>
      </div>

      {/* V2 Quick Action Modals */}
      {v2QuickAction === 'compose' && isV2Enabled && (
        <React.Suspense fallback={null}>
          <div className="fixed inset-0 z-[9998] bg-black/40" onClick={() => setV2QuickAction(null)} />
          <div className="fixed left-1/2 top-1/2 z-[9999] w-[440px] -translate-x-1/2 -translate-y-1/2 rounded-[var(--v2-r-xl)] border border-v2border bg-white shadow-[var(--v2-shadow-2)]">
            <V2InboxComposer onClose={() => setV2QuickAction(null)} />
          </div>
        </React.Suspense>
      )}
      {(v2QuickAction === 'task' || v2QuickAction === 'note') && isV2Enabled && (
        <React.Suspense fallback={null}>
          <V2QuickTaskModal
            mode={v2QuickAction === 'task' ? 'task' : 'note'}
            taskId={v2QuickAction === 'task' ? v2EditTaskId : null}
            onClose={() => { setV2QuickAction(null); setV2EditTaskId(null); }}
            onCreated={() => {}}
            userId={typeof user?.id === 'number' ? user.id : parseInt(String(user?.id || '0'), 10)}
          />
        </React.Suspense>
      )}

      {/* Authentication Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={handleCloseAuthModal}
        onNavigateToForgotPassword={handleNavigateToForgotPassword}
      />

      {/* User Profile Modal */}
      {showUserProfile && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={handleCloseUserProfile}
            ></div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <UserProfile onClose={handleCloseUserProfile} onLogout={handleLogout} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrandingProvider>
        <AccessibilityProvider>
          <RoleProvider>
            <DataShieldProvider>
              <ProgramProvider>
                <GuidedTourProvider>
                  <KeyboardShortcutProvider>
                    <AppContent />
                    <KeyboardShortcutsHelp />
                  </KeyboardShortcutProvider>
                </GuidedTourProvider>
              </ProgramProvider>
            </DataShieldProvider>
          </RoleProvider>
        </AccessibilityProvider>
      </BrandingProvider>
    </AuthProvider>
  );
}// Cache bust: 1776798080
